/**
 * 获取 GA4 关键 ID 集合
 * @returns {Promise<{client_id: string, session_id: string, session_number: string}>}
 */
function getGA4Identifiers() {
  const measurementId = "G-0675B7XDGQ";
  
  return new Promise((resolve) => {
    const data = {};
    const params = ["client_id", "session_id", "session_number"];
    let count = 0;

    params.forEach((param) => {
      gtag("get", measurementId, param, (val) => {
        data[param] = val;
        count++;
        if (count === params.length) resolve(data);
      });
    });
  });
}

// 使用示例
async function debugGA4() {
  const ids = await getGA4Identifiers();
  console.log("GA4 关键信息:", ids);
}

function getGA4ClientId() {
    try {
      const match = document.cookie.match(/_ga=GA1\.\d+\.(\d+\.\d+)/);
      return match ? match[1] : null;
    } catch (e) {
      return null;
    }
  }

/**
 * Adds an observer to initialise a script when an element is scrolled into view.
 * @param {Element} element - Element to observe.
 * @param {Function} callback - Function to call when element is scrolled into view.
 * @param {number} [threshold=500] - Distance from viewport (in pixels) to trigger init.
 */
function initLazyScript(element, callback, threshold = 500) {
    if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    if (typeof callback === 'function') {
                        callback();
                        observer.unobserve(entry.target);
                    }
                }
            });
        }, { rootMargin: `0px 0px ${threshold}px 0px` });

        io.observe(element);
    } else {
        callback();
    }
}

theme.stickyHeaderHeight = () => {
    const v = getComputedStyle(document.documentElement).getPropertyValue('--theme-sticky-header-height');
    if (v) {
        return parseInt(v, 10) || 0;
    }
    return 0;
};

theme.getOffsetTopFromDoc = (el) => el.getBoundingClientRect().top + window.scrollY;

theme.getOffsetLeftFromDoc = (el) => el.getBoundingClientRect().left + window.scrollX;

theme.getScrollParent = (node) => {
    const isElement = node instanceof HTMLElement;
    const overflowY = isElement && window.getComputedStyle(node).overflowY;
    const isScrollable = overflowY !== 'visible' && overflowY !== 'hidden';

    if (!node) {
        return null;
    }

    if (isScrollable && node.scrollHeight > node.clientHeight) {
        return node;
    }

    return theme.getScrollParent(node.parentNode) || document.scrollingElement || window;
};

theme.scrollToRevealElement = (el) => {
    const scrollContainer = theme.getScrollParent(el);
    const scrollTop = scrollContainer === window ? window.scrollY : scrollContainer.scrollTop;
    const scrollVisibleHeight = scrollContainer === window
        ? window.innerHeight : scrollContainer.clientHeight;
    const elTop = theme.getOffsetTopFromDoc(el);
    const elBot = elTop + el.offsetHeight;
    const inViewTop = scrollTop + theme.stickyHeaderHeight();
    const inViewBot = scrollTop + scrollVisibleHeight - 50;

    if (elTop < inViewTop || elBot > inViewBot) {
        scrollContainer.scrollTo({
            top: elTop - 100 - theme.stickyHeaderHeight(),
            left: 0,
            behavior: 'smooth'
        });
    }
};

theme.getEmptyOptionSelectors = (formContainer) => {
    const emptySections = [];

    formContainer.querySelectorAll('[data-selector-type="dropdown"].option-selector').forEach((el) => {
        if (!el.querySelector('[aria-selected="true"][data-value]:not([data-value=""])')) {
            emptySections.push(el);
        }
    });

    formContainer.querySelectorAll('[data-selector-type="listed"].option-selector').forEach((el) => {
        if (!el.querySelector('input:checked')) {
            emptySections.push(el);
        }
    });

    return emptySections;
};

theme.suffixIds = (container, prefix) => {
    const suffixCandidates = ['id', 'for', 'aria-describedby', 'aria-controls'];
    for (let i = 0; i < suffixCandidates.length; i += 1) {
        container.querySelectorAll(`[${suffixCandidates[i]}]`).forEach((el) => el.setAttribute(suffixCandidates[i], el.getAttribute(suffixCandidates[i]) + prefix));
    }
};

theme.addDelegateEventListener = (
    element,
    eventName,
    selector,
    callback,
    addEventListenerParams = null
) => {
    if (!element || typeof element.addEventListener !== 'function') {
        return () => {};
    }

    const cb = (evt) => {
        const el = evt.target.closest(selector);
        if (!el) return;
        if (!element.contains(el)) return;
        callback.call(el, evt, el);
    };
    element.addEventListener(eventName, cb, addEventListenerParams);
    return cb;
};

theme.hideAndRemove = (el) => {
    // disable
    el.querySelectorAll('input').forEach((input) => { input.disabled = true; });

    // wrap
    const wrapper = document.createElement('div');
    wrapper.className = 'merge-remove-wrapper';
    el.parentNode.insertBefore(wrapper, el);
    wrapper.appendChild(el);
    el.classList.add('merge-remove-item');
    wrapper.style.height = `${wrapper.clientHeight}px`;

    const cs = getComputedStyle(el);
    const fadeDuration = parseFloat(cs.getPropertyValue('--fade-duration')) * 1000;
    const slideDuration = parseFloat(cs.getPropertyValue('--slide-duration')) * 1000;

    setTimeout(() => {
        wrapper.classList.add('merge-remove-wrapper--fade');

        setTimeout(() => {
            wrapper.classList.add('merge-remove-wrapper--slide');

            setTimeout(() => wrapper.remove(), slideDuration);
        }, fadeDuration);
    }, 10);
};

theme.insertAndReveal = (el, target, iaeCmd, delay) => {
    const initialDelay = delay || 10;
    el.classList.add('merge-add-wrapper');
    target.insertAdjacentElement(iaeCmd, el);
    el.style.height = `${el.firstElementChild.clientHeight}px`;

    const cs = getComputedStyle(el);
    const fadeDuration = parseFloat(cs.getPropertyValue('--fade-duration')) * 1000;
    const slideDuration = parseFloat(cs.getPropertyValue('--slide-duration')) * 1000;

    setTimeout(() => {
        el.classList.add('merge-add-wrapper--slide');

        setTimeout(() => {
            el.classList.add('merge-add-wrapper--fade');

            setTimeout(() => {
                // tidy up
                el.style.height = '';
                el.classList.remove('merge-add-wrapper', 'merge-add-wrapper--slide', 'merge-add-wrapper--fade');
            }, fadeDuration);
        }, slideDuration);
    }, initialDelay);
};

theme.mergeNodes = (newContent, targetContainer) => {
    try {
        // merge: replace content if changed
        newContent.querySelectorAll('[data-merge]').forEach((newEl) => {
            const targetEl = targetContainer.querySelector(`[data-merge="${newEl.dataset.merge}"]`);
            if (!newEl.dataset.mergeCache
                || !targetEl.dataset.mergeCache
                || newEl.dataset.mergeCache !== targetEl.dataset.mergeCache) {
                targetEl.innerHTML = newEl.innerHTML;
                if (newEl.dataset.mergeCache || targetEl.dataset.mergeCache) {
                    targetEl.dataset.mergeCache = newEl.dataset.mergeCache;
                }
            }
        });
        // merge: attributes only
        newContent.querySelectorAll('[data-merge-attributes]').forEach((newEl) => {
            const targetEl = targetContainer.querySelector(`[data-merge-attributes="${newEl.dataset.mergeAttributes}"]`);
            const newElAttributeNames = newEl.getAttributeNames();
            for (let i = 0; i < newElAttributeNames.length; i += 1) {
                const attributeName = newElAttributeNames[i];
                targetEl.setAttribute(attributeName, newEl.getAttribute(attributeName));
            }
        });
        // merge: insert/remove/replace in list
        newContent.querySelectorAll('[data-merge-list]').forEach((newList) => {
            const targetList = targetContainer.querySelector(`[data-merge-list="${newList.dataset.mergeList}"]`);
            let targetListItems = Array.from(targetList.querySelectorAll('[data-merge-list-item]'));
            const newListItems = Array.from(newList.querySelectorAll('[data-merge-list-item]'));

            // remove
            targetListItems.forEach((targetListItem) => {
                // eslint-disable-next-line max-len
                const matchedItem = newListItems.find((item) => item.dataset.mergeListItem === targetListItem.dataset.mergeListItem);
                if (!matchedItem) {
                    theme.hideAndRemove(targetListItem);
                }
            });

            // rebuild target list excluding removed items
            targetListItems = Array.from(targetList.querySelectorAll('[data-merge-list-item]:not(.merge-remove-item)'));

            for (let i = 0; i < newListItems.length; i += 1) {
                const newListItem = newListItems[i];
                // eslint-disable-next-line max-len
                const matchedItem = targetListItems.find((item) => item.dataset.mergeListItem === newListItem.dataset.mergeListItem);
                if (matchedItem) {
                    // replace if changed
                    if (!newListItem.dataset.mergeCache
                        || !matchedItem.dataset.mergeCache
                        || newListItem.dataset.mergeCache !== matchedItem.dataset.mergeCache) {
                        matchedItem.innerHTML = newListItem.innerHTML;
                        if (newListItem.dataset.mergeCache) {
                            matchedItem.dataset.mergeCache = newListItem.dataset.mergeCache;
                        }
                    }
                } else {
                    // add
                    if (i === 0) {
                        // first place
                        theme.insertAndReveal(newListItem, targetList, 'afterbegin', 500);
                    } else if (i >= targetListItems.length) {
                        // at end
                        theme.insertAndReveal(newListItem, targetList, 'beforeend', 500);
                    } else {
                        // before element currently at that indext
                        theme.insertAndReveal(newListItem, targetListItems[i], 'beforebegin', 500);
                    }
                    // update target list
                    targetListItems.splice(i, 0, newListItem);
                }
            }
        });
    } catch (ex) {
        window.location.reload();
    }
};

// all 
const  zoomFactor = window.outerWidth / window.innerWidth;
if(zoomFactor>1){
    document.documentElement.style.setProperty('--page-zoom', `${zoomFactor}`);
}else{
    document.documentElement.style.setProperty('--page-zoom', `${1/zoomFactor}`);
}


// Show a short-lived text popup above an element
theme.showQuickPopup = (message, origin) => {
    const offsetLeft = theme.getOffsetLeftFromDoc(origin);
    const offsetTop = theme.getOffsetTopFromDoc(origin);
    const originLeft = origin.getBoundingClientRect().left;
    const popup = document.createElement('div');
    popup.className = 'simple-popup simple-popup--hidden';
    popup.innerHTML = message;
    popup.style.left = `${offsetLeft}px`;
    popup.style.top = `${offsetTop}px`;

    document.body.appendChild(popup);

    let marginLeft = -(popup.clientWidth - origin.clientWidth) / 2;
    if ((originLeft + marginLeft) < 0) {
        // Pull it away from the left edge of the screen
        marginLeft -= (originLeft + marginLeft) - 2;
    }
    // Pull from right edge + small gap
    const offsetRight = offsetLeft + marginLeft + popup.clientWidth + 5;
    if (offsetRight > window.innerWidth) {
        marginLeft -= (offsetRight - window.innerWidth);
    }
    popup.style.marginTop = -popup.clientHeight - 10;
    popup.style.marginLeft = marginLeft;
    setTimeout(() => {
        popup.classList.remove('simple-popup--hidden');
    }, 10);
    setTimeout(() => {
        popup.classList.add('simple-popup--hidden');
    }, 3500);
    setTimeout(() => {
        popup.remove();
    }, 4000);
};

theme.manuallyLoadImages = (container) => {
    container.querySelectorAll('img[data-manual-src]').forEach((el) => {
        el.src = el.dataset.manualSrc;
        el.removeAttribute('data-manual-src');
        if (el.dataset.manualSrcset) {
            el.srcset = el.dataset.manualSrcset;
            el.removeAttribute('data-manual-srcset');
        }
    });
};

theme.whenComponentLoaded = (component, callback) => {
    const components = Symbol.iterator in Object(component) ? [...component] : [component];
    if (!components.find((c) => !c.hasAttribute('loaded'))) {
        callback();
        return;
    }
    const onMutation = (mutationList, observer) => {
        for (let i = 0; i < mutationList.length; i += 1) {
            const mutation = mutationList[i];
            if (mutation.type === 'attributes') {
                if (!components.find((c) => !c.hasAttribute('loaded'))) {
                    observer.disconnect();
                    callback.call();
                }
            }
        }
    };
    const observer = new MutationObserver(onMutation);
    components.forEach((c) => observer.observe(c, { attributes: true, attributeFilter: ['loaded'] }));
};

/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
    let t;
    return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
    };
}

window.addEventListener(
    'resize',
    debounce(() => {
        window.dispatchEvent(new CustomEvent('on:debounced-resize'));
    })
);

/**
 * Creates a 'mediaMatches' object from the media queries specified in the theme,
 * and adds listeners for each media query. If a breakpoint is crossed, the mediaMatches
 * values are updated and a 'on:breakpoint-change' event is dispatched.
 */
(() => {
    const { mediaQueries } = theme;
    if (!mediaQueries) return;

    const mqKeys = Object.keys(mediaQueries);
    const mqLists = {};
    theme.mediaMatches = {};

    /**
     * Handles a media query (breakpoint) change.
     */
    const handleMqChange = () => {
        const newMatches = mqKeys.reduce((acc, media) => {
            acc[media] = !!(mqLists[media] && mqLists[media].matches);
            return acc;
        }, {});

        // Update mediaMatches values after breakpoint change.
        Object.keys(newMatches).forEach((key) => {
            theme.mediaMatches[key] = newMatches[key];
        });

        window.dispatchEvent(new CustomEvent('on:breakpoint-change'));
    };

    mqKeys.forEach((mq) => {
        // Create mqList object for each media query.
        mqLists[mq] = window.matchMedia(mediaQueries[mq]);

        // Get initial matches for each query.
        theme.mediaMatches[mq] = mqLists[mq].matches;

        // Add an event listener to each query.
        try {
            mqLists[mq].addEventListener('change', handleMqChange);
        } catch (err1) {
            // Fallback for legacy browsers (Safari < 14).
            mqLists[mq].addListener(handleMqChange);
        }
    });
})();

/**
 * Sets a 'viewport-height' custom property on the root element.
 */
function setViewportHeight() {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
}

/**
 * Sets a 'header-height' custom property on the root element.
 */
function setHeaderHeight() {
    const header = document.querySelector('.js-header-height');
    if (!header) return;
    let height = header.offsetHeight;

    // Add announcement bar height (if shown).
    const announcement = document.querySelector('.cc-announcement');
    const announcementHeight = announcement ? announcement.offsetHeight : 0;
    height += announcementHeight;

    document.documentElement.style.setProperty('--announcement-height', `${announcementHeight}px`);
    document.documentElement.style.setProperty('--header-height', `${height}px`);
}

/**
 * Sets a 'scrollbar-width' custom property on the root element.
 */
function setScrollbarWidth() {
    document.documentElement.style.setProperty(
        '--scrollbar-width',
        `${window.innerWidth - document.documentElement.clientWidth}px`
    );
}

/**
 * Sets the dimension variables.
 */
function setDimensionVariables() {
    setViewportHeight();
    setHeaderHeight();
    setScrollbarWidth();
}

function urlRestore(shopifyUrl) {
    if (!shopifyUrl || typeof shopifyUrl !== 'string') {
        return shopifyUrl;
    }
    try {
      let baseUrl = shopifyUrl.split('?')[0];
      const sizeRegex = /(_(\d+x|\w+))\./;
      const restoredUrl = baseUrl.replace(sizeRegex, '.');
      if (restoredUrl !== baseUrl) {
            return restoredUrl;
        } else {
            return baseUrl;
        }
    }catch (e) {
        console.error("URL 还原失败:", e);
        return shopifyUrl;
    }
}

// Set the dimension variables once the DOM is loaded
document.addEventListener('DOMContentLoaded', setDimensionVariables);

// Update the dimension variables if viewport resized.
window.addEventListener('resize', debounce(setDimensionVariables, 400));

// iOS alters screen width without resize event, if unexpectedly wide content is found
setTimeout(setViewportHeight, 3000);

/**
 * Pauses all media (videos/models) within an element.
 * @param {Element} [el=document] - Element to pause media within.
 */
function pauseAllMedia(el = document) {
    el.querySelectorAll('.js-youtube, .js-vimeo, video').forEach((video) => {
        const component = video.closest('video-component');
        if (component && component.dataset.background === 'true') return;

        if (video.matches('.js-youtube')) {
            video.contentWindow.postMessage('{ "event": "command", "func": "pauseVideo", "args": "" }', '*');
        } else if (video.matches('.js-vimeo')) {
            video.contentWindow.postMessage('{ "method": "pause" }', '*');
        } else {
            video.pause();
        }
    });

    el.querySelectorAll('product-model').forEach((model) => {
        if (model.modelViewerUI) model.modelViewerUI.pause();
    });
}

class DeferredMedia extends HTMLElement {
    constructor() {
        super();

        const loadBtn = this.querySelector('.js-load-media');
        if (loadBtn) {
            loadBtn.addEventListener('click', this.loadContent.bind(this));
        } else {
            this.addObserver();
        }
    }

    /**
     * Adds an Intersection Observer to load the content when viewport scroll is near
     */
    addObserver() {
        if ('IntersectionObserver' in window === false) return;

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    this.loadContent(false, false, 'observer');
                    observer.unobserve(this);
                }
            });
        }, { rootMargin: '0px 0px 1000px 0px' });

        observer.observe(this);
    }

    /**
     * Loads the deferred media.
     * @param {boolean} [focus=true] - Focus the deferred media element after loading.
     * @param {boolean} [pause=true] - Whether to pause all media after loading.
     * @param {string} [loadTrigger='click'] - The action that caused the deferred content to load.
     */
    loadContent(focus = true, pause = true, loadTrigger = 'click') {
        if (pause) pauseAllMedia();
        if (this.getAttribute('loaded') !== null) return;

        this.loadTrigger = loadTrigger;
        const content = this.querySelector('template').content.firstElementChild.cloneNode(true);
        this.appendChild(content);
        this.setAttribute('loaded', '');

        const deferredEl = this.querySelector('video, model-viewer, iframe');
        if (deferredEl && focus) deferredEl.focus();
    }
}

customElements.define('deferred-media', DeferredMedia);

class DetailsDisclosure extends HTMLElement {
    constructor() {
        super();
        this.disclosure = this.querySelector('details');
        this.toggle = this.querySelector('summary');
        this.panel = this.toggle.nextElementSibling;
        this.init();
        const grandParent = this.parentElement.parentElement;
        this.index = 0
        this.siblings = Array.from(grandParent.querySelectorAll('details-disclosure')).filter((el,index) => {
            if(el==this){
                this.index = index
            }
            return el !== this
        });
    }

    init() {
        // Check if the content element has a CSS transition.
        if (window.getComputedStyle(this.panel).transitionDuration !== '0s') {
            this.toggle.addEventListener('click', this.handleToggle.bind(this));
            this.disclosure.addEventListener('transitionend', this.handleTransitionEnd.bind(this));
        }
    }

    /**
     * Handles 'click' events on the summary element.
     * @param {object} evt - Event object.
     */
    handleToggle(evt) {
        evt.preventDefault();
        const top = this.getBoundingClientRect().top
        this.siblings.forEach((details,index) => {
            const detail = details.querySelector('details')
            if(detail.hasAttribute('open')){
                details.close()
                // if(isMobile){
                //     if(this.index>index){
                //         const toggle = detail.querySelector('summary');
                //         const panel = toggle.nextElementSibling;
                //         window.scrollBy(0, `-${panel.scrollHeight}`);
                //     }
                // }
            }
        });

 
        

        if (!this.disclosure.open) {
            this.open();
        } else {
            this.close();
        }

    }

    /**
     * Handles 'transitionend' events on the details element.
     * @param {object} evt - Event object.
     */
    handleTransitionEnd(evt) {
        if (evt.target !== this.panel) return;

        if (this.disclosure.classList.contains('is-closing')) {
            this.disclosure.classList.remove('is-closing');
            this.disclosure.open = false;
        }

        this.panel.removeAttribute('style');
    }

    /**
     * Adds inline 'height' style to the content element, to trigger open transition.
     */
    addContentHeight() {
        this.panel.style.height = `${this.panel.scrollHeight}px`;
    }

    /**
     * Opens the details element.
     */
    open() {
        // Set content 'height' to zero before opening the details element.
        this.panel.style.height = '0';

        // Open the details element
        this.disclosure.open = true;

        // Set content 'height' to its scroll height, to enable CSS transition.
        this.addContentHeight();
    }

    /**
     * Closes the details element.
     */
    close() {
        // Set content height to its scroll height, to enable transition to zero.
        this.addContentHeight();

        // Add class to enable styling of content or toggle icon before or during close transition.
        this.disclosure.classList.add('is-closing');

        // Set content height to zero to trigger the transition.
        // Slight delay required to allow scroll height to be applied before changing to '0'.
        setTimeout(() => {
            this.panel.style.height = '0';
        });
    }
}

customElements.define('details-disclosure', DetailsDisclosure);

if (!customElements.get('gift-card-recipient')) {
    class GiftCardRecipient extends HTMLElement {
        connectedCallback() {
            this.recipientEmail = this.querySelector('[name="properties[Recipient email]"]');
            this.recipientEmailLabel = this.querySelector(`label[for="${this.recipientEmail.id}"]`);
            this.recipientName = this.querySelector('[name="properties[Recipient name]"]');
            this.recipientMessage = this.querySelector('[name="properties[Message]"]');
            this.recipientSendOn = this.querySelector('[name="properties[Send on]"]');
            this.recipientOffsetProperty = this.querySelector('[name="properties[__shopify_offset]"]');

            // When JS is enabled, the recipient email field is required.
            // Input labels are changed to reflect this.
            if (this.recipientEmailLabel && this.recipientEmailLabel.dataset.jsLabel) {
                this.recipientEmailLabel.innerText = this.recipientEmailLabel.dataset.jsLabel;
            }

            // Set the timezone offset property input and enable it
            if (this.recipientOffsetProperty) {
                this.recipientOffsetProperty.value = new Date().getTimezoneOffset().toString();
                this.recipientOffsetProperty.removeAttribute('disabled');
            }

            this.recipientCheckbox = this.querySelector('.gift-card-recipient__checkbox');
            this.recipientCheckbox.addEventListener('change', () => this.synchronizeProperties());
            this.synchronizeProperties();
        }

        synchronizeProperties() {
            if (this.recipientCheckbox.checked) {
                this.recipientEmail.setAttribute('required', '');
                this.recipientEmail.removeAttribute('disabled');
                this.recipientName.removeAttribute('disabled');
                this.recipientMessage.removeAttribute('disabled');
                this.recipientSendOn.removeAttribute('disabled');
                if (this.recipientOffsetProperty) {
                    this.recipientOffsetProperty.removeAttribute('disabled');
                }
            } else {
                this.recipientEmail.removeAttribute('required');
                this.recipientEmail.setAttribute('disabled', '');
                this.recipientName.setAttribute('disabled', '');
                this.recipientMessage.setAttribute('disabled', '');
                this.recipientSendOn.setAttribute('disabled', '');
                if (this.recipientOffsetProperty) {
                    this.recipientOffsetProperty.setAttribute('disabled', '');
                }
            }
        }
    }

    customElements.define('gift-card-recipient', GiftCardRecipient);
}

const trapFocusHandlers = {};

/**
 * Removes focus trap event listeners and optionally focuses an element.
 * @param {Element} [elementToFocus=null] - Element to focus when trap is removed.
 */
function removeTrapFocus(elementToFocus = null) {
    document.removeEventListener('focusin', trapFocusHandlers.focusin);
    document.removeEventListener('focusout', trapFocusHandlers.focusout);
    document.removeEventListener('keydown', trapFocusHandlers.keydown);

    if (elementToFocus) elementToFocus.focus();
}

/**
 * Traps focus within a container, e.g. modal or side drawer.
 * @param {Element} container - Container element to trap focus within.
 * @param {Element} [elementToFocus=container] - Initial element to focus when trap is applied.
 */
function trapFocus(container, elementToFocus = container) {
    const focusableEls = Array.from(
        container.querySelectorAll('summary, a[href], area[href], button:not([disabled]), input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled]), object, iframe, audio[controls], video[controls], [tabindex]:not([tabindex^="-"])')
    );

    const firstEl = focusableEls[0];
    const lastEl = focusableEls[focusableEls.length - 1];

    removeTrapFocus();

    trapFocusHandlers.focusin = (evt) => {
        if (evt.target !== container && evt.target !== lastEl && evt.target !== firstEl) return;
        document.addEventListener('keydown', trapFocusHandlers.keydown);
    };

    trapFocusHandlers.focusout = () => {
        document.removeEventListener('keydown', trapFocusHandlers.keydown);
    };

    trapFocusHandlers.keydown = (evt) => {
        if (evt.code !== 'Tab') return;

        // If tab pressed on last focusable element, focus the first element.
        if (evt.target === lastEl && !evt.shiftKey) {
            evt.preventDefault();
            firstEl.focus();
        }

        //  If shift + tab pressed on the first focusable element, focus the last element.
        if ((evt.target === container || evt.target === firstEl) && evt.shiftKey) {
            evt.preventDefault();
            lastEl.focus();
        }
    };

    document.addEventListener('focusout', trapFocusHandlers.focusout);
    document.addEventListener('focusin', trapFocusHandlers.focusin);

    (elementToFocus || container).focus();
}

class Modal extends HTMLElement {
    constructor() {
        super();
        this.addEventListener('click', this.handleClick.bind(this));
    }

    /**
     * Handles 'click' events on the modal.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
        if (evt.target !== this && !evt.target.matches('.js-close-modal')) return;
        this.close();
    }

    /**
     * Opens the modal.
     * @param {Element} opener - Modal opener element.
     */
    open(opener) {
        this.setAttribute('open', '');
        this.openedBy = opener;

        trapFocus(this);
        window.pauseAllMedia();

        // Add event handler (so the bound event listener can be removed).
        this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();

        // Add event listener (for while modal is open).
        this.addEventListener('keyup', this.keyupHandler);

        // Wrap tables in a '.scrollable-table' element for a better mobile experience.
        this.querySelectorAll('table').forEach((table) => {
            if (table.closest('.product-size-guide-modal__table-scroll')) return;
            if (table.parentElement?.classList.contains('scrollable-table')) return;
            const wrapper = document.createElement('div');
            wrapper.className = 'scrollable-table';
            table.parentNode.insertBefore(wrapper, table);
            wrapper.appendChild(table);
        });
    }

    /**
     * Closes the modal.
     */
    close() {
        // remove all child dialog
        this.querySelectorAll(".dialog").forEach(item=>{item && item.hide()})
        this.removeAttribute('open');

        removeTrapFocus(this.openedBy);
        window.pauseAllMedia();

        // Remove event listener added on modal opening.
        this.removeEventListener('keyup', this.keyupHandler);
    }
}

customElements.define('modal-dialog', Modal);

class ModalOpener extends HTMLElement {
    constructor() {
        super();

        const button = this.querySelector('button');
        if (!button) return;

        button.addEventListener('click', () => {
            const modal = document.getElementById(this.dataset.modal);
            if (modal) modal.open(button);
        });
    }
}

customElements.define('modal-opener', ModalOpener);

class SizeGuideContent extends HTMLElement {
    connectedCallback() {
        if (this.dataset.ready === 'true') return;
        this.dataset.ready = 'true';

        const modal = this.closest('.product-size-guide-modal') || this;
        const desktopQuery = window.matchMedia('(min-width: 750px)');

        const rebalanceWideTable = (tableSection) => {
            const table = tableSection.querySelector('.product-size-guide-modal__table');
            const columns = Array.from(table?.querySelectorAll('col') || []);
            const headers = Array.from(table?.querySelectorAll('thead th') || []);

            if (!table || columns.length < 7 || headers.length !== columns.length || !desktopQuery.matches) {
                columns.forEach((column) => column.style.removeProperty('width'));
                return;
            }

            const equalWidth = table.clientWidth / columns.length;
            if (!equalWidth) return;

            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            const requiredWidths = columns.map(() => 0);
            const visibleCells = [
                ...headers,
                ...Array.from(table.querySelectorAll('tbody tr:not([hidden]) td'))
            ];

            visibleCells.forEach((cell, index) => {
                const columnIndex = index % columns.length;
                const style = window.getComputedStyle(cell);
                context.font = `${style.fontStyle} ${style.fontVariant} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
                const horizontalSpace = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight) + 1;
                requiredWidths[columnIndex] = Math.max(
                    requiredWidths[columnIndex],
                    context.measureText(cell.textContent.trim()).width + horizontalSpace
                );
            });

            const extraWidths = requiredWidths.map((requiredWidth) => Math.max(0, requiredWidth - equalWidth));
            const totalExtraWidth = extraWidths.reduce((total, extraWidth) => total + extraWidth, 0);
            const donorIndexes = extraWidths
                .map((extraWidth, index) => extraWidth === 0 ? index : -1)
                .filter((index) => index >= 0);
            const minimumWidth = 72;
            const donorMinimumWidths = requiredWidths.map((requiredWidth) => Math.max(requiredWidth, minimumWidth));
            const availableWidth = donorIndexes.reduce(
                (total, index) => total + Math.max(0, equalWidth - donorMinimumWidths[index]),
                0
            );
            const allocationRatio = totalExtraWidth ? Math.min(1, availableWidth / totalExtraWidth) : 0;
            const widths = extraWidths.map((extraWidth) => extraWidth
                ? equalWidth + (extraWidth * allocationRatio)
                : equalWidth
            );
            let remainingReduction = totalExtraWidth * allocationRatio;
            let activeDonors = donorIndexes.filter((index) => widths[index] > donorMinimumWidths[index]);

            while (remainingReduction > 0.01 && activeDonors.length) {
                const share = remainingReduction / activeDonors.length;
                let reducedWidth = 0;
                activeDonors = activeDonors.filter((index) => {
                    const reduction = Math.min(share, widths[index] - donorMinimumWidths[index]);
                    widths[index] -= reduction;
                    reducedWidth += reduction;
                    return widths[index] > donorMinimumWidths[index] + 0.01;
                });
                if (reducedWidth <= 0.01) break;
                remainingReduction -= reducedWidth;
            }

            columns.forEach((column, index) => {
                column.style.width = `${widths[index]}px`;
            });
        };

        const syncMobileScrollbar = (tableSection) => {
            const scroll = tableSection.querySelector('.product-size-guide-modal__table-scroll');
            const scrollbar = tableSection.querySelector('.product-size-guide-modal__table-scrollbar');
            const thumb = tableSection.querySelector('.product-size-guide-modal__table-scrollbar-thumb');
            if (!scroll || !scrollbar || !thumb) return;

            const update = () => {
                if (desktopQuery.matches) {
                    scrollbar.hidden = true;
                    return;
                }

                const maximumScroll = scroll.scrollWidth - scroll.clientWidth;
                scrollbar.hidden = maximumScroll <= 1;
                if (scrollbar.hidden) return;

                const trackWidth = scrollbar.clientWidth;
                const thumbWidth = Math.max(24, trackWidth * (scroll.clientWidth / scroll.scrollWidth));
                const scrollPosition = Math.min(maximumScroll, Math.max(0, scroll.scrollLeft));
                const offset = maximumScroll
                    ? (scrollPosition / maximumScroll) * (trackWidth - thumbWidth)
                    : 0;
                thumb.style.width = `${thumbWidth}px`;
                thumb.style.transform = `translateX(${offset}px)`;
            };

            if (!scroll.dataset.sizeGuideScrollbarReady) {
                scroll.dataset.sizeGuideScrollbarReady = 'true';
                scroll.addEventListener('scroll', update, { passive: true });
            }
            update();
        };

        const refreshTables = () => {
            this.querySelectorAll('[data-size-guide-table]').forEach((tableSection) => {
                rebalanceWideTable(tableSection);
                syncMobileScrollbar(tableSection);
            });
        };

        modal.addEventListener('click', (event) => {
            const button = event.target.closest('[data-size-guide-unit]');
            if (!button || !modal.contains(button)) return;

            const table = button.closest('[data-size-guide-table]');
            const sizeGuideTable = table || this.querySelector('[data-size-guide-table]');
            if (!sizeGuideTable) return;

            const unit = button.dataset.sizeGuideUnit;
            const unitControls = table
                ? table.querySelectorAll('[data-size-guide-unit]')
                : modal.querySelectorAll('[data-size-guide-header-units] [data-size-guide-unit]');
            unitControls.forEach((unitButton) => {
                const isActive = unitButton.dataset.sizeGuideUnit === unit;
                unitButton.classList.toggle('is-active', isActive);
                unitButton.setAttribute('aria-pressed', isActive ? 'true' : 'false');
            });
            sizeGuideTable.querySelectorAll('[data-size-guide-row]').forEach((row) => {
                row.hidden = row.dataset.sizeGuideRow !== unit;
            });
            requestAnimationFrame(refreshTables);
        });

        desktopQuery.addEventListener('change', refreshTables);
        window.addEventListener('resize', refreshTables);
        if ('ResizeObserver' in window) {
            new ResizeObserver(refreshTables).observe(modal);
        }
        modal.addEventListener('transitionend', refreshTables);
        requestAnimationFrame(refreshTables);
    }
}

customElements.define('size-guide-content', SizeGuideContent);

class SideDrawer extends HTMLElement {
    constructor() {
        super();
        this.overlay = document.querySelector('.js-overlay');
    }

    /**
     * Handles a 'click' event on the drawer.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
        if (evt.target.matches('.js-close-drawer') || evt.target === this.overlay) {
            this.close();
        }
    }

    /**
     * Opens the drawer.
     * @param {Element} [opener] - Element that triggered opening of the drawer.
     * @param {Element} [elementToFocus] - Element to focus after drawer opened.
     * @param {Function} [callback] - Callback function to trigger after the open has completed
     */
    open(opener, elementToFocus, callback) {
        this.dispatchEvent(new CustomEvent(`on:${this.dataset.name}:before-open`, {
            bubbles: true
        }));

        this.overlay.classList.add('is-visible');
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        this.opener = opener;

        trapFocus(this, elementToFocus);

        // Create event handler variables (so the bound event listeners can be removed).
        this.clickHandler = this.clickHandler || this.handleClick.bind(this);
        this.keyupHandler = (evt) => {
            if (evt.key !== 'Escape' || evt.target.closest('.cart-drawer-popup')) return;
            this.close();
        };

        // Add event listeners (for while drawer is open).
        this.addEventListener('click', this.clickHandler);
        this.addEventListener('keyup', this.keyupHandler);
        this.overlay.addEventListener('click', this.clickHandler);

        // Handle events after the drawer opens
        const transitionDuration = parseFloat(getComputedStyle(this).getPropertyValue('--longest-transition-in-ms'));
        setTimeout(() => {
            if (callback) callback();
            this.dispatchEvent(new CustomEvent(`on:${this.dataset.name}:after-open`, {
                bubbles: true
            }));
        }, transitionDuration);
    }

    /**
     * Closes the drawer.
     * @param {Function} [callback] - Call back function to trigger after the close has completed
     */
    close(callback) {
        this.dispatchEvent(new CustomEvent(`on:${this.dataset.name}:before-close`, {
            bubbles: true
        }));

        this.removeAttribute('open');
        this.setAttribute('aria-hidden', 'true');
        this.overlay.classList.remove('is-visible');

        removeTrapFocus(this.opener);

        // Remove event listeners added on drawer opening.
        this.removeEventListener('click', this.clickHandler);
        this.removeEventListener('keyup', this.keyupHandler);
        this.overlay.removeEventListener('click', this.clickHandler);

        // Handle events after the drawer closes
        const transitionDuration = parseFloat(getComputedStyle(this).getPropertyValue('--longest-transition-in-ms'));
        setTimeout(() => {
            if (callback) callback();
            this.dispatchEvent(new CustomEvent(`on:${this.dataset.name}:after-close`, {
                bubbles: true
            }));
        }, transitionDuration);
    }
}

customElements.define('side-drawer', SideDrawer);

class BuyButtons extends HTMLElement {
    constructor() {
        super();
        window.initLazyScript(this, this.initLazySection.bind(this));
    }

    initLazySection() {
        this.dynamicPaymentButtonTemplate = this.querySelector('.dynamic-payment-button-template');

        if (this.dynamicPaymentButtonTemplate) {
            this.variantIdInput = this.querySelector('[name="id"]');

            if (this.variantIdInput.value) {
                this.tryInitDynamicPaymentButton();
            } else {
                this.boundTryInitDynamicPaymentButtonOnChange = this.tryInitDynamicPaymentButton.bind(this);
                this.variantIdInput.addEventListener('change', this.boundTryInitDynamicPaymentButtonOnChange);
            }
        }
    }

    /**
     * Initialise dynamic payment button, if variant exists
     */
    tryInitDynamicPaymentButton() {
        if (this.variantIdInput.value) {
            if (this.boundTryInitDynamicPaymentButtonOnChange) {
                this.variantIdInput.removeEventListener('change', this.boundTryInitDynamicPaymentButtonOnChange);
            }

            this.dynamicPaymentButtonTemplate.insertAdjacentHTML('afterend', this.dynamicPaymentButtonTemplate.innerHTML);
            this.dynamicPaymentButtonTemplate.remove();

            if (Shopify.PaymentButton) {
                Shopify.PaymentButton.init();
            }
        }
    }
}

customElements.define('buy-buttons', BuyButtons);

const CartForm = class extends HTMLElement {
    connectedCallback() {
        this.enableAjaxUpdate = this.dataset.ajaxUpdate;
        if (this.enableAjaxUpdate) {
            this.sectionId = this.dataset.sectionId;
            this.boundRefresh = this.refresh.bind(this);
            document.addEventListener('on:cart:change', this.boundRefresh);

            theme.addDelegateEventListener(this, 'click', '.cart-item__remove', (evt) => {
                evt.preventDefault();
                this.adjustItemQuantity(evt.target.closest('.cart-item'), { to: 0 });
            });

            theme.addDelegateEventListener(this, 'click', '.quantity-down', (evt) => {
                evt.preventDefault();
                this.adjustItemQuantity(evt.target.closest('.cart-item'), { decrease: true });
            });

            theme.addDelegateEventListener(this, 'click', '.quantity-up', (evt) => {
                evt.preventDefault();
                this.adjustItemQuantity(evt.target.closest('.cart-item'), { increase: true });
            });

            theme.addDelegateEventListener(this, 'change', '.cart-item__quantity-input', (evt) => {
                this.adjustItemQuantity(evt.target.closest('.cart-item'), { currentValue: true });
            });
        }
    }

    disconnectedCallback() {
        if (this.enableAjaxUpdate) {
            document.removeEventListener('on:cart:change', this.boundRefresh);
        }
    }

    refresh() {
        this.classList.add('cart-form--refreshing');
        fetch(`${window.Shopify.routes.root}?section_id=${this.sectionId}`)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.text();
            })
            .then((response) => {
                this.refreshFromHtml(response);
            });
    }

    refreshFromHtml(html) {
        const frag = document.createDocumentFragment();
        const newContent = document.createElement('div');
        frag.appendChild(newContent);
        newContent.innerHTML = html;

        newContent.querySelectorAll('[data-cc-animate]').forEach((el) => el.removeAttribute('data-cc-animate'));

        theme.mergeNodes(newContent, this);

        this.classList.remove('cart-form--refreshing');
        this.querySelectorAll('.merge-item-refreshing').forEach((el) => el.classList.remove('merge-item-refreshing'));

        this.dispatchEvent(
            new CustomEvent('on:cart:after-merge', { bubbles: true, cancelable: false })
        );

        if (theme.settings.afterAddToCart === 'drawer' && this.closest('.drawer') && !this.closest('.drawer').hasAttribute('open')) {
            document.dispatchEvent(
                new CustomEvent('theme:open-cart-drawer', { bubbles: true, cancelable: false })
            );
        }
    }

    adjustItemQuantity(item, change) {
        const quantityInput = item.querySelector('.cart-item__quantity-input');

        let newQuantity = parseInt(quantityInput.value, 10);
        if (typeof change.to !== 'undefined') {
            newQuantity = change.to;
            quantityInput.value = newQuantity;
        } else if (change.increase) {
            newQuantity += quantityInput.step || 1;
            quantityInput.value = newQuantity;
        } else if (change.decrease) {
            newQuantity -= quantityInput.step || 1;
            quantityInput.value = newQuantity;
        } else if (change.currentValue);

        if (quantityInput.max && parseInt(quantityInput.value, 10) > parseInt(quantityInput.max, 10)) {
            newQuantity = quantityInput.max;
            quantityInput.value = newQuantity;
            theme.showQuickPopup(theme.strings.cartItemsQuantityError.replace('[QUANTITY]', quantityInput.max), quantityInput);
        }

        clearTimeout(this.adjustItemQuantityTimeout);
        this.adjustItemQuantityTimeout = setTimeout( async () => {
            const updateParam = { updates: {},attributes: {} };
            const ga4Identifiers = await getGA4Identifiers();
            if(ga4Identifiers){
                updateParam.attributes['_ga4_client_id'] = ga4Identifiers.client_id;
                updateParam.attributes['_ga4_session_id'] = ga4Identifiers.session_id;
                updateParam.attributes['_ga4_session_number'] = ga4Identifiers.session_number;
            }
            this.querySelectorAll('.cart-item__quantity-input:not([disabled])').forEach((el) => {
                updateParam.updates[el.dataset.key] = el.value;
                if (el.value !== el.dataset.initialValue) {
                    el.closest('[data-merge-list-item]').classList.add('merge-item-refreshing');
                }
            });
            fetch(theme.routes.cartUpdate, {
                method: 'POST',
                body: JSON.stringify(updateParam),
                headers: {
                    'Content-Type': 'application/json'
                }
            })
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(`HTTP error! Status: ${response.status}`);
                    }
                    document.dispatchEvent(
                        new CustomEvent('on:cart:change', { bubbles: true, cancelable: false })
                    );
                })
                .catch((error) => {
                    // eslint-disable-next-line no-console
                    console.log(error.message);
                    this.dispatchEvent(new CustomEvent('on:cart:error', {
                        bubbles: true,
                        detail: {
                            error: error.message
                        }
                    }));
                    // Uncertainty... Reload page.
                    window.location.reload();
                });
        }, newQuantity === 0 ? 10 : 700);
    }
};

window.customElements.define('cart-form', CartForm);

const CCCartCrossSell = class extends HTMLElement {
    init() {
        this.productList = this.querySelector('.product-grid');

        if (this.dataset.from) {
            fetch(this.dataset.from)
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(`HTTP error! Status: ${response.status}`);
                    }
                    return response.text();
                })
                .then((response) => {
                    const frag = document.createDocumentFragment();
                    const newContent = document.createElement('div');
                    frag.appendChild(newContent);
                    newContent.innerHTML = response;

                    const pl = newContent.querySelector('.product-grid');
                    if (pl) {
                        this.productList.innerHTML = pl.innerHTML;
                        this.querySelectorAll('.product-block').forEach((el) => el.classList.add('slider__item'));
                        this.querySelectorAll('carousel-slider').forEach((el) => el.refresh());
                    } else {
                        this.classList.add('hidden');
                    }
                });
        }
    }
};

window.customElements.define('cc-cart-cross-sell', CCCartCrossSell);

const CCFetchedContent = class extends HTMLElement {
    connectedCallback() {
        fetch(this.dataset.url)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.text();
            })
            .then((response) => {
                const frag = document.createDocumentFragment();
                const fetchedContent = document.createElement('div');
                frag.appendChild(fetchedContent);
                fetchedContent.innerHTML = response;

                const replacementContent = fetchedContent.querySelector(`[data-id="${this.dataset.id}"]`);
                if (replacementContent) {
                    this.innerHTML = replacementContent.innerHTML;
                }
            });
    }
};

window.customElements.define('cc-fetched-content', CCFetchedContent);

/**
 * Returns a function, that, when invoked, will only be triggered at most once during
 * a given window of time.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Time window (in milliseconds).
 * @returns {Function}
 */
function throttle(fn, wait = 300) {
    let throttleTimeoutId = -1;
    let tick = false;

    return () => {
        clearTimeout(throttleTimeoutId);
        throttleTimeoutId = setTimeout(fn, wait);

        if (!tick) {
            fn.call();
            tick = true;
            setTimeout(() => {
                tick = false;
            }, wait);
        }
    };
}

const FilterContainer = class extends HTMLElement {
    constructor() {
        super();
        this.section = this.closest('.shopify-section');
        this.filters = this.querySelector('.filters');

        const utilityBar = document.querySelector('.utility-bar');
        if (utilityBar) {
            // duplicate utility bar for mobile
            this.utilBarClone = utilityBar.cloneNode(true);
            this.utilBarClone.classList.add('utility-bar--sticky-mobile-copy');
            this.utilBarClone.removeAttribute('data-ajax-container');
            utilityBar.insertAdjacentElement('afterend', this.utilBarClone);
            // ensure ids are unique
            theme.suffixIds(this.utilBarClone, 'dupe');

            this.previousScrollTop = window.scrollY;
            this.throttledCheckStickyScroll = throttle(this.checkStickyScroll.bind(this), 200);
        }

        if (this.filters) {
            // auto-select availability
            this.allowAutoApplyHideUnavailable = this.filters.dataset.autoApplyHideUnavailable === 'true';
            if (this.allowAutoApplyHideUnavailable) {
                theme.addDelegateEventListener(this, 'change', '.filter-group__checkbox, .cc-price-range__input', (evt, delEl) => {
                    if (this.allowAutoApplyHideUnavailable) {
                        if ((delEl.type === 'checkbox' && delEl.checked) || (delEl.type === 'text' && delEl.value)) {
                            const toEnable = this.filters.querySelector('[name="filter.v.availability"][value="1"]');
                            if (toEnable) {
                                toEnable.checked = true;
                                toEnable.dispatchEvent(
                                    new CustomEvent('change', { bubbles: true, cancelable: false })
                                );
                            }
                        }
                    }
                });

                theme.addDelegateEventListener(this, 'change', '.filter-group--availability .filter-toggle__input', (evt, delEl) => {
                    if (delEl.checked && delEl.value === '1') {
                        this.allowAutoApplyHideUnavailable = false;
                    }
                });
            }

            // ajax filter
            if (this.dataset.ajaxFiltering === 'true') {
                // ajax load form submission
                const debouncedAjaxLoadForm = debounce(this.ajaxLoadForm.bind(this), 700);
                theme.addDelegateEventListener(this, 'change', '#CollectionFilterForm', debouncedAjaxLoadForm);
                theme.addDelegateEventListener(this, 'submit', '#CollectionFilterForm', debouncedAjaxLoadForm);
            } else {
                theme.addDelegateEventListener(this, 'change', '#CollectionFilterForm', (_evt, delEl) => delEl.submit());
            }

            // init things that may need re-initialising on ajax load
            this.initFiltersEtc();
        }

        // ajax sort, filter, and pagination
        if (this.dataset.ajaxFiltering === 'true') {
            // ajax load on link click
            theme.addDelegateEventListener(this.section, 'click', '.link-dropdown__link, .filter-group__applied-item, .filter-group__clear-link, .pagination a', (evt, delEl) => {
                evt.preventDefault();
                this.ajaxLoadUrl(delEl.href);
            });
        }



    }

    connectedCallback() {
        // scroll
        if (this.throttledCheckStickyScroll) {
            window.addEventListener('scroll', this.throttledCheckStickyScroll);
        }

        // back button
        if (this.dataset.ajaxFiltering === 'true') {
            this.boundAjaxPopState = this.ajaxPopState.bind(this);
            window.addEventListener('popstate', this.boundAjaxPopState);
        }

        // layout switch
        if (this.section.querySelector('.layout-switchers')) {
            this.boundSwitchGridLayout = theme.addDelegateEventListener(this.section, 'click', '.layout-switch', this.switchGridLayout.bind(this));
        }

        // show/hide filters
        this.delegatedToggleFiltersCallback = theme.addDelegateEventListener(this.section, 'click', '[data-toggle-filters]', (evt) => {
            evt.preventDefault();
            this.classList.toggle('filter-container--show-filters-desktop');
            this.classList.toggle('filter-container--show-filters-mobile');
            const isNowVisible = this.classList.contains('filter-container--show-filters-desktop');
            this.section.querySelectorAll('.toggle-btn[data-toggle-filters]').forEach((el) => {
                el.classList.toggle('toggle-btn--revealed-desktop', isNowVisible);
            });
        });

        //ga4 log
        // console.log('ga4 clicked log')
        // this.section.addEventListener('click', (e) => {
        //   const card = e.target.closest('.product-block');
        //   if (!card) return;
        //    // 只处理商品卡片点击（避免点击收藏、quick view）
        //   const link = card.querySelector('.product-link');
        //   if (link && !link.contains(e.target)) return; // 如果点的不是链接，忽略
        //    console.log('Clicked product:', card.dataset.productId,card.dataset);

        // });

    }

    disconnectedCallback() {
        if (this.boundCheckStickyScroll) {
            window.removeEventListener('scroll', this.throttledCheckStickyScroll);
        }

        if (this.boundAjaxPopState) {
            window.removeEventListener('popstate', this.boundAjaxPopState);
        }
    }

    initFiltersEtc() {
        this.classList.add('filter-container--mobile-initialised');

        // append query vars onto sort urls (e.g. filters, vendor collection)
        if (window.location.href.indexOf('?') >= 0) {
            document.querySelectorAll('#sort-dropdown-options .link-dropdown__link').forEach((el) => {
                const queryTerms = window.location.href.split('?')[1].split('&');
                let newHref = el.href;
                queryTerms.forEach((term) => {
                    if (term.indexOf('sort_by=') === -1) {
                        newHref += `&${term}`;
                    }
                });
                el.href = newHref;
            });
        }
    }

    switchGridLayout(evt) {
        evt.preventDefault();
        if (evt.target.classList.contains('layout-switch--one-column')) {
            this.querySelectorAll('.product-grid').forEach((el) => {
                el.classList.remove('product-grid--per-row-mob-2');
                el.classList.add('product-grid--per-row-mob-1');
            });
        } else {
            this.querySelectorAll('.product-grid').forEach((el) => {
                el.classList.remove('product-grid--per-row-mob-1');
                el.classList.add('product-grid--per-row-mob-2');
            });
        }
        evt.target.classList.add('layout-switch--active');
        (evt.target.nextElementSibling || evt.target.previousElementSibling).classList.remove('layout-switch--active');
    }

    checkStickyScroll() {
        const utilityBarOffsetY = theme.getOffsetTopFromDoc(this.section.querySelector('.utility-bar'));
        if (window.innerWidth < 768
            && this.previousScrollTop > window.scrollY
            && window.scrollY > utilityBarOffsetY) {
            document.body.classList.add('utility-bar-sticky-mobile-copy-reveal');
        } else {
            document.body.classList.remove('utility-bar-sticky-mobile-copy-reveal');
        }
        this.previousScrollTop = window.scrollY;
    }

    ajaxLoadForm(evt) {
        if (evt.type === 'submit') {
            evt.preventDefault();
        }
        const queryVals = [];
        this.filters.querySelectorAll('input, select').forEach((input) => {
            if (
                ((input.type !== 'checkbox' && input.type !== 'radio') || input.checked) // is an active input value
                && input.value !== '' // has a value
            ) {
                queryVals.push([input.name, encodeURIComponent(input.value)]);
            }
        });
        // new url
        let newUrl = window.location.pathname;
        queryVals.forEach((value) => {
            newUrl += `&${value[0]}=${value[1]}`;
        });
        newUrl = newUrl.replace('&', '?');
        // load
        this.ajaxLoadUrl.call(this, newUrl);
    }

    ajaxPopState() {
        this.ajaxLoadUrl.call(this, document.location.href, true);
    }

    ajaxLoadUrl(url, noPushState) {
        if (!noPushState) {
            // update url history
            let fullUrl = url;
            if (fullUrl.slice(0, 1) === '/') {
                fullUrl = `${window.location.protocol}//${window.location.host}${fullUrl}`;
            }
            window.history.pushState({ path: fullUrl }, '', fullUrl);
        }

        // limit render to section, if possible (added after pushState)
        let fetchUrl = url;
        if (this.dataset.filterSectionId) {
            fetchUrl = `${url}${url.indexOf('?') >= 0 ? '&' : '?'}section_id=${this.dataset.filterSectionId}`;
        }

        // start fetching URL
        const refreshContainerSelector = '[data-ajax-container]';
        const ajaxContainers = this.section.querySelectorAll(refreshContainerSelector);

        // loading state
        ajaxContainers.forEach((el) => el.classList.add('ajax-loading'));

        // cancel current fetch & fetch next
        if (this.ajaxLoadUrlFetchAbortController) {
            this.ajaxLoadUrlFetchAbortController.abort('Existing request not needed');
        }
        this.ajaxLoadUrlFetchAbortController = new AbortController();

        fetch(fetchUrl, {
            method: 'get',
            signal: this.ajaxLoadUrlFetchAbortController.signal
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.text();
            })
            .then((response) => {
                // save active element
                if (document.activeElement) {
                    this.activeElementId = document.activeElement.id;
                }

                // before replace - check if container scrolls, if so find item at scroll top
                const scrollContainer = this.querySelector('.filters');
                let elAboveScrollTopData = null;
                if (scrollContainer && getComputedStyle(scrollContainer).overflow === 'auto') {
                    const allFilterChildren = scrollContainer.querySelectorAll('.filters *');
                    let elAboveScrollTop = allFilterChildren[0];
                    for (let i = 1; i < allFilterChildren.length; i += 1) {
                        if (allFilterChildren[i].offsetTop) {
                            // if above fold
                            if (allFilterChildren[i].offsetTop < scrollContainer.scrollTop) {
                                // if below previously assigned element, assign
                                if (allFilterChildren[i].offsetTop > elAboveScrollTop.offsetTop) {
                                    elAboveScrollTop = allFilterChildren[i];
                                }
                            } else {
                                break;
                            }
                        }
                    }
                    if (elAboveScrollTop.offsetTop === 0) {
                        elAboveScrollTop = false;
                    } else {
                        // determine how to identify this element
                        elAboveScrollTopData = {
                            selector: '',
                            textContent: elAboveScrollTop.textContent,
                            extraScrollOffset: scrollContainer.scrollTop - elAboveScrollTop.offsetTop
                        };
                        const attributeNames = elAboveScrollTop.getAttributeNames();
                        for (let i = 0; i < attributeNames.length; i += 1) {
                            const attrName = attributeNames[i];
                            if (attrName === 'class') {
                                Array.from(elAboveScrollTop.classList).filter((a) => a !== 'filter-group__item--disabled').forEach((cl) => {
                                    elAboveScrollTopData.selector += `.${cl}`;
                                });
                            } else {
                                elAboveScrollTopData.selector += `[${attrName}="${CSS.escape(elAboveScrollTop.getAttribute(attrName))}"]`;
                            }
                        }
                    }
                }

                // replace contents
                const template = document.createElement('template');
                template.innerHTML = response;
                const newAjaxContainers = template.content.querySelectorAll(refreshContainerSelector);
                newAjaxContainers.forEach((el, index) => {
                    ajaxContainers[index].innerHTML = el.innerHTML;
                });

                // init js
                this.initFiltersEtc();

                // after replace
                if (elAboveScrollTopData) {
                    this.querySelectorAll(elAboveScrollTopData.selector).forEach((el) => {
                        if (el.textContent === elAboveScrollTopData.textContent) {
                            scrollContainer.scrollTop = el.offsetTop + elAboveScrollTopData.extraScrollOffset;
                        }
                    });
                }

                // update mobile count
                if (this.utilBarClone) {
                    const from = document.querySelector('.utility-bar:not(.utility-bar--sticky-mobile-copy) .utility-bar__centre');
                    const to = this.utilBarClone.querySelector('.utility-bar__centre');
                    if (from && to) {
                        to.innerHTML = from.innerHTML;
                    }
                }

                // remove loading state
                ajaxContainers.forEach((el) => el.classList.remove('ajax-loading'));

                // restore active element
                if (this.activeElementId) {
                    const el = document.getElementById(this.activeElementId);
                    if (el) {
                        el.focus();
                    }
                }

                // scroll viewport (must be done after any page size changes)
                const scrollToY = theme.getOffsetTopFromDoc(this.section.querySelector('[data-ajax-scroll-to]')) - document.querySelector('.section-header').clientHeight;
                window.scrollTo({
                    top: scrollToY,
                    behavior: 'smooth'
                });
            })
            .catch((error) => {
                console.warn(error);
            });
    }
};

window.customElements.define('filter-container', FilterContainer);

/* eslint-disable max-len */
const GalleryViewer = class extends HTMLElement {
    connectedCallback() {
        if (!this.initialised) {
            this.initialised = true;

            // ui
            this.classList.add('gallery-viewer--pre-reveal');
            this.zoomContainer = this.querySelector('.gallery-viewer__zoom-container');
            this.thumbContainer = this.querySelector('.gallery-viewer__thumbs');
            this.controlsContainer = this.querySelector('.gallery-viewer__controls');
            this.previousBtn = this.querySelector('.gallery-viewer__prev');
            this.nextBtn = this.querySelector('.gallery-viewer__next');

            // consts
            this.wheelZoomMultiplier = -0.001;
            this.pinchZoomMultiplier = 0.003;
            this.touchPanModifier = 1.0;

            // vars
            this.currentZoomImage = null;
            this.currentTransform = {
                panX: 0,
                panY: 0,
                zoom: 1
            };
            this.pinchTracking = {
                isTracking: false,
                lastPinchDistance: 0
            };
            this.touchTracking = {
                isTracking: false,
                lastTouchX: 0,
                lastTouchY: 0
            };

            // events
            theme.addDelegateEventListener(this, 'click', '.gallery-viewer__thumb', this.onThumbClick.bind(this));
            this.addEventListener('touchend', this.stopTrackingTouch.bind(this));
            this.addEventListener('touchmove', this.trackInputMovement.bind(this));
            this.addEventListener('mousemove', this.trackInputMovement.bind(this));
            this.addEventListener('wheel', this.trackWheel.bind(this));
            // prevent pan while swiping thumbnails
            this.thumbContainer.addEventListener('touchmove', (evt) => evt.stopPropagation());
            this.previousBtn.addEventListener('click', this.selectPreviousThumb.bind(this));
            this.nextBtn.addEventListener('click', this.selectNextThumb.bind(this));
            this.zoomContainer.addEventListener('click', this.onZoomContainerClick.bind(this));
            new ResizeObserver(() => this.setInitialImagePosition()).observe(this);
        }

        document.documentElement.classList.add('gallery-viewer-open');
        this.addEventListener('keyup', this.handleKeyup.bind(this));
        setTimeout(() => this.classList.remove('gallery-viewer--pre-reveal'), 10);
    }

    // eslint-disable-next-line class-methods-use-this
    disconnectedCallback() {
        document.documentElement.classList.remove('gallery-viewer-open');
    }

    static createEl(type, className, appendTo, innerHTML) {
        const el = document.createElement(type);
        el.className = className;
        if (type === 'a') {
            el.href = '#';
        }
        if (appendTo) {
            appendTo.insertAdjacentElement('beforeend', el);
        }
        if (innerHTML) {
            el.innerHTML = innerHTML;
        }
        return el;
    }

    init(currentFullUrl) {
        this.selectThumb([...this.thumbContainer.children].find((el) => el.dataset.zoomUrl === currentFullUrl) || this.thumbContainer.firstElementChild);
    }

    panZoomImageFromCoordinate(inputX, inputY) {
        // do nothing if the image fits, pan if not
        const doPanX = this.currentZoomImage.clientWidth > this.clientWidth;
        const doPanY = this.currentZoomImage.clientHeight > this.clientHeight;

        if (doPanX || doPanY) {
            const midX = this.clientWidth / 2;
            const midY = this.clientHeight / 2;

            const offsetFromCentreX = inputX - midX;
            const offsetFromCentreY = inputY - midY;

            // the offsetMultipler ensures it can only pan to the edge of the image, no further
            let finalOffsetX = 0;
            let finalOffsetY = 0;

            if (doPanX) {
                const offsetMultiplierX = ((this.currentZoomImage.clientWidth - this.clientWidth) / 2) / midX;
                finalOffsetX = Math.round(-offsetFromCentreX * offsetMultiplierX);
            }
            if (doPanY) {
                const offsetMultiplierY = ((this.currentZoomImage.clientHeight - this.clientHeight) / 2) / midY;
                finalOffsetY = Math.round(-offsetFromCentreY * offsetMultiplierY);
            }

            this.currentTransform.panX = finalOffsetX;
            this.currentTransform.panY = finalOffsetY;
            this.alterCurrentPanBy(0, 0); // sanitise
            this.updateImagePosition();
        }
    }

    alterCurrentPanBy(x, y) {
        this.currentTransform.panX += x;
        // limit offset to keep most of image on screen
        let panXMax = (this.currentZoomImage.naturalWidth * this.currentTransform.zoom - this.clientWidth) / 2.0;
        panXMax = Math.max(panXMax, 0);
        this.currentTransform.panX = Math.min(this.currentTransform.panX, panXMax);
        this.currentTransform.panX = Math.max(this.currentTransform.panX, -panXMax);

        this.currentTransform.panY += y;
        let panYMax = (this.currentZoomImage.naturalHeight * this.currentTransform.zoom - this.clientHeight) / 2.0;
        panYMax = Math.max(panYMax, 0);
        this.currentTransform.panY = Math.min(this.currentTransform.panY, panYMax);
        this.currentTransform.panY = Math.max(this.currentTransform.panY, -panYMax);
        this.updateImagePosition();
    }

    setCurrentTransform(panX, panY, zoom) {
        this.currentTransform.panX = panX;
        this.currentTransform.panY = panY;
        this.currentTransform.zoom = zoom;
        this.alterCurrentTransformZoomBy(0);
    }

    alterCurrentTransformZoomBy(delta) {
        this.currentTransform.zoom += delta;
        // do not zoom out further than fit
        const maxZoomX = this.clientWidth / this.currentZoomImage.naturalWidth;
        const maxZoomY = this.clientHeight / this.currentZoomImage.naturalHeight;
        this.currentTransform.zoom = Math.max(this.currentTransform.zoom, Math.min(maxZoomX, maxZoomY));

        // do not zoom in further than native size
        this.currentTransform.zoom = Math.min(this.currentTransform.zoom, 1.0);

        // reasses pan bounds
        this.alterCurrentPanBy(0, 0);
        this.updateImagePosition();
    }

    setInitialImagePosition() {
        this.currentZoomImage.style.top = `${this.clientHeight / 2 - this.currentZoomImage.clientHeight / 2}px`;
        this.currentZoomImage.style.left = `${this.clientWidth / 2 - this.currentZoomImage.clientWidth / 2}px`;
        this.setCurrentTransform(0, 0, 0); // centre, zoomed out
        this.classList.toggle('gallery-viewer--zoomable', this.currentZoomImage.naturalWidth > this.clientWidth || this.currentZoomImage.naturalHeight > this.clientHeight);
    }

    updateImagePosition() {
        this.currentZoomImage.style.transform = `translate3d(${this.currentTransform.panX}px, ${this.currentTransform.panY}px, 0) scale(${this.currentTransform.zoom})`;
    }

    selectThumb(thumb) {
        [...thumb.parentElement.children].forEach((el) => {
            if (el === thumb) {
                el.classList.add('gallery-viewer__thumb--active');
            } else {
                el.classList.remove('gallery-viewer__thumb--active');
            }
        });
        // replace zoom image
        this.zoomContainer.classList.add('gallery-viewer__zoom-container--loading');
        this.currentZoomImage = GalleryViewer.createEl('img', 'gallery-viewer__zoom-image');
        this.currentZoomImage.alt = '';
        this.currentZoomImage.style.visibility = 'hidden';
        this.currentZoomImage.onload = () => {
            this.zoomContainer.classList.remove('gallery-viewer__zoom-container--loading');
            this.currentZoomImage.style.visibility = '';
            this.setInitialImagePosition();
        };
        this.currentZoomImage.src = thumb.dataset.zoomUrl;
        this.zoomContainer.replaceChildren(this.currentZoomImage);
    }

    selectPreviousThumb(evt) {
        if (evt) evt.preventDefault();
        if (this.thumbContainer.childElementCount < 2) return;

        let previous = this.thumbContainer.querySelector('.gallery-viewer__thumb--active').previousElementSibling;
        while (!previous || !previous.offsetParent) {
            if (!previous) {
                previous = this.thumbContainer.lastElementChild;
            } else {
                previous = previous.previousElementSibling;
            }
        }
        this.selectThumb(previous);
    }

    selectNextThumb(evt) {
        if (evt) evt.preventDefault();
        if (this.thumbContainer.childElementCount < 2) return;

        let next = this.thumbContainer.querySelector('.gallery-viewer__thumb--active').nextElementSibling;
        while (!next || !next.offsetParent) {
            if (!next) {
                next = this.thumbContainer.firstElementChild;
            } else {
                next = next.nextElementSibling;
            }
        }
        this.selectThumb(next);
    }

    stopTrackingTouch() {
        this.pinchTracking.isTracking = false;
        this.touchTracking.isTracking = false;
    }

    trackInputMovement(evt) {
        evt.preventDefault();
        if (evt.type === 'touchmove' && evt.touches.length > 0) {
            // pan
            const touch1 = evt.touches[0];
            if (!this.touchTracking.isTracking) {
                this.touchTracking.isTracking = true;
                this.touchTracking.lastTouchX = touch1.clientX;
                this.touchTracking.lastTouchY = touch1.clientY;
            } else {
                this.alterCurrentPanBy(
                    (touch1.clientX - this.touchTracking.lastTouchX) * this.touchPanModifier,
                    (touch1.clientY - this.touchTracking.lastTouchY) * this.touchPanModifier
                );
                this.touchTracking.lastTouchX = touch1.clientX;
                this.touchTracking.lastTouchY = touch1.clientY;
            }

            if (evt.touches.length === 2) {
                // pinch
                const touch2 = evt.touches[1];
                const pinchDistance = Math.sqrt((touch1.clientX - touch2.clientX) ** 2 + (touch1.clientY - touch2.clientY) ** 2);
                if (!this.pinchTracking.isTracking) {
                    this.pinchTracking.lastPinchDistance = pinchDistance;
                    this.pinchTracking.isTracking = true;
                } else {
                    const pinchDelta = pinchDistance - this.pinchTracking.lastPinchDistance;
                    this.alterCurrentTransformZoomBy(pinchDelta * this.pinchZoomMultiplier);
                    this.pinchTracking.lastPinchDistance = pinchDistance;
                }
            } else {
                this.pinchTracking.isTracking = false;
            }
        } else {
            // mousemove
            this.panZoomImageFromCoordinate(evt.clientX, evt.clientY);
        }
    }

    trackWheel(evt) {
        evt.preventDefault();
        if (evt.deltaY !== 0) {
            this.alterCurrentTransformZoomBy(evt.deltaY * this.wheelZoomMultiplier);
        }
    }

    onThumbClick(evt, thumb) {
        evt.preventDefault();
        this.selectThumb(thumb);
    }

    onZoomContainerClick(evt) {
        evt.preventDefault();

        if (this.currentTransform.zoom === 1.0) {
            this.currentTransform.zoom = 0;
            this.alterCurrentTransformZoomBy(0);
        } else {
            this.currentTransform.zoom = 1;
            this.alterCurrentTransformZoomBy(0);
            this.panZoomImageFromCoordinate(evt.clientX, evt.clientY);
        }
    }

    handleKeyup(evt) {
        switch (evt.key) {
            case 'ArrowLeft':
                evt.preventDefault();
                this.selectPreviousThumb();
                break;
            case 'ArrowRight':
                evt.preventDefault();
                this.selectNextThumb();
                break;
        }
    }
};

window.customElements.define('gallery-viewer', GalleryViewer);

const LinkDropdown = class extends HTMLElement {
    constructor() {
        super();

        this.open = false;
        this.button = this.querySelector('.link-dropdown__button');
        this.button.addEventListener('click', this.toggle.bind(this));
    }

    connectedCallback() {
        if (this.open) {
            this.addDismissListener();
        }
    }

    disconnectedCallback() {
        if (this.open) {
            this.removeDismissListener();
        }
    }

    toggle(evt, isDismiss) {
        if (!isDismiss) {
            evt.preventDefault();
            evt.stopPropagation();
        }

        const doExpand = this.button.getAttribute('aria-expanded') === 'false';
        this.button.setAttribute('aria-expanded', doExpand);
        this.button.style.width = `${this.button.clientWidth}px`;

        let newWidth = null;
        const optsBox = this.button.nextElementSibling;
        const isLeftAligned = this.button.closest('.link-dropdown').classList.contains('link-dropdown--left-aligned');
        if (!isLeftAligned) {
            if (doExpand) {
                newWidth = optsBox.clientWidth;
                // rtl - could be either side
                if (document.querySelector('html[dir=rtl]')) {
                    newWidth += parseInt(getComputedStyle(optsBox).left, 10);
                } else {
                    newWidth += parseInt(getComputedStyle(optsBox).right, 10);
                }
                newWidth -= parseInt(getComputedStyle(optsBox.querySelector('.link-dropdown__link')).paddingInlineStart, 10);
            } else {
                newWidth = parseInt(getComputedStyle(this.button).paddingInlineEnd, 10) + Math.ceil(this.button.querySelector('.link-dropdown__button-text').getBoundingClientRect().width);
            }
            setTimeout(() => {
                this.button.style.width = `${newWidth}px`;
            }, 10);
        }

        if (doExpand) {
            this.open = true;
            this.addDismissListener();
        } else {
            this.open = false;
            this.removeDismissListener();
        }
    }

    addDismissListener() {
        this.dismissCallback = this.toggle.bind(this, true);
        document.addEventListener('click', this.dismissCallback);
    }

    removeDismissListener() {
        document.removeEventListener('click', this.dismissCallback);
        this.dismissCallback = null;
    }
};

window.customElements.define('link-dropdown', LinkDropdown);

const MainNavigation = class extends HTMLElement {
    constructor() {
        super();
        this.showerList = new Map()
        this.navImpressionObservers = new Set();
        this.navHoverDelay = 250;
        this.navLastOpenDropdown = null;
        this.navOpenTimeoutId = -1;

        // hover
        this.querySelectorAll('.navigation__tier-1 > .navigation__item--with-children').forEach((el) => {
            el.addEventListener('mouseenter', this.onNavParentHoverIn.bind(this));
        });
        this.querySelectorAll('.navigation__tier-1 > .navigation__item--with-children').forEach((el) => {
            el.addEventListener('mouseleave', this.onNavParentHoverOut.bind(this));
        });

        // touch
        theme.addDelegateEventListener(this, 'touchstart', '.navigation__tier-1 > .navigation__item--with-children > .navigation__link', (evt, el) => { this.handleTouch(evt, el); }, { passive: true });
        theme.addDelegateEventListener(this, 'touchend', '.navigation__tier-1 > .navigation__item--with-children > .navigation__link', (evt, el) => { this.handleTouch(evt, el); });
        
        theme.addDelegateEventListener(this, 'click', '.navigation__tier-1 > .navigation__item--with-children > .navigation__link', this.onNavParentHoverIn.bind(this));

        // keypress
        theme.addDelegateEventListener(this, 'keydown', '.navigation__tier-1 > .navigation__item--with-children > .navigation__link', this.onNavKeydown.bind(this));


        // click navigation__tier-3
   
        theme.addDelegateEventListener(this, 'click', '.navigation__tier-2 > .navigation__item > .navigation__link:not(.navigation__column-title)', this.onNavClick.bind(this));
        theme.addDelegateEventListener(this, 'click','.navigation__tier-3  .navigation__link', this.onNavClick.bind(this))

        
        const navInner = document.querySelector('.logo-area__left__inner #proxy-nav')
        if (navInner) {
            theme.addDelegateEventListener(navInner, 'click', '.navigation__tier-1 .navigation__link', function(evt){
                const clickTag = evt.target.closest('.navigation__link');
                if (!clickTag) return;
                const parent = clickTag.dataset.gaParent
                const href = clickTag.dataset.gaUrl
                const level = clickTag.dataset.gaLevel
                const name = clickTag.dataset.gaName
                gtag('event', 'nav_selected', {
                    parent,
                    href,
                    level,
                    name,
                    isMobile: isMobile?true:false,
                    type:'anchor',
                    'transport_type': 'beacon'  
            });
              });
        }
          
          
        // click banner

        theme.addDelegateEventListener(this,'click','.navigation__column--promotion .menu-promotion__link',function(evt){
                const clickTag = evt.target.closest('.menu-promotion').querySelector('.menu-promotion__link')
                console.log(clickTag)
                const parent = clickTag.dataset.gaParent
                const href = clickTag.dataset.gaUrl
                const level = clickTag.dataset.gaLevel
                const name = clickTag.dataset.gaName
                const image = clickTag.dataset.gaImage
                console.log({
                        parent,
                        href,
                        level,
                        name,
                        device:'pc',
                        type:'image',
                        image_url:image,
                        'transport_type': 'beacon'  
                    })
                gtag('event', 'nav_selected', {
                        parent,
                        href,
                        level,
                        name,
                        device:'pc',
                        type:'image',
                        image_url:image,
                        'transport_type': 'beacon'  
                });
        })  

        // click #-link - open child nav
        this.querySelectorAll('.navigation__link[href="#"][aria-haspopup="true"]').forEach((el) => {
            el.addEventListener('click', (evt) => {
                evt.preventDefault();
                evt.currentTarget.nextElementSibling.dispatchEvent(new Event('click', { bubbles: true }));
            });
        });

        // transparent header hover
        this.addEventListener('mouseenter', this.handleNavHover.bind(this));
        this.addEventListener('mouseleave', this.handleNavHover.bind(this));
    }

    ensureDropdownsInPageBounds() {
        this.querySelectorAll('.navigation__item--with-small-menu > .navigation__child-tier').forEach((el) => {
            const parentBcr = el.parentElement.getBoundingClientRect();
            const childBcr = el.getBoundingClientRect();
            const diff = window.innerWidth - (parentBcr.left + childBcr.width);
            if (diff < 25) {
                el.style.setProperty('--nav-side-offset', `${diff - 25}px`);
            }
        });
    }

    connectedCallback() {
        // shuffle dropdowns off RHS of viewport
        const debouncedEnsureDropdownsInPageBounds = debounce(
            this.ensureDropdownsInPageBounds.bind(this),
            300
        );
        debouncedEnsureDropdownsInPageBounds();
        this.resizeObserver = new ResizeObserver((entries) => {
            for (let i = 0; i < entries.length; i += 1) {
                const entry = entries[i];
                if (entry.contentBoxSize || entry.contentRect) {
                    debouncedEnsureDropdownsInPageBounds();
                }
            }
        });
        this.resizeObserver.observe(this);

        // proxy for desktop nav interaction events
        this.proxyTier1Nav = document.getElementById(this.dataset.proxyNav);

        if (this.proxyTier1Nav) {
            // reposition small menu
            this.proxyTier1NavBoundEvents = [];

            const boundOnProxyNavEnterSmallMenu = this.onProxyNavEnterSmallMenu.bind(this);
            this.proxyTier1Nav.querySelectorAll('.navigation__item--with-small-menu').forEach((el) => {
                el.addEventListener('mouseenter', boundOnProxyNavEnterSmallMenu);
                this.proxyTier1NavBoundEvents.push({ element: el, name: 'mouseenter', fn: boundOnProxyNavEnterSmallMenu });
            });

            this.proxyTier1NavBoundEvents.push({
                element: this.proxyTier1Nav,
                name: 'touchstart',
                fn: theme.addDelegateEventListener(this.proxyTier1Nav, 'touchstart', '.navigation__item--with-small-menu', this.onProxyNavEnterSmallMenu.bind(this), { passive: true })
            });

            // transfer all events to main menu
            const onProxyNavEnterLeaveLargeMenu = (evt) => {
                const elIndex = [...evt.currentTarget.parentNode.children].indexOf(evt.currentTarget);
                this.querySelectorAll('.navigation__tier-1 > .navigation__item')[elIndex].dispatchEvent(new Event(evt.type));
            };
            this.proxyTier1Nav.querySelectorAll('.navigation__tier-1 > .navigation__item--with-children').forEach((el) => {
                el.addEventListener('mouseenter', onProxyNavEnterLeaveLargeMenu);
                this.proxyTier1NavBoundEvents.push({ element: el, name: 'mouseenter', fn: onProxyNavEnterLeaveLargeMenu });
                el.addEventListener('mouseleave', onProxyNavEnterLeaveLargeMenu);
                this.proxyTier1NavBoundEvents.push({ element: el, name: 'mouseleave', fn: onProxyNavEnterLeaveLargeMenu });
            });

            const eventNames = ['touchstart', 'touchend', 'click'];
            for (let i = 0; i < eventNames.length; i += 1) {
                const eventName = eventNames[i];
                this.proxyTier1NavBoundEvents.push({
                    element: this.proxyTier1Nav,
                    name: eventName,
                    fn: theme.addDelegateEventListener(this.proxyTier1Nav, eventName, '.navigation__tier-1 > .navigation__item--with-children > .navigation__link', (evt, el) => {
                        const elIndex = [...el.parentNode.parentNode.children].indexOf(el.parentNode);
                        const proxiedLink = this.querySelectorAll('.navigation__tier-1 > .navigation__item')[elIndex].firstElementChild;
                        this.handleTouch(evt, proxiedLink);
                    }, { passive: eventName === 'touchstart' })
                });
            }

            this.proxyTier1NavBoundEvents.push({
                element: this.proxyTier1Nav,
                name: 'keydown',
                fn: theme.addDelegateEventListener(this.proxyTier1Nav, 'keydown', '.navigation__tier-1 > .navigation__item--with-children > .navigation__link', (evt, el) => {
                    if (evt.key === 'Enter') {
                        const elIndex = [...el.parentNode.parentNode.children].indexOf(el.parentNode);
                        const proxiedLink = this.querySelectorAll('.navigation__tier-1 > .navigation__item')[elIndex].firstElementChild;
                        el.setAttribute('aria-expanded', !proxiedLink.parentElement.classList.contains('navigation__item--show-children'));
                        this.onNavKeydown(evt, proxiedLink);
                    }
                })
            });

            // transparent header hover
            const boundHandleNavHover = this.handleNavHover.bind(this);
            this.proxyTier1Nav.addEventListener('mouseenter', boundHandleNavHover);
            this.proxyTier1Nav.addEventListener('mouseleave', boundHandleNavHover);
            this.proxyTier1NavBoundEvents.push({ element: this.proxyTier1Nav, name: 'mouseenter', fn: boundHandleNavHover });
            this.proxyTier1NavBoundEvents.push({ element: this.proxyTier1Nav, name: 'mouseleave', fn: boundHandleNavHover });
        }

        // create mobile nav
        const tDiv = document.createElement('div');
        tDiv.innerHTML = this.querySelector('.mobile-navigation-drawer-template').innerHTML;
        this.mobileDrawer = tDiv.firstElementChild;

        // add announcement bar items
        const mobileDrawerFooter = this.mobileDrawer.querySelector('.mobile-navigation-drawer__footer');
        const annBarMenu = document.querySelector('.announcement-bar .inline-menu');
        if (annBarMenu) {
            const clone = annBarMenu.cloneNode(true);
            clone.classList.remove('desktop-only');
            mobileDrawerFooter.appendChild(clone);
        }

        const annBarLocalizations = document.querySelector('.announcement-bar .header-localization');
        if (annBarLocalizations) {
            const clone = annBarLocalizations.cloneNode(true);
            clone.classList.remove('desktop-only');
            mobileDrawerFooter.appendChild(clone);

            clone.querySelector('form').addEventListener('change', (evt) => {
                const input = evt.target.previousElementSibling;
                if (input && input.tagName === 'INPUT') {
                    input.value = evt.detail.selectedValue;
                    evt.currentTarget.submit();
                }
            });
        }

        const annBarSocial = document.querySelector('.announcement-bar .social');
        if (annBarSocial) {
            const clone = annBarSocial.cloneNode(true);
            clone.classList.remove('desktop-only');
            mobileDrawerFooter.appendChild(clone);
        }

        // ensure ids are unique
        theme.suffixIds(this.mobileDrawer, 'MobileNav');

        // insert into page
        document.querySelector('.section-header').insertAdjacentElement('afterend', this.mobileDrawer);


        theme.addDelegateEventListener(this.mobileDrawer, 'click', '.navigation__tier-2 > .navigation__item > .navigation__link:not(.navigation__column-title)', this.onNavClick.bind(this));
        theme.addDelegateEventListener(this.mobileDrawer,'click', '.navigation__tier-3  .navigation__link', (evt, delEl) => { 
                const clickTag = evt.target.closest('.navigation__link');
                if (!clickTag) return;
                const parent = clickTag.dataset.gaParent
                const href = clickTag.dataset.gaUrl
                const level = clickTag.dataset.gaLevel
                const name = clickTag.dataset.gaName
                gtag('event', 'nav_selected', {
                    parent,
                    href,
                    level,
                    name,
                    isMobile: isMobile?true:false,
                    type:'anchor',
                    'transport_type': 'beacon'  
                });

        })


        theme.addDelegateEventListener(this.mobileDrawer,'click','.navigation__mobile-promotions .menu-promotion__link',function(evt){
                const clickTag = evt.target.closest('.menu-promotion').querySelector('.menu-promotion__link')
                if (!clickTag) return;
                const parent = clickTag.dataset.gaParent
                const href = clickTag.dataset.gaUrl
                const level = clickTag.dataset.gaLevel
                const name = clickTag.dataset.gaName
                const image = clickTag.dataset.gaImage
                gtag('event', 'nav_selected', {
                        parent,
                        href,
                        level,
                        name,
                        device:'mobile',
                        type:'image',
                        image_url:image,
                        'transport_type': 'beacon'
                });
        })

        theme.addDelegateEventListener(this.mobileDrawer, 'click', '.mobile-navigation-drawer__footer .footer-account', (evt) => {
            const clickTag = evt.target.closest('.footer-account');
            if (!clickTag) return;
            const parent = clickTag.dataset.gaParent;
            const href = clickTag.dataset.gaUrl;
            const level = clickTag.dataset.gaLevel;
            const name = clickTag.dataset.gaName;
            gtag('event', 'nav_selected', {
                parent,
                href,
                level,
                name,
                isMobile: true,
                type: 'anchor',
                'transport_type': 'beacon'
            });
        });




        theme.addDelegateEventListener(this.mobileDrawer, 'click', '.navigation__tier-1 > .navigation__item:not(.navigation__item--with-children)', (evt, delEl) => {
            const clickTag = evt.target.closest('.navigation__item').querySelector('.navigation__link')
            const clickTagParent = clickTag.dataset.gaParent
            const clickTagHref = clickTag.dataset.gaUrl
            const clickTagLevel = clickTag.dataset.gaLevel
            const clickTagName = clickTag.dataset.gaName
            gtag('event', 'nav_selected', {
                parent:clickTagParent,
                href:clickTagHref,
                level:clickTagLevel,
                name:clickTagName,
                isMobile: true,
                type:'anchor',
                'transport_type': 'beacon'  
            });

        })


        // event: open second tier
        theme.addDelegateEventListener(this.mobileDrawer, 'click', '.navigation__tier-1 > .navigation__item > .navigation__children-toggle', (evt, delEl) => {
            evt.preventDefault();
            
            // set text in header
            delEl.parentElement.classList.add('navigation__item--open');
            this.mobileDrawer.classList.add('mobile-navigation-drawer--child-open');
            this.mobileDrawer.querySelector('.mobile-nav-title').innerText = delEl.previousElementSibling.innerText;

            // position under header
            const mobileTierTop = Math.ceil(this.mobileDrawer.querySelector('.navigation__mobile-header').clientHeight + 1);
            delEl.nextElementSibling.style.top = `${mobileTierTop}px`;
            delEl.nextElementSibling.style.setProperty('--mobile-tier-top', `${mobileTierTop}px`);
            const clickTag = evt.target.closest('.navigation__item').querySelector('.navigation__link')
            const clickTagParent = clickTag.dataset.gaParent
            const clickTagHref = clickTag.dataset.gaUrl
            const clickTagLevel = clickTag.dataset.gaLevel
            const clickTagName = clickTag.dataset.gaName
            gtag('event', 'nav_selected', {
                parent:clickTagParent,
                href:clickTagHref,
                level:clickTagLevel,
                name:clickTagName,
                isMobile: true,
                type:'anchor',
                'transport_type': 'beacon'  
            });

            this.observeNavImpressions(delEl.parentElement, 'mobile');

            // scroll container
            this.mobileDrawer.closest('.mobile-navigation-drawer').scrollTo({ top: 0, left: 0, behavior: 'instant' }); // 'smooth' not working in iOS 15
        });

        if (this.mobileDrawer.dataset.mobileExpandWithEntireLink === 'true') {
            theme.addDelegateEventListener(this.mobileDrawer, 'click', '.navigation__item--with-children > .navigation__link', (evt, delEl) => {
                evt.preventDefault();
                delEl.nextElementSibling.dispatchEvent(new Event('click', { bubbles: true, cancelable: true }));
            });
        } else {
            theme.addDelegateEventListener(this.mobileDrawer, 'click', '.navigation__item--with-children > .navigation__link[href="#"]', (evt, delEl) => {
                evt.preventDefault();
                delEl.nextElementSibling.dispatchEvent(new Event('click', { bubbles: true, cancelable: true }));
            });
        }

        // event: close second tier
        theme.addDelegateEventListener(this.mobileDrawer, 'click', '.mobile-nav-back', (evt) => {
            evt.preventDefault();
            this.mobileDrawer.classList.remove('mobile-navigation-drawer--child-open');
            this.mobileDrawer.querySelectorAll('.navigation__tier-1 > .navigation__item--open').forEach((el) => {
                el.classList.remove('navigation__item--open');
            });
        });

        // event: toggle third tier
        theme.addDelegateEventListener(this.mobileDrawer, 'click', '.navigation__tier-2 > .navigation__item > .navigation__children-toggle', (evt, delEl) => {
            evt.preventDefault();
            return false
            const doOpen = !delEl.parentElement.classList.contains('navigation__item--open');

            if (doOpen) {
                delEl.parentElement.classList.add('navigation__item--open');
                delEl.nextElementSibling.style.height = `${delEl.nextElementSibling.firstElementChild.clientHeight}px`;
            } else {
                delEl.parentElement.classList.remove('navigation__item--open');
                delEl.nextElementSibling.style.height = '';
            }
        });
    }

    handleNavHover(evt) {
        this.closest('.section-header').classList.toggle('section-header--nav-hover', evt.type === 'mouseenter');
    }

    onNavClick(evt){
        const clickTag = evt.target.closest('.navigation__link');
        if (!clickTag) return;
        const parent = clickTag.dataset.gaParent
        const href = clickTag.dataset.gaUrl
        const level = clickTag.dataset.gaLevel
        const name = clickTag.dataset.gaName
        gtag('event', 'nav_selected', {
            parent,
            href,
            level,
            name,
            isMobile: isMobile?true:false,
            type:'anchor',
            'transport_type': 'beacon'  
        });


        
        // window.gaPort.postMessage({
        //     type: 'GA4_EVENT',
        //     eventName: 'nav_click',
        //     params:{
        //         parent,
        //         href,
        //         level,
        //         name,
        //         isMobile: isMobile?true:false,
        //         type:'anchor',
        //     },
        //     href: href
        // });
    }

    onNavParentHoverIn(evt) {
        const dropdownContainer = evt.currentTarget;
        clearTimeout(this.navOpenTimeoutId);
        clearTimeout(dropdownContainer.dataset.navCloseTimeoutId);
        const openSiblings = [...dropdownContainer.parentNode.children].filter((child) => child !== dropdownContainer && child.classList.contains('navigation__item--show-children'));

        // close all menus but last opened
        openSiblings.filter((el) => el !== this.navLastOpenDropdown).forEach((el) => el.classList.remove('navigation__item--show-children'));
        this.navLastOpenDropdown = dropdownContainer;

        // show after a delay, based on first-open or not
        const timeoutDelay = openSiblings.length === 0 ? 0 : this.navHoverDelay;
        // open it
        const newNavOpenTimeoutId = setTimeout(() => {
            [...dropdownContainer.parentNode.children].forEach((el) => {
                if (el === dropdownContainer) {
                    el.classList.add('navigation__item--show-children');
                    const currentLevel1 = dropdownContainer.querySelector('.navigation__link').innerHTML
                    if(!this.showerList.get(currentLevel1)){
                        this.showerList.set(currentLevel1,1)
                        this.observeNavImpressions(dropdownContainer, 'pc');


                    }
                } else {
                    el.classList.remove('navigation__item--show-children');
                }
            });
            dropdownContainer.closest('.section-header').classList.add('section-header--nav-open');
        }, timeoutDelay);

        this.navOpenTimeoutId = newNavOpenTimeoutId;
        dropdownContainer.dataset.navOpenTimeoutId = newNavOpenTimeoutId;

        dropdownContainer.firstElementChild.setAttribute('aria-expanded', true);
    }

    observeNavImpressions(container, device) {
        // The second-tier panel is the actual scroll viewport in the mobile drawer.
        // Using the browser viewport alone treats off-screen drawer content as exposed.
        const root = device === 'mobile' ? container.querySelector('.navigation__tier-2-container') : null;
        const items = device === 'mobile'
            ? [
                ...container.querySelectorAll('.container ul .navigation__link:not(.navigation__column-title)'),
                ...container.querySelectorAll('.navigation__mobile-promotions .menu-promotion__link')
            ]
            : [
                ...container.querySelectorAll('.container .navigation__link:not(.navigation__column-title)'),
                ...container.querySelectorAll('.container .navigation__column--promotion .menu-promotion__link')
            ];

        const report = (item) => {
            if (item.dataset.gaImpressionReported === 'true') return;

            item.dataset.gaImpressionReported = 'true';
            const parent = item.dataset.gaParent;
            const href = item.dataset.gaUrl;
            const level = item.dataset.gaLevel;
            const name = item.dataset.gaName;

            if (item.matches('.menu-promotion__link')) {
                window.gaPort?.postMessage({
                    type: 'GA4_EVENT',
                    eventName: 'nav_impression',
                    params: { parent, href, level, name, device, type: 'image', image_url: item.dataset.gaImage }
                });
                return;
            }

            gtag('event', 'nav_impression', {
                parent,
                href,
                level,
                name,
                device,
                type: 'anchor',
                transport_type: 'beacon'
            });
        };

        if (!('IntersectionObserver' in window)) {
            const rootRect = root ? root.getBoundingClientRect() : {
                top: 0,
                right: window.innerWidth,
                bottom: window.innerHeight,
                left: 0
            };
            items.forEach((item) => {
                const rect = item.getBoundingClientRect();
                const visibleWidth = Math.max(0, Math.min(rect.right, rootRect.right, window.innerWidth) - Math.max(rect.left, rootRect.left, 0));
                const visibleHeight = Math.max(0, Math.min(rect.bottom, rootRect.bottom, window.innerHeight) - Math.max(rect.top, rootRect.top, 0));
                if (rect.width * rect.height > 0 && (visibleWidth * visibleHeight) / (rect.width * rect.height) >= 0.5) {
                    report(item);
                }
            });
            return;
        }

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                    report(entry.target);
                    observer.unobserve(entry.target);
                }
            });
        }, { root, threshold: [0.5] });

        items.forEach((item) => {
            if (item.dataset.gaImpressionReported !== 'true') observer.observe(item);
        });
        this.navImpressionObservers.add(observer);
    }

    onNavParentHoverOut(evt) {
        // cancel opening, close after delay, and clear transforms
        const dropdownContainer = evt.currentTarget;
        clearTimeout(dropdownContainer.dataset.navOpenTimeoutId);
        dropdownContainer.dataset.navCloseTimeoutId = setTimeout(() => {
            dropdownContainer.classList.remove('navigation__item--show-children');
            dropdownContainer.closest('.section-header').classList.remove('section-header--nav-open');
        }, this.navHoverDelay);

        dropdownContainer.firstElementChild.setAttribute('aria-expanded', false);
    }

    handleTouch(evt, link) {
        if (window.innerWidth > 767) {
            if (evt.type === 'touchstart') {
                link.dataset.touchstartedAt = evt.timeStamp.toString();
            } else if (evt.type === 'touchend') {
                // down & up in under a second - presume tap
                if (evt.timeStamp - parseInt(link.dataset.touchstartedAt, 10) < 1000) {
                    link.dataset.touchOpenTriggeredAt = evt.timeStamp.toString();
                    if (link.parentElement.classList.contains('navigation__item--show-children')) {
                        // trigger close
                        link.parentElement.dispatchEvent(new Event('mouseleave'));
                    } else {
                        // trigger close on any open items
                        this.querySelectorAll('.navigation__item--show-children').forEach((el) => el.dispatchEvent(new Event('mouseleave')));
                        // trigger open
                        link.parentElement.dispatchEvent(new Event('mouseenter'));
                    }
                    // prevent fake click
                    evt.preventDefault();
                    evt.stopPropagation();
                }
            } else if (evt.type === 'click') {
                // if touch open was triggered very recently, prevent click event
                if (link.dataset.touchOpenTriggeredAt
                    && evt.timeStamp - parseInt(link.dataset.touchOpenTriggeredAt, 10) < 1000) {
                    evt.preventDefault();
                    evt.stopPropagation();
                }
            }
        }
    }

    // eslint-disable-next-line class-methods-use-this
    onNavKeydown(evt, el) {
        if (evt.key === 'Enter') {
            if (el.parentElement.classList.contains('navigation__item--show-children')) {
                el.parentElement.dispatchEvent(new Event('mouseleave'));
            } else {
                el.parentElement.dispatchEvent(new Event('mouseenter'));
            }
            evt.preventDefault();
        }
    }

    onProxyNavEnterSmallMenu(evt, delEl) {
        const el = delEl || evt.currentTarget;
        const elIndex = [...el.parentNode.children].indexOf(el);
        const dropdown = this.querySelectorAll('.navigation__tier-1 > .navigation__item')[elIndex].querySelector('.navigation__tier-2-container');
        if (document.querySelector('html[dir="rtl"]')) {
            dropdown.style.right = `${dropdown.offsetParent.clientWidth - (theme.getOffsetLeftFromDoc(el) + el.clientWidth)}px`;
        } else {
            dropdown.style.left = `${theme.getOffsetLeftFromDoc(el)}px`;
        }
    }

    disconnectedCallback() {
        // remove externalities
        this.mobileDrawer.remove();
        this.navImpressionObservers.forEach((observer) => observer.disconnect());
        this.navImpressionObservers.clear();

        if (this.proxyTier1NavBoundEvents) {
            for (let i = 0; i < this.proxyTier1NavBoundEvents.length; i += 1) {
                this.proxyTier1NavBoundEvents[i].element.removeEventListener(
                    this.proxyTier1NavBoundEvents[i].name,
                    this.proxyTier1NavBoundEvents[i].fn
                );
            }
        }
    }
};

window.customElements.define('main-navigation', MainNavigation);

const ProductBlock = class extends HTMLElement {
    constructor() {
        super();
        window.initLazyScript(this, this.init.bind(this));
    }

    init() {
        this.images = Array.from(this.querySelectorAll('.product-block__image'));
        this.reportedSwatchVariantIds = new Set();
        if (this.images.length > 1) {
            this.initImagePagination();
            this.monitorSwatchSelection();
            this.initCustomLazyLoading();
            if (location.href.indexOf('/collections') > 0) {
                this.trackProductClick();
            }
        }


    }

    initCustomLazyLoading() {
        // preload hover images after a delay
        setTimeout(() => {
            this.querySelector('.product-block__image--show-on-hover').classList.remove('product-block__image--inactivated');
        }, 1000);

        // activate all images after first interaction
        this.boundLazyLoadAll = this.lazyLoadAll.bind(this);
        this.addEventListener('mouseenter', this.boundLazyLoadAll);
        this.addEventListener('touchstart', this.boundLazyLoadAll, { passive: true });
    }

    lazyLoadAll() {
        this.querySelectorAll('.product-block__image--inactivated').forEach((el) => el.classList.remove('product-block__image--inactivated'));
        this.removeEventListener('mouseenter', this.boundLazyLoadAll);
        this.removeEventListener('touchstart', this.boundLazyLoadAll);
    }

    // get image at index, safely wrapping index
    getImageAt(index) {
        // positive modulo, like it should be, JS does not do *maths* correctly
        return this.images[((index % this.images.length) + this.images.length) % this.images.length];
    }

    incrementActiveImage(increment) {
        let index = 0;
        for (let i = 0; i < this.images.length; i += 1) {
            if (this.images[i].classList.contains('product-block__image--active')) {
                index = i;
                break;
            }
        }
        this.setActiveImage(index + increment);
    }

    setActiveImage(index) {
        const newActiveImage = this.getImageAt(index);
        const hoverImage = this.getImageAt(index + 1);

        // set new active image visibility
        [...newActiveImage.parentElement.children].forEach((el) => {
            el.classList.toggle('product-block__image--active', el === newActiveImage);
            el.classList.toggle('product-block__image--show-on-hover', el === hoverImage);
        });

        // dots
        this.querySelectorAll('.product-block__image-dot').forEach((el, iter) => {
            el.classList.toggle('product-block__image-dot--active', iter === index);
        });
    }

    initImagePagination() {
        // next button
        this.querySelector('.image-page-button--next').addEventListener('click', (evt) => {
            evt.preventDefault();
            this.incrementActiveImage(1);
        });

        // previous button
        this.querySelector('.image-page-button--previous').addEventListener('click', (evt) => {
            evt.preventDefault();
            this.incrementActiveImage(-1);
        });

        // swipe (when not in a carousel)
        if (!this.closest('.carousel, .product-grid--scrollarea')) {
            const touchContainer = this.querySelector('.image-cont--with-secondary-image');
            touchContainer.addEventListener('touchstart', (evt) => {
                // swipe may end in another block
                theme.productBlockTouchTracking = true;
                theme.productBlockTouchStartX = evt.touches[0].clientX;
                theme.productBlockTouchStartY = evt.touches[0].clientY;
            }, { passive: true });

            touchContainer.addEventListener('touchmove', (evt) => {
                if (theme.productBlockTouchTracking) {
                    if (Math.abs(evt.touches[0].clientY - theme.productBlockTouchStartY) < 30) {
                        const deltaX = evt.touches[0].clientX - theme.productBlockTouchStartX;
                        if (deltaX > 25) {
                            this.incrementActiveImage(-1);
                            theme.productBlockTouchTracking = false;
                        } else if (deltaX < -25) {
                            this.incrementActiveImage(1);
                            theme.productBlockTouchTracking = false;
                        }
                    }
                }
            }, { passive: true });

            touchContainer.addEventListener('touchend', () => {
                theme.productBlockTouchTracking = false;
            });
        }
    }

    onSelectSwatch(evt) {
        evt.preventDefault();
        let index = -1;

        for (let i = 0; i < this.images.length; i += 1) {
            if (this.images[i].dataset.mediaId === evt.currentTarget.dataset.media) {
                index = i;
                break;
            }
        }

        if (index < 0) return;

        const activeImage = this.querySelector('.product-block__image--active');
        this.setActiveImage(index);

        const variantId = evt.currentTarget.dataset.variantId
            || this.images[index].dataset.variantId?.split('-')[0];
        if (this.images[index] !== activeImage && variantId && !this.reportedSwatchVariantIds.has(variantId) && window.gaPort) {
            this.reportedSwatchVariantIds.add(variantId);
            window.gaPort.postMessage({
                type: 'GA4_EVENT',
                eventName: 'swatch_view_item_list',
                params: { variantId }
            });
        }

        // include in URL
        const { optionName } = evt.currentTarget.closest('.product-block-options').dataset;
        const optionValue = evt.currentTarget.dataset.optionItem;
        this.querySelectorAll('.product-link, .quickbuy-toggle').forEach((el) => {
            const url = new URL(el.href);
            const params = new URLSearchParams(url.search);
            params.set(optionName, optionValue);
            el.href = `${url.pathname}?${params}`;
            el.rel = 'nofollow';
        });
    }

    monitorSwatchSelection() {
        this.querySelectorAll('[data-media].product-block-options__item').forEach((el) => {
            el.addEventListener('mouseenter', this.onSelectSwatch.bind(this));
            el.addEventListener('click', this.onSelectSwatch.bind(this));
        });
    }

    trackProductClick() {
        const links = this.querySelectorAll('a.product-links');
        const actvieImage = this.querySelector('.product-block__image--active')
        const variantId = actvieImage.dataset.variantId?.split('-')[0]
        const productId = this.dataset.productId
        links.forEach((link) => {
            link.addEventListener('click', (e) => {
                e.preventDefault();
                const href = link.href;
                window.gaPort.postMessage({
                    type: 'GA4_EVENT',
                    eventName: 'select_item',
                    params: {
                        variantId: variantId,
                    },
                    href: href
                });
                setTimeout(() => {
                    window.location.href = href
                }, 200);

            })
        })

    }

};

window.customElements.define('product-block', ProductBlock);

const MediaGallery = class extends HTMLElement {
    constructor() {
        super();
        this.section = this.closest('.js-product');
        this.mainImageContainer = this.querySelector('.main-image');
        this.slideshow = this.mainImageContainer.querySelector('.main-image carousel-slider');
        this.collage = this.querySelector('.product-media-collage');
        this.xrButton = this.querySelector('[data-shopify-xr]');
        this.variantPicker = this.section.querySelector('variant-picker');
        this.mediaGroupingEnabled = this.variantPicker && this.hasAttribute('data-media-grouping-enabled') && this.getMediaGroupData();
        this.deferredMediaHandles = new Set();
        this.deferredMediaRequests = new Map();
        this.deferredMediaAbortControllers = new Map();
        this.pendingMediaGroup = null;
        this.pendingVariantChangeToken = 0;
        this.refreshMediaLists();

        if (this.variantPicker) {
            const toLoad = [this.variantPicker];
            if (this.slideshow.offsetParent) {
                toLoad.push(this.slideshow);
            }
            const thumbnailSlider = this.querySelector('carousel-slider.thumbnails');
            if (thumbnailSlider && thumbnailSlider.offsetParent) {
                toLoad.push(thumbnailSlider);
            }
            theme.whenComponentLoaded(toLoad, this.setFromVariantPicker.bind(this));
        }

        setTimeout(() => {
            if (!this.variantPicker) {
                this.variantPicker = this.section.querySelector('variant-picker');
            }
            if (!this.variantPicker || this.currentMediaGroup) return;

            if (this.variantPicker.hasAttribute('loaded')) {
                this.setFromVariantPicker();
            } else {
                theme.whenComponentLoaded(this.variantPicker, () => {
                    if (!this.currentMediaGroup) {
                        this.setFromVariantPicker();
                    }
                });
            }
        }, 0);

        if (this.hasAttribute('data-zoom-enabled')) {
            this.galleryModal = this.querySelector('.js-media-zoom-template').content.firstElementChild.cloneNode(true);
            this.refreshMediaLists();
            theme.addDelegateEventListener(this, 'click', '.show-gallery', this.openGalleryViewer.bind(this));

            if (this.hasAttribute('data-preload')) {
                this.mainImageContainer.addEventListener('mouseover', MediaGallery.hoverMainImage.bind(this));
                this.mainImageContainer.addEventListener('touchstart', MediaGallery.hoverMainImage.bind(this), { passive: true });
            }
        }

        this.section.addEventListener('on:carousel-slider:select', this.selectMainMedia.bind(this));
        theme.addDelegateEventListener(this, 'click', '.thumbnail', this.selectThumbnail.bind(this));
        this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));

        if (this.hasAttribute('data-preload')) {
            // Preload slideshow images after a short delay, if slideshow is visible
            setTimeout(() => {
                if (this.slideshow.offsetParent) {
                    this.slideshow.querySelectorAll('.theme-img').forEach((el) => { el.loading = 'eager'; });
                }
            }, 3000);
        }
    }

    refreshMediaLists() {
        this.mediaLists = [
            this.querySelectorAll('.main-image .slider__item'),
            this.querySelectorAll('.product-media-collage__item'),
            this.querySelectorAll('.thumbnails .slider__item')
        ];

        if (this.galleryModal) {
            this.mediaLists.push(this.galleryModal.querySelectorAll('.gallery-viewer__thumb'));
        }
    }

    getSelectedModelInfo() {
        const defaultModelInfo = { text: '', photoPosition: 1 };
        if (!this.variantPicker) return defaultModelInfo;

        const checkedInput = Array.from(
            this.variantPicker.querySelectorAll('.option-selector input.js-option:checked')
        ).find((input) => {
            const optionName = input.closest('.option-selector')?.dataset.option || '';
            return /^colou?r$/i.test(optionName);
        });
        if (!checkedInput) return defaultModelInfo;

        const label = checkedInput.nextElementSibling && checkedInput.nextElementSibling.matches('.opt-label')
            ? checkedInput.nextElementSibling
            : null;

        if (!label) return defaultModelInfo;

        const position = Number.parseInt(label.dataset.modelPhotoPosition, 10);
        return {
            text: (label.dataset.model || '').trim(),
            photoPosition: Number.isInteger(position) && position > 0 ? position : null
        };
    }

    updateModelInfo() {
        const { text: modelText, photoPosition: modelPhotoPosition } = this.getSelectedModelInfo();
        const mobileModelInfo = this.mainImageContainer
            ? this.mainImageContainer.querySelector('.model-info.mobile-only')
            : null;

        if (this.collage) {
            const visibleCollageItems = [...this.collage.querySelectorAll('.product-media-collage__item')]
                .filter((item) => !item.hidden);
            const targetCollageIndex = Number.isInteger(modelPhotoPosition) && modelPhotoPosition > 0
                ? modelPhotoPosition - 1
                : -1;

            this.collage.querySelectorAll('.product-media-collage__item .model-info').forEach((el) => {
                el.innerHTML = '';
            });

            const targetCollageItem = targetCollageIndex >= 0
                ? visibleCollageItems[targetCollageIndex]
                : null;
            const targetModelInfo = targetCollageItem
                ? targetCollageItem.querySelector('.model-info')
                : null;

            if (targetModelInfo && modelText) {
                targetModelInfo.innerHTML = `<span>${modelText}</span>`;
            }
        }

        const mobileSlides = this.slideshow?.slides?.length
            ? this.slideshow.slides
            : (this.mainImageContainer
                ? [...this.mainImageContainer.querySelectorAll('.slider__item:not([hidden])')]
                : []);
        const targetMobileIndex = Number.isInteger(modelPhotoPosition) && modelPhotoPosition > 0
            ? modelPhotoPosition - 1
            : -1;

        const activeMobileIndex = Number.isInteger(this.slideshow?.currentIndex)
            ? this.slideshow.currentIndex
            : mobileSlides.findIndex((slide) => slide.classList.contains('is-active'));
        const shouldShowMobileModel = Boolean(
            mobileModelInfo
            && modelText
            && targetMobileIndex >= 0
            && activeMobileIndex >= targetMobileIndex
        );

        if (mobileModelInfo) {
            mobileModelInfo.innerHTML = shouldShowMobileModel ? `<span>${modelText}</span>` : '';
            mobileModelInfo.style.display = shouldShowMobileModel ? 'flex' : 'none';
        }
    }

    setFromVariantPicker() {
        if (this.mediaGroupingEnabled) {
            this.setMediaGroupOnFirstLoad();
        } else {
            // Find which variant is selected and set active media.
            const variant = this.variantPicker.getSelectedVariant();
            if (variant && variant.featured_media) {
                this.setActiveMedia(variant.featured_media.id, true, true);
            }
        }

        this.updateModelInfo();
    }

    /**
     * Run on first load to set initial media group and focussed image
     */
    setMediaGroupOnFirstLoad() {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
        // Find which variant is selected and set active media.
        const variant = this.variantPicker.getSelectedVariant();
        if (variant && variant.featured_media) {
            this.setActiveMedia(variant.featured_media.id, true, true);
            return;
        }

        // Default to first visible image.
        this.setActiveMedia(this.querySelector('.thumbnails .slider__item:not([hidden])').dataset.mediaId, false, true);
    }

    /**
     * Hover main image.
     * @param {Event} evt - hover event for image
     */
    static hoverMainImage(evt) {
        MediaGallery.loadImage(evt.currentTarget.querySelector('.slider__item.is-active a.show-gallery'));
    }

    /**
     * Open large image viewer.
     * @param {Event} evt - click event for image
     * @param {Element} imageLink - link clicked on
     */
    openGalleryViewer(evt, imageLink) {
        evt.preventDefault();

        // add to document on first open
        if (!this.galleryModal.parentElement) {
            document.body.appendChild(this.galleryModal);
        }

        this.galleryModal.open(imageLink);
        const viewer = this.galleryModal.querySelector('gallery-viewer');
        viewer.init(imageLink.getAttribute('href'));
        viewer.focus();

        if (this.hasAttribute('data-preload')) {
            this.mediaLists[0].forEach((sliderItem) => {
                MediaGallery.loadImage(sliderItem.querySelector('a.show-gallery'));
            });
        }
    }

    /**
     * Load an image.
     * @param {Element} anchor - anchor element
     */
    static loadImage(anchor) {
        if (!anchor || anchor.hasAttribute('data-loading') || anchor.hasAttribute('data-loaded')) return;

        anchor.setAttribute('data-loading', '');

        const image = new Image(5000);
        image.addEventListener('load', () => {
            anchor.removeAttribute('data-loading');
            anchor.setAttribute('data-loaded', '');
        }, {
            once: true
        });
        image.src = anchor.href;
    }

    /**
     * Handle a change in variant on the page.
     * @param {Event} evt - variant change event dispatched by variant-picker
     */
    async onVariantChange(evt) {
        const variantChangeToken = ++this.pendingVariantChangeToken;
        const nextMediaGroup = this.mediaGroupingEnabled ? this.getMediaGroupFromOptionSelectors(evt) : null;
        const shouldShowSwatchLoading = this.shouldShowSwatchLoading(evt, nextMediaGroup);
        const swatchLoadingOwner = shouldShowSwatchLoading ? `media-gallery:${variantChangeToken}` : null;
        const swatchLoadingStartedAt = shouldShowSwatchLoading ? performance.now() : 0;

        if (shouldShowSwatchLoading) {
            this.setSwatchLoadingState(true, evt, swatchLoadingOwner);
        }

        try {
            await this.ensureVariantMediaLoaded(evt);
            if (variantChangeToken !== this.pendingVariantChangeToken) return;

            if (this.mediaGroupingEnabled) {
                this.setActiveMediaGroup(nextMediaGroup);
            }

            if (evt.detail.variant && evt.detail.variant.featured_media) {
                this.setActiveMedia(evt.detail.variant.featured_media.id, true);
            } else if (this.mediaGroupChanged) {
                // default to first visible image, if group changed and current variant has no related image
                this.setActiveMedia(this.querySelector('.thumbnails .slider__item:not([hidden])').dataset.mediaId, true);
            }

            // Keep model information in sync even when the selected variant does not change media.
            this.updateModelInfo();
        } finally {
            if (shouldShowSwatchLoading && variantChangeToken === this.pendingVariantChangeToken) {
                const remainingDuration = 450 - (performance.now() - swatchLoadingStartedAt);
                if (remainingDuration > 0) {
                    await new Promise((resolve) => {
                        setTimeout(resolve, remainingDuration);
                    });
                }
                this.setSwatchLoadingState(false, null, swatchLoadingOwner);
            }
        }
    }

    shouldShowSwatchLoading(evt, nextMediaGroup) {
        if (this.isColorSwatchTrigger(evt)) {
            return true;
        }

        return !!(this.mediaGroupingEnabled && nextMediaGroup && nextMediaGroup !== this.currentMediaGroup);
    }

    getDeferredMediaData() {
        if (typeof this.deferredMediaData === 'undefined') {
            const dataEl = this.querySelector('.js-data-deferred-media-handles');
            this.deferredMediaData = dataEl ? JSON.parse(dataEl.textContent) : false;
        }

        return this.deferredMediaData;
    }

    getDeferredHandleForGroup(groupName) {
        if (!groupName) return null;

        const deferredMediaData = this.getDeferredMediaData();
        if (!deferredMediaData || !deferredMediaData.handlesByGroup) return null;

        return deferredMediaData.handlesByGroup[groupName.toString().trim().toLowerCase()] || null;
    }

    hasMediaForGroup(groupName) {
        if (!groupName) return false;

        const mediaGroupData = this.getMediaGroupData();
        if (!mediaGroupData || !mediaGroupData.media) return false;

        return [...this.querySelectorAll('.main-image .slider__item')].some((mediaItem) => {
            const mediaMeta = mediaGroupData.media[mediaItem.dataset.mediaId];
            if (!mediaMeta) return false;

            return mediaMeta.group === true || mediaMeta.group === groupName;
        });
    }

    async ensureVariantMediaLoaded(evt) {
        if (!this.mediaGroupingEnabled) return;

        const groupName = this.getMediaGroupFromOptionSelectors(evt);
        if (this.hasMediaForGroup(groupName)) return;

        const deferredTarget = this.getDeferredHandleForGroup(groupName);
        if (!deferredTarget || !deferredTarget.handle) return;

        const cacheKey = `${deferredTarget.handle}:${deferredTarget.variantId || ''}`;
        if (this.deferredMediaHandles.has(cacheKey)) return;

        this.abortStaleDeferredMediaRequests(cacheKey);
        this.setMediaLoadingState(true, groupName);
        try {
            await this.loadDeferredHandleMedia(deferredTarget);
        } finally {
            if (this.pendingMediaGroup === groupName) {
                this.setMediaLoadingState(false);
            }
        }
    }

    setMediaLoadingState(isLoading, groupName = null) {
        this.classList.toggle('media-gallery--loading', isLoading);
        this.setAttribute('aria-busy', isLoading ? 'true' : 'false');

        if (isLoading && groupName) {
            this.pendingMediaGroup = groupName;
            this.dataset.loadingGroup = groupName;
        } else {
            this.pendingMediaGroup = null;
            delete this.dataset.loadingGroup;
        }
    }

    clearLoadingSwatch() {
        if (this.loadingSwatchSpinner && this.loadingSwatchSpinner.parentNode) {
            this.loadingSwatchSpinner.remove();
        }

        if (this.loadingSwatchLabel) {
            this.loadingSwatchLabel.classList.remove('is-media-loading');
            this.loadingSwatchLabel.removeAttribute('aria-busy');
        }

        this.loadingSwatchLabel = null;
        this.loadingSwatchSpinner = null;
        this.loadingSwatchOwners = new Set();
    }

    getLoadingSwatchLabelFromEvent(evt) {
        const trigger = evt?.detail?.trigger;
        if (!trigger) return null;

        const input = trigger.matches('.js-option') ? trigger : trigger.closest('.js-option');
        if (!input) return null;

        const optionSelector = input.closest('.option-selector');
        const optionName = optionSelector?.dataset?.option?.trim()?.toLowerCase();
        if (optionName !== 'color' && optionName !== 'colour') return null;

        const label = this.variantPicker.querySelector(`label[for="${CSS.escape(input.id)}"]`)
            || (input.nextElementSibling && input.nextElementSibling.matches('label') ? input.nextElementSibling : null);
        if (!label || !label.classList.contains('opt-label')) return null;

        return label;
    }

    isColorSwatchTrigger(evt) {
        return !!this.getLoadingSwatchLabelFromEvent(evt);
    }

    setLoadingSwatchFromEvent(evt, ownerKey = null, reset = false) {
        const label = this.getLoadingSwatchLabelFromEvent(evt);
        if (reset) {
            this.clearLoadingSwatch();
        } else if (!this.loadingSwatchOwners) {
            this.loadingSwatchOwners = new Set();
        }
        if (!label) return;

        if (this.loadingSwatchLabel !== label) {
            this.clearLoadingSwatch();
        }

        if (!this.loadingSwatchOwners) {
            this.loadingSwatchOwners = new Set();
        }

        label.classList.add('is-media-loading');
        label.setAttribute('aria-busy', 'true');

        if (!this.loadingSwatchSpinner || this.loadingSwatchSpinner.parentNode !== label) {
            const spinner = document.createElement('span');
            spinner.className = 'opt-label__loading-spinner';
            spinner.setAttribute('aria-hidden', 'true');
            label.appendChild(spinner);
            this.loadingSwatchSpinner = spinner;
        }

        this.loadingSwatchLabel = label;
        if (ownerKey) {
            this.loadingSwatchOwners.add(ownerKey);
        }
    }

    setSwatchLoadingState(isLoading, evt = null, ownerKey = null) {
        if (isLoading) {
            this.setLoadingSwatchFromEvent(evt, ownerKey, false);
        } else {
            if (!ownerKey) {
                this.clearLoadingSwatch();
                return;
            }

            if (!this.loadingSwatchOwners) {
                this.loadingSwatchOwners = new Set();
            }

            this.loadingSwatchOwners.delete(ownerKey);

            if (!this.loadingSwatchOwners.size) {
                this.clearLoadingSwatch();
            }
        }
    }

    async loadDeferredHandleMedia(deferredTarget) {
        const cacheKey = `${deferredTarget.handle}:${deferredTarget.variantId || ''}`;
        if (this.deferredMediaHandles.has(cacheKey)) return;

        if (!this.deferredMediaRequests.has(cacheKey)) {
            const abortController = new AbortController();
            this.deferredMediaAbortControllers.set(cacheKey, abortController);
            this.deferredMediaRequests.set(cacheKey, this.fetchDeferredHandleMedia(deferredTarget, abortController.signal)
                .then((data) => {
                    if (!data) return;

                    this.appendDeferredMedia(cacheKey, data);
                    this.deferredMediaHandles.add(cacheKey);
                })
                .catch((error) => {
                    if (error.name === 'AbortError') return;
                    console.warn(`Failed to load deferred media for ${cacheKey}`, error);
                })
                .finally(() => {
                    this.deferredMediaRequests.delete(cacheKey);
                    this.deferredMediaAbortControllers.delete(cacheKey);
                }));
        }

        await this.deferredMediaRequests.get(cacheKey);
    }

    getProductUrlByHandle(handle) {
        const root = window.Shopify?.routes?.root || '/';
        const normalisedRoot = root.endsWith('/') ? root : `${root}/`;
        return `${window.location.origin}${normalisedRoot}products/${handle}`;
    }

    abortStaleDeferredMediaRequests(activeCacheKey) {
        this.deferredMediaAbortControllers.forEach((controller, cacheKey) => {
            if (cacheKey !== activeCacheKey) {
                controller.abort('Superseded by newer swatch selection');
            }
        });
    }

    async fetchDeferredHandleMedia(deferredTarget, signal) {
        const productUrl = new URL(this.getProductUrlByHandle(deferredTarget.handle));
        if (deferredTarget.variantId) {
            productUrl.searchParams.set('variant', deferredTarget.variantId);
        }
        productUrl.searchParams.set('section_id', 'pdp-deferred-media');
        const response = await fetch(productUrl, {
            headers: {
                Accept: 'text/html'
            },
            signal
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        return this.parseDeferredMediaMarkup(await response.text());
    }

    parseDeferredMediaMarkup(markup) {
        const template = document.createElement('template');
        template.innerHTML = markup.trim();

        const gallery = template.content.querySelector('media-gallery[data-deferred-media-source]') || template.content.querySelector('media-gallery');
        if (!gallery) return null;

        const zoomTemplate = gallery.querySelector('.js-media-zoom-template');
        const variantMediaDataEl = gallery.querySelector('.js-data-variant-media');

        return {
            mainItems: [...gallery.querySelectorAll('.main-image .slider__item')],
            collageItems: [...gallery.querySelectorAll('.product-media-collage__item')],
            thumbnailItems: [...gallery.querySelectorAll('.thumbnails .slider__item')],
            zoomThumbItems: zoomTemplate
                ? [...zoomTemplate.content.querySelectorAll('.gallery-viewer__thumb')]
                : [],
            variantMediaData: variantMediaDataEl ? JSON.parse(variantMediaDataEl.textContent) : null
        };
    }

    appendDeferredMedia(cacheKey, mediaMarkup) {
        const mainGrid = this.querySelector('.main-image .slider__grid');
        const collageGrid = this.querySelector('.product-media-collage');
        const thumbnailGrid = this.querySelector('.thumbnails .slider__grid');
        const zoomThumbGrid = this.galleryModal && this.galleryModal.querySelector('.gallery-viewer__thumbs');

        mediaMarkup.mainItems.forEach((item) => {
            if (mainGrid.querySelector(`[data-media-id="${item.dataset.mediaId}"]`)) return;
            item.dataset.deferredHandle = cacheKey;
            mainGrid.appendChild(item);
        });

        mediaMarkup.collageItems.forEach((item) => {
            if (collageGrid && collageGrid.querySelector(`[data-media-id="${item.dataset.mediaId}"]`)) return;
            item.dataset.deferredHandle = cacheKey;
            if (collageGrid) {
                collageGrid.appendChild(item);
            }
        });

        mediaMarkup.thumbnailItems.forEach((item) => {
            if (thumbnailGrid && thumbnailGrid.querySelector(`[data-media-id="${item.dataset.mediaId}"]`)) return;
            item.dataset.deferredHandle = cacheKey;
            if (thumbnailGrid) {
                thumbnailGrid.appendChild(item);
            }
        });

        mediaMarkup.zoomThumbItems.forEach((item) => {
            if (zoomThumbGrid && zoomThumbGrid.querySelector(`[data-media-id="${item.dataset.mediaId}"]`)) return;
            item.dataset.deferredHandle = cacheKey;
            if (zoomThumbGrid) {
                zoomThumbGrid.appendChild(item);
            }
        });

        if (mediaMarkup.variantMediaData && mediaMarkup.variantMediaData.media) {
            const currentVariantMediaData = this.getMediaGroupData();
            if (currentVariantMediaData && currentVariantMediaData.media) {
                currentVariantMediaData.media = {
                    ...currentVariantMediaData.media,
                    ...mediaMarkup.variantMediaData.media
                };
            }
        }

        this.refreshMediaLists();
        this.refreshMediaCarousels();
    }

    refreshMediaCarousels() {
        [this.slideshow, this.querySelector('carousel-slider.thumbnails')].forEach((slider) => {
            if (slider) {
                slider.refresh();
            }
        });
    }

    /**
     * Gets the media group from currently selected variant options.
     * @param {Event} evt - variant change event dispatched by variant-picker
     * @returns {?object}
     */
    getMediaGroupFromOptionSelectors(evt) {
        if (evt) {
            return evt.detail.selectedOptions[this.getMediaGroupData().groupOptionIndex];
        }

        return this.variantPicker.getSelectedOptions()[this.getMediaGroupData().groupOptionIndex];
    }

    /**
     * Gets the variant media associations for a product.
     * @returns {?object}
     */
    getMediaGroupData() {
        if (typeof this.variantMediaData === 'undefined') {
            const dataEl = this.querySelector('.js-data-variant-media');
            if (dataEl) {
                this.variantMediaData = JSON.parse(dataEl.textContent);
            } else {
                this.variantMediaData = false;
            }
        }

        return this.variantMediaData;
    }

    /**
     * Show only images associated to the current variant.
     * @param {string} groupName - optional - Group to show (uses this.currentItem if empty)
     */
    setActiveMediaGroup(groupName) {
        this.mediaGroupChanged = this.currentMediaGroup !== groupName;
        this.currentMediaGroup = groupName;

        if (!this.mediaGroupChanged) return;

        if (!groupName) {
            this.mediaLists.forEach((list) => {
                list.forEach((mediaItem) => { mediaItem.hidden = false; });
            });
            return;
        }

        const mediaGroupData = this.getMediaGroupData();

        this.mediaLists.forEach((list) => {
            // Set hidden
            list.forEach((el) => {
                const mediaItemGroup = mediaGroupData.media[el.dataset.mediaId].group;
                const showThis = mediaItemGroup === groupName || mediaItemGroup === true;
                el.hidden = !showThis;
            });

            // Move all-groups media to start/end
            [...list].forEach((el) => {
                if (mediaGroupData.media[el.dataset.mediaId].group === true) {
                    if (mediaGroupData.media[el.dataset.mediaId].position === 'start') {
                        el.parentElement.prepend(el);
                    } else {
                        el.parentElement.append(el);
                    }
                }
            });

            // Move hidden elements to the end (to allow nth-child CSS in layouts)
            [...list].filter((el) => el.hidden).forEach((el) => el.parentElement.append(el));

            // Refresh slider
            if (list.length > 0) {
                const slider = list[0].closest('carousel-slider');
                if (slider && slider.offsetParent) {
                    slider.refresh();
                }
            }
        });
    }

    /**
     * Sets the active media item.
     * @param {number} mediaId - Media ID to set as active.
     * @param {boolean} [scrollToItem=true] - Scroll to the active media item.
     * @param {boolean} [firstLoad=false] - Is gallery initialising.
     */
    setActiveMedia(mediaId, scrollToItem = true, firstLoad = false) {
        const strMediaId = mediaId.toString();
        const mediaEl = this.mainImageContainer.querySelector(`[data-media-id="${strMediaId}"]`);

        if (scrollToItem) {
            // slideshow (if visible)
            if (this.slideshow.hasAttribute('loaded') && this.slideshow.offsetParent) {
                this.slideshow.scrollToElement(mediaEl, 'instant');
            }

            // collage (if visible and not first load)
            if (this.collage && this.collage.offsetParent && !firstLoad) {
                const collageMediaEl = this.collage.querySelector(`[data-media-id="${strMediaId}"]`);
                if (collageMediaEl) {
                    theme.scrollToRevealElement(collageMediaEl);

                    clearTimeout(this.collage.dataset.highlightTimeoutId);
                    // When media group changes, assume first media has attention
                    
                    if (this.mediaGroupChanged) {
                        this.collage.querySelectorAll('.product-media-collage__item--highlight-off').forEach((el) => {
                            el.classList.remove('product-media-collage__item--highlight-off');
                        });
                    } else {
                        // this.collage.querySelectorAll('.product-media-collage__item').forEach((el) => {
                        //     if (el === collageMediaEl) {
                        //         el.classList.remove('product-media-collage__item--highlight-off');
                        //     } else {
                        //         el.classList.add('product-media-collage__item--highlight-off');
                        //     }
                        // });
                        // this.collage.dataset.highlightTimeoutId = setTimeout(() => {
                        //     this.collage.querySelectorAll('.product-media-collage__item--highlight-off').forEach((el) => {
                        //         el.classList.remove('product-media-collage__item--highlight-off');
                        //     });
                        // }, 1200);
                    }
                }
            }
        }

        this.mediaLists.forEach((list) => {
            list.forEach((mediaItem) => {
                if (mediaItem.dataset.mediaId === strMediaId) {
                    mediaItem.classList.add('is-active');
                    const carousel = mediaItem.closest('carousel-slider');
                    if (carousel && carousel.offsetParent) {
                        carousel.scrollToElement(mediaItem);
                    }
                } else {
                    mediaItem.classList.remove('is-active');
                }
            });
        });

        // play media
        window.pauseAllMedia();
        if (mediaEl) {
            // deferred
            const deferredMedia = mediaEl.querySelector('deferred-media, product-model');
            if (deferredMedia) deferredMedia.loadContent(true);

            // video tag
            const playableMedia = mediaEl.querySelector('video');
            if (playableMedia) {
                playableMedia.play();
            }
        }

        // set model ID
        if (this.xrButton && this.mediaIsModel(mediaId)) {
            this.xrButton.dataset.shopifyModel3dId = mediaId;
        }

        document.dispatchEvent(
            new CustomEvent('on:media-gallery:change', { bubbles: true, cancelable: false })
        );

        this.updateModelInfo();
    }

    /**
     * Check if media is a 3D model
     * @param {number} mediaId - Media ID to check.
     * @returns {boolean} - If it is a 3D model.
     */
    mediaIsModel(mediaId) {
        return !!this.querySelector(`.product-media--model[data-model-id="${mediaId}"]`);
    }

    selectThumbnail(evt, thumbnail) {
        evt.preventDefault();
        this.setActiveMedia(thumbnail.parentNode.dataset.mediaId, true);
    }

    selectMainMedia(evt) {
        if (evt.detail.slide.dataset.mediaId) {
            this.setActiveMedia(evt.detail.slide.dataset.mediaId, false);
        }
    }
};

window.customElements.define('media-gallery', MediaGallery);

/* eslint-disable max-len */
/* eslint-disable no-multiple-empty-lines */
const ProductForm = class extends HTMLElement {
    constructor() {
        super();

        this.form = this.querySelector('.js-product-form');
        if (this.form) {
            const idInput = this.form.querySelector('[name="id"]');
            idInput.disabled = false;
            idInput.required = true;

            if (theme.settings.afterAddToCart !== 'no-js') {
                this.submitBtn = this.querySelector('[name="add"]');
                this.form.addEventListener('submit', this.handleSubmit.bind(this));
            }
        }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
        evt.preventDefault();
        if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

        this.setErrorMsgState();

        const formValid = this.validate();
        if (!formValid) return;

        // Disable "Add to Cart" button until submission is complete.
        this.submitBtn.setAttribute('aria-disabled', 'true');
        this.submitBtn.classList.add('is-loading');

        const formData = new FormData(this.form);
        const sectionsToUpdate = ['page-header', 'cart-drawer'].map((sel) => document.querySelector(sel)).filter((el) => el);
        const sectionIds = sectionsToUpdate.map((el) => el.dataset.sectionId);
        formData.append('sections_url', window.location.pathname);
        if(this.from_section == 'ctl' && this.from_title && this.from_color && this.channel ){
            formData.append('attributes[_theLook]', 'ctl');
            formData.append('properties[_handle]', this.channel);
            formData.append('properties[_channel]', 'complete-the-look');
            formData.append('properties[_from_title]', this.from_title);
            formData.append('properties[_from_color]', this.from_color);
        }else if(this.from_section == 'gtl'){
            formData.append('attributes[_theLook]', 'gtl');
            formData.append('properties[_handle]', this.channel);
            formData.append('properties[_channel]', 'styled-by-you');
            formData.append('properties[_get_the_look_id]', this._get_the_look_id);
            formData.append('properties[_influencer_handle]', this._influencer_handle);
        }else if(this.from_section == 'soy'){
            formData.append('attributes[_theLook]', 'soy');
            formData.append('properties[_handle]', this.channel);
            formData.append('properties[_channel]', 'seen-on-you');
            formData.append('properties[_get_the_look_id]', this._get_the_look_id);
            formData.append('properties[_influencer_handle]', this._influencer_handle);
        }else if(this.from_section == 'ls'){
            formData.append('attributes[_theLook]', 'ls');
            formData.append('properties[_handle]', this.channel);
            formData.append('properties[_channel]', 'length-switch');
            formData.append('properties[_from_title]', this.from_title);
            formData.append('properties[_from_color]', this.from_color);
        }
        formData.append('sections', sectionIds.join(','));

        const ga4Identifiers = await getGA4Identifiers();
        if(ga4Identifiers){
             formData.append('attributes[_ga4_client_id]', ga4Identifiers.client_id);
            formData.append('attributes[_ga4_session_id]', ga4Identifiers.session_id);
            formData.append('attributes[_ga4_session_number]', ga4Identifiers.session_number);
        }
        
        const fetchRequestOpts = {
            method: 'POST',
            headers: {
                Accept: 'application/javascript',
                'X-Requested-With': 'XMLHttpRequest'
            },
            body: formData
        };

        try {
            const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
            const data = await response.json();
            let error = typeof data.description === 'string' ? data.description : data.message;
            if (data.errors && typeof data.errors === 'object') {
                error = Object.entries(data.errors).map((item) => item[1].join(', '));
            }
            if (data.status) this.setErrorMsgState(error);
            if (!response.ok) throw new Error(response.status);

            if (document.querySelector('.template-cart')) {
                // Cart page
                const cartForm = document.querySelector('cart-form');
                if (cartForm && cartForm.enableAjaxUpdate) {
                    cartForm.refresh();
                }
            } else if (theme.settings.afterAddToCart === 'page') {
                // Allow the tick animation to complete
                setTimeout(() => {
                    window.location.href = theme.routes.cart;
                }, 300);
            } else {
                if (data.sections && sectionsToUpdate.length === Object.keys(data.sections).length) {
                    sectionsToUpdate.forEach((el) => {
                        el.updateFromCartChange(data.sections[el.dataset.sectionId]);
                    });
                } else {
                    // Unexpected null section return has been observed
                    this.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
                }

                if (theme.settings.afterAddToCart === 'notification') {
                    const notification = document.getElementById('AddedNotification').content.firstElementChild.cloneNode(true);
                    notification.dataset.productTitle = data.product_title;
                    let notificationContainer = document.querySelector('.pageheader--sticky');
                    if (!notificationContainer) {
                        notificationContainer = document.querySelector('body');
                    }
                    notificationContainer.appendChild(notification);
                } else if (theme.settings.afterAddToCart === 'drawer') {
                    document.querySelector('.js-cart-drawer').open();
                }
            }

            this.dispatchEvent(new CustomEvent('on:cart:add', {
                bubbles: true,
                detail: {
                    variantId: data.variant_id
                }
            }));

            // Re-enable 'Add to Cart' button.
            this.submitBtn.classList.add('is-success');
            this.submitBtn.removeAttribute('aria-disabled');
            setTimeout(() => {
                this.submitBtn.classList.remove('is-loading');
                this.submitBtn.classList.remove('is-success');
            }, 2000);
        } catch (error) {
            console.log(error); // eslint-disable-line
            this.dispatchEvent(new CustomEvent('on:cart:error', {
                bubbles: true,
                detail: {
                    error: this.errorMsg.textContent
                }
            }));

            // Cart may change despite error
            this.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));

            // Re-enable 'Add to Cart' button.
            this.submitBtn.classList.remove('is-loading');
            this.submitBtn.classList.remove('is-success');
            this.submitBtn.removeAttribute('aria-disabled');
        }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
        this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
        if (!this.errorMsg) return;

        this.errorMsg.hidden = !error;
        if (error) {
            this.errorMsg.innerHTML = '';
            const errorArray = Array.isArray(error) ? error : [error];
            errorArray.forEach((err, index) => {
                if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
                this.errorMsg.insertAdjacentText('beforeend', err);
            });
        }
    }

    /**
     * Validate form & show errors.
     * @returns {boolean} - Is form valid.
     */
    validate() {
        let isAnyValid = true;
        this.querySelectorAll('variant-picker .option-selector').forEach((el) => {
            // Clear prior errors
            let isValid = true;
            el.querySelectorAll('.label__prefix').forEach((label) => label.remove());
            el.querySelectorAll('.label--contains-error').forEach((label) => label.classList.remove('label--contains-error'));

            // Check for errors
            if (el.dataset.selectorType === 'listed') {
                const input = el.querySelector('input');
                const inputIsValid = input.checkValidity();
                if (!inputIsValid) {
                    isValid = false;
                }
            } else {
                const selectedOption = el.querySelector('.custom-select .js-option[aria-selected="true"]');
                if (!selectedOption) {
                    isValid = false;
                }
            }

            // Show custom message and scroll to it
            if (!isValid) {
                isAnyValid = false;
                const label = el.querySelector('.label');
                label.setAttribute('aria-live', 'polite');
                label.classList.add('label--contains-error');
                const labelPrefix = document.createElement('span');
                labelPrefix.innerText = `${theme.strings.productsProductChooseA} `;
                labelPrefix.className = 'label__prefix';
                label.prepend(labelPrefix);
                theme.scrollToRevealElement(el);
            }
        });

        return isAnyValid;
    }

    connectedCallback(){
        const mediaallery = this.querySelector("media-gallery")
        const detailContainer = this.querySelector(".detail")
         const observer = new ResizeObserver(entries => {
            for (const entry of entries) {
                if(mediaallery && detailContainer){
                    const mediaalleryStyles = mediaallery.getBoundingClientRect();
                    const detailContainerStyle =detailContainer.getBoundingClientRect();
                    this.dispatchEvent(new CustomEvent('on:resize:change',  {
                        detail: {
                            mediaWidth: mediaalleryStyles.width,
                            detailWidth: detailContainerStyle.width
                        },
                        bubbles: true,     
                        cancelable: true 
                    }
                     ));
                }
                 
            }
        });
        observer.observe(this);

        this.loadCTLForm()
    }

    loadCTLForm(){
        const urlParams = new URLSearchParams(window.location.search)
        this.from_section = urlParams.get('from_section') 
        this.from_title   = urlParams.get('from_title')  
        this.from_color   = urlParams.get('from_color') 
        this.channel     = urlParams.get('channel')
        this._get_the_look_id = urlParams.get('_get_the_look_id')
        this._influencer_handle = urlParams.get('_influencer_handle')
    }
};

window.customElements.define('product-form', ProductForm);

class ProductContainer extends HTMLElement {
    constructor(){
        super()
    }
    connectedCallback(){
        const productForm = document.querySelector("product-form")
        if(!productForm) return; 
        productForm.addEventListener("on:resize:change",(e)=>{
            const detail = e.detail
            const detailWidth = detail.detailWidth
            const mediaWidth = detail.mediaWidth
            if(detailWidth && mediaWidth){
                this.style.maxWidth = (detailWidth + mediaWidth + 44) + 'px'
            }else{
                this.style.maxWidth = '100%'
            }
        })
    }
}

customElements.define('product-container', ProductContainer)

class ProductInventory extends HTMLElement {
    constructor() {
        super();
        window.initLazyScript(this, this.initLazySection.bind(this));
    }

    initLazySection() {
        this.threshold = parseInt(this.dataset.threshold, 10);
        this.productInventory = this.querySelector('.product-inventory');
        this.inventoryNotice = this.querySelector('.product-inventory__status');
        this.variantInventory = this.getVariantInventory();

        this.closest('.js-product').addEventListener('on:variant:change', this.handleVariantChange.bind(this));
    }

    /**
     * Gets the inventory data for all product variants
     * @returns {?object}
     */
    getVariantInventory() {
        const dataEl = this.querySelector('[type="application/json"]');
        return this.variantInventory || JSON.parse(dataEl.textContent);
    }

    /**
     * Handles a 'change' event on the variant picker element
     * @param {object} evt - Event object
     */
    handleVariantChange(evt) {
        this.updateInventory(
            evt.detail.variant
                ? this.variantInventory.find((v) => v.id === evt.detail.variant.id)
                : null
        );
    }

    /**
     *
     * Updates the inventory notice
     * @param {?object} inventory - the inventory data
     */
    updateInventory(inventory) {
        if (!inventory) {
            this.productInventory.hidden = true;
            return;
        }

        const count = inventory.inventory_quantity;
        const showCount = this.dataset.showInventoryCount === 'always'
            || (
                this.dataset.showInventoryCount === 'low'
                && count <= this.threshold
            );

        let notice = null;
        if (showCount) {
            if (count <= this.threshold) {
                notice = this.dataset.textXLeftLow.replace('[QTY]', count);
            } else {
                notice = this.dataset.textXLeftOk.replace('[QTY]', count);
            }
        } else {
            // eslint-disable-next-line no-lonely-if
            if (count <= this.threshold) {
                notice = this.dataset.textLow;
            } else {
                notice = this.dataset.textOk;
            }
        }

        this.productInventory.classList.toggle('product-inventory--low', count <= this.threshold);
        this.productInventory.classList.toggle('product-inventory--ok', count > this.threshold);
        this.inventoryNotice.innerText = notice;
        this.productInventory.hidden = false;
    }
}

customElements.define('product-inventory', ProductInventory);

if (!customElements.get('quantity-wrapper')) {
    class QuantityWrapper extends HTMLElement {
        connectedCallback() {
            this.addEventListener('click', this.handleClick.bind(this));
        }

        handleClick(evt) {
            const btn = evt.target.closest('[data-quantity]');
            if (btn) {
                evt.preventDefault();
                const input = this.querySelector('[name="quantity"]');
                let change = btn.dataset.quantity === 'up' ? 1 : -1;
                if (input.step) {
                    change *= parseInt(input.step, 10);
                }
                input.value = Math.max(1, parseInt(input.value, 10) + change);
                input.dispatchEvent(
                    new CustomEvent('change', { bubbles: true, cancelable: false })
                );
            }
        }
    }

    customElements.define('quantity-wrapper', QuantityWrapper);
}

if (theme.settings.quickbuyStyle !== 'off') {
    theme.quickbuy = {
        quickbuyResizeObserver: null,
        init: () => {
            theme.quickbuy.quickbuyResizeObserver = new ResizeObserver((entries) => {
                for (let i = 0; i < entries.length; i += 1) {
                    const entry = entries[i];
                    if (entry.contentBoxSize || entry.contentRect) {
                        theme.quickbuy.debouncedQuickbuyResize(entry.target);
                    }
                }
            });

            theme.addDelegateEventListener(document, 'click', '.quickbuy-toggle', (evt, delEl) => {
                const productUrl = delEl.href;

                // Only show dropdown if screen is large enough for it to be useful
                if (window.innerWidth >= 768) {
                    evt.preventDefault();

                    // cancel current request if one exists
                    if (theme.quickbuy.currentRequestAbortController) {
                        theme.quickbuy.currentRequestAbortController.abort('Existing request not needed');
                    }
                    theme.quickbuy.currentRequestAbortController = new AbortController();

                    const block = delEl.closest('.product-block');
                    const slider = delEl.closest('.collection-slider');
                    let detailCont = null;
                    let quickbuyCont = null;
                    let sliderRow = null;

                    // do different things if it's inside a slideshow
                    if (slider) {
                        sliderRow = slider.closest('.collection-slider-row');
                        // slider without detail
                        if (!sliderRow.querySelector('.quickbuy-container')) {
                            return;
                        }
                        quickbuyCont = sliderRow.querySelector('.quickbuy-container');
                    } else {
                        theme.quickbuy.setBlockHeights(block);
                        quickbuyCont = block.querySelector('.quickbuy-container');
                    }
                    detailCont = quickbuyCont.querySelector('.inner');

                    // toggle active class on block
                    block.classList.toggle('expanded');
                    if (block.classList.contains('expanded')) {
                        quickbuyCont.dispatchEvent(new CustomEvent('on:quickbuy:before-open', { bubbles: true }));

                        // expanding
                        if (slider) {
                            // if another block is expanded, remove its expanded class
                            const otherExpanded = Array.from(slider.querySelectorAll('.product-block.expanded')).filter((el) => el !== block);
                            otherExpanded.forEach((el) => el.classList.remove('expanded'));

                            // if expanding from empty, set initial detail container height to 0
                            if (otherExpanded.length === 0) {
                                quickbuyCont.style.height = 0;
                            }
                        } else {
                            // close expanded siblings
                            [...block.parentElement.children].forEach((el) => {
                                if (el !== block && el.classList.contains('expanded')) {
                                    theme.quickbuy.contractDetail(el, true);
                                }
                            });
                        }

                        // monitor for contents changing size
                        theme.quickbuy.quickbuyResizeObserver.disconnect();
                        theme.quickbuy.quickbuyResizeObserver.observe(detailCont);

                        // add spinner
                        detailCont.innerHTML = '<div class="loading-spinner"></div>';

                        // load in content
                        fetch(productUrl, {
                            method: 'get',
                            signal: theme.quickbuy.currentRequestAbortController.signal
                        })
                            .then((response) => {
                                if (!response.ok) {
                                    throw new Error(`HTTP error! Status: ${response.status}`);
                                }
                                return response.text();
                            })
                            .then((response) => {
                                const tmpl = document.createElement('template');
                                tmpl.innerHTML = response;
                                const newDetail = tmpl.content.querySelector('.quickbuy-content').cloneNode(true);

                                // convert to quickbuy content
                                newDetail.querySelectorAll('.more').forEach((el) => { el.href = productUrl; });
                                newDetail.querySelectorAll('.product-title').forEach((el) => {
                                    el.innerHTML = `<a href="">${el.innerHTML}</a>`;
                                    el.firstElementChild.href = productUrl;
                                });
                                newDetail.querySelectorAll('.show-gallery').forEach((el) => {
                                    el.classList.remove('show-gallery');
                                    el.href = productUrl;
                                });
                                newDetail.querySelectorAll('.not-in-quickbuy').forEach((el) => { el.remove(); });
                                newDetail.querySelectorAll('.only-in-quickbuy').forEach((el) => { el.classList.remove('only-in-quickbuy'); });
                                newDetail.querySelectorAll('.sticky-content-container').forEach((el) => { el.classList.remove('sticky-content-container'); });
                                newDetail.querySelectorAll('pickup-availability').forEach((el) => { el.remove(); });
                                newDetail.querySelectorAll('variant-picker[data-update-url="true"]').forEach((el) => { el.dataset.updateUrl = false; });
                                newDetail.querySelectorAll('noscript').forEach((el) => el.remove());

                                // enforce thumbnails-under gallery layout
                                newDetail.querySelectorAll('.media-gallery .main-image carousel-slider').forEach((el) => { el.classList.remove('mobile-only'); });
                                newDetail.querySelectorAll('.product-media-collage').forEach((el) => { el.remove(); });
                                newDetail.querySelectorAll('.media-gallery .thumbnails').forEach((el) => { el.classList.remove('mobile-only'); });
                                ['media-gallery--layout-carousel-beside', 'media-gallery--layout-columns-1', 'media-gallery--layout-columns-2', 'media-gallery--layout-collage-1', 'media-gallery--layout-collage-2'].forEach((cl) => {
                                    newDetail.querySelectorAll(`.${cl}`).forEach((el) => {
                                        el.classList.remove(cl);
                                        el.classList.add('media-gallery--layout-carousel-under');
                                    });
                                });

                                // add any external resources
                                tmpl.content.querySelectorAll('.section-main-product link[rel="stylesheet"]').forEach((el) => {
                                    if (!document.querySelector(`link[rel="stylesheet"][href="${el.getAttribute('href')}"]`)) {
                                        document.head.insertAdjacentHTML('beforeend', el.outerHTML);
                                    }
                                });
                                tmpl.content.querySelectorAll('.section-main-product script[src]').forEach((el) => {
                                    if (!document.querySelector(`script[src="${el.getAttribute('src')}"]`)) {
                                        const scr = document.createElement('script');
                                        scr.src = el.getAttribute('src');
                                        scr.defer = 'defer';
                                        document.head.appendChild(scr);
                                    }
                                });

                                detailCont.innerHTML = '';
                                detailCont.appendChild(newDetail);

                                quickbuyCont.dispatchEvent(new CustomEvent('on:quickbuy:after-open', { bubbles: true }));
                            })
                            .catch((error) => {
                                console.warn(error);
                            });

                        // enable close button
                        const closeButton = quickbuyCont.querySelector('.close-detail');
                        closeButton.removeAttribute('tabindex');
                        closeButton.addEventListener('click', theme.quickbuy.onCloseClick);

                        // Scroll to an appropriate position
                        const scrollOffset = -120 - theme.stickyHeaderHeight();
                        if (slider) {
                            // Simple for a carousel
                            window.scrollTo({ top: theme.getOffsetTopFromDoc(quickbuyCont) + scrollOffset, behavior: 'smooth' });
                        } else {
                            // Grid - calculate where to scroll if quickbuys above this block will collapse
                            theme.quickbuy.saveProductGridSpacingData();
                            window.scrollTo({ top: parseFloat((block.dataset.qbScrollTo || theme.getOffsetTopFromDoc(quickbuyCont))) + scrollOffset, behavior: 'smooth' });
                        }
                    } else {
                        // Close quick buy
                        if (slider) {
                            // collapse detail container & empty
                            quickbuyCont.style.height = '0px';
                            setTimeout(() => {
                                if (quickbuyCont.style.height === '0px') {
                                    detailCont.innerHTML = '';
                                }
                            }, parseFloat(
                                getComputedStyle(quickbuyCont).transitionDuration
                            ) * 1000);
                        } else {
                            theme.quickbuy.contractDetail(block);
                        }

                        // scroll to top of closing block, after reflow
                        setTimeout(() => {
                            window.scrollTo({ top: theme.getOffsetTopFromDoc(block) - theme.stickyHeaderHeight() - 20, behavior: 'smooth' });
                        }, 100);

                        // disable close button
                        const closeButton = quickbuyCont.querySelector('.close-detail');
                        closeButton.setAttribute('tabindex', '-1');
                        closeButton.removeEventListener('click', theme.quickbuy.onCloseClick);
                    }
                }
            });
        },

        debouncedQuickbuyResizeTimeoutID: -1,
        debouncedQuickbuyResize: (qbInner) => {
            clearTimeout(theme.quickbuy.debouncedQuickbuyResizeTimeoutID);
            theme.quickbuy.debouncedQuickbuyResizeTimeoutID = setTimeout(() => {
                const qbc = qbInner.closest('.quickbuy-container');
                const block = qbc.closest('.product-block');
                if (block) {
                    // use padding in grid
                    // also check expanded class in case it's mid close transition
                    if (block.classList.contains('expanded')) {
                        const targetHeight = qbInner.clientHeight;
                        block.style.paddingBottom = `${targetHeight + 20}px`;
                        qbc.style.height = `${targetHeight}px`;
                    }
                } if (qbInner.childElementCount > 0) {
                    // use height in carousel
                    // - if not empty (e.g. post-close tidy-up)
                    qbc.style.height = `${qbInner.clientHeight}px`;
                }
            }, 100);
        },

        contractDetail: (block, instant) => {
            // Cancel everything
            theme.quickbuy.quickbuyResizeObserver.disconnect();
            clearTimeout(theme.quickbuy.debouncedQuickbuyResizeTimeoutID);

            block.classList.remove('expanded');
            const quickbuyCont = block.querySelector('.quickbuy-container');
            if (instant) {
                quickbuyCont.style.transitionDuration = '0ms';
                block.style.transitionDuration = '0ms';
            }
            quickbuyCont.style.height = 0;
            block.style.paddingBottom = 0;
            // empty .inner after transition
            const msToClose = parseFloat(getComputedStyle(quickbuyCont).transitionDuration) * 1000;
            setTimeout(() => {
                if (quickbuyCont.style.height === '0px') {
                    quickbuyCont.querySelector('.inner').innerHTML = '';
                }
                if (instant) {
                    quickbuyCont.style.transitionDuration = '';
                    block.style.transitionDuration = '';
                }
            }, msToClose);

            quickbuyCont.dispatchEvent(new CustomEvent('on:quickbuy:after-close', { bubbles: true }));
        },

        saveProductGridSpacingData: () => {
            document.querySelectorAll('.product-grid').forEach((el) => {
                const blocks = el.querySelectorAll('.product-block .block-inner');
                if (blocks.length <= 1) return; // Skip for empty colls
                let row = 0;
                let currTop = 0;
                let runningHeight = 0;
                const gutter = parseFloat(getComputedStyle(el).rowGap);
                const listingOffset = theme.getOffsetTopFromDoc(blocks[0]);
                const rowOffsets = [listingOffset];
                blocks.forEach((block, index) => {
                    if (block.clientHeight > runningHeight) {
                        runningHeight = block.clientHeight;
                    }

                    const currOffsetTop = theme.getOffsetTopFromDoc(block);
                    if (index === 0) {
                        currTop = currOffsetTop;
                    } else if (currOffsetTop > currTop) {
                        row += 1;
                        currTop = currOffsetTop;
                        rowOffsets.push(gutter + runningHeight + rowOffsets[row - 1]);
                        runningHeight = 0;
                    }

                    block.dataset.gridRow = row;
                });

                blocks.forEach((block) => {
                    block.dataset.qbScrollTo = rowOffsets[block.dataset.gridRow] + block.clientHeight;
                });
            });
        },

        setBlockHeights: (block) => {
            // Align product block heights in this row
            const list = block.closest('.product-grid');
            if (!list) return;

            let tallest = 0;
            const blockTop = block.offsetTop;
            const blocksInThisRow = [...list.querySelectorAll('.product-block')].filter((el) => el.offsetTop === blockTop);
            blocksInThisRow.forEach((el) => {
                const elInnerHeight = el.querySelector('.block-inner .block-inner-inner').clientHeight;
                if (elInnerHeight > tallest) tallest = elInnerHeight;
            });
            blocksInThisRow.forEach((el) => {
                el.querySelector('.block-inner').style.setProperty('--qb-block-height', `${tallest}px`);
            });
        },

        onCloseClick: (evt) => {
            evt.preventDefault();
            evt.stopPropagation();
            const slider = evt.target.closest('.collection-slider-row');
            if (slider) {
                slider.querySelector('.product-block.expanded .quickbuy-toggle').dispatchEvent(new CustomEvent('click', { bubbles: true, cancelable: true }));
            } else {
                evt.target.closest('.product-block').querySelector('.quickbuy-toggle').dispatchEvent(new CustomEvent('click', { bubbles: true, cancelable: true }));
            }
        }
    };

    theme.quickbuy.init();
}

// quick add
if (typeof theme.quickadd === 'undefined') {
    theme.quickadd = {
        resizeObserver: null,
        currentRequestAbortController: null,
        debouncedResizeTimeoutID: -1,

        init: () => {
            // 初始化 ResizeObserver
            theme.quickadd.resizeObserver = new ResizeObserver((entries) => {
                for (let i = 0; i < entries.length; i += 1) {
                    const entry = entries[i];
                    if (entry.contentBoxSize || entry.contentRect) {
                        theme.quickadd.debouncedResize(entry.target);
                    }
                }
            });

            // 绑定点击事件（只处理自定义 .quickadd-toggle）
            theme.addDelegateEventListener(document, 'click', '.quickadd-toggle', (evt, delEl) => {
                const productUrl = delEl.href;
                evt.preventDefault();
                const block = delEl.closest('.product-block');
                const simpleModal = delEl.dataset.simpleModal
                const sizesSelector = document.querySelector('sizes-selector-drawer')
                // is in  dialog
                const isInDialog =  block?.closest("modal-dialog")
                const theLook = delEl.closest('the-looks')
                if(simpleModal == 'true' && sizesSelector){
                    const varintTemplateSource = block?.querySelector('.size-varint-template ul');
                    const ProductImg = block?.querySelector('img');
                    const sizesSelectorUl = sizesSelector.querySelector('.size-selector-body ul');
                    if(!block || !varintTemplateSource || !sizesSelectorUl){
                        return;
                    }
                    const varintTemplate = varintTemplateSource.cloneNode(true);
                    const src = ProductImg?.getAttribute('src');
                    const restoredLink = src ? urlRestore(src) : '';
                    const variantValue = typeof block.dataset.variant === 'string' ? block.dataset.variant.trim() : '';
                    const variantColor = variantValue ? variantValue.split('/')[0].trim() : '';
                    if(theLook && variantColor){
                        const channel = theLook.dataset.channel
                        gtag('event', 'select_item', {
                            items: [{
                                item_id: block.dataset.productId,
                                item_name: block.dataset.title,
                                price: block.dataset.price,
                                variant: variantColor,
                                item_category5:restoredLink,
                                item_list_name: channel,
                            }],
                            currency: Shopify.currency.active,
                            item_list_name:`'${channel}'`,
                            item_list_id:"ctl",
                            transport_type: 'beacon'
                        });
                    }
                    sizesSelectorUl.replaceWith(varintTemplate)
                    const rect = block.getBoundingClientRect();
                   // const distanceFromTop = rect.top + window.scrollY - headerHeight;       
                     const distanceFromTop = evt.clientY-100;    
                   // const top = document.querySelector('.input-row').clientHeight + 500
                    const top = distanceFromTop
                    const ctl = document.querySelector('the-looks')
                    const left = delEl.getBoundingClientRect().left ;
                    if(isInDialog){
                        sizesSelector.position(rect.top,left)
                    }else{
                        sizesSelector.position(top,left)
                    }

                    sizesSelector.show()
                    sizesSelectorUl.style.height = varintTemplate.clientHeight +'px'
                    return
                }

                // abort 旧请求
                if (theme.quickadd.currentRequestAbortController) {
                    theme.quickadd.currentRequestAbortController.abort('Existing request not needed');
                }
                theme.quickadd.currentRequestAbortController = new AbortController();


                const modal = document.getElementById("modal-ctl")
                const modalInner = modal.querySelector('.inner')
                const quickaddCont = block.querySelector('.quickadd-container');
                const inner = quickaddCont.querySelector('.inner');
                block.classList.toggle('expanded');
                if (block.classList.contains('expanded')) {
                    // 打开 Quick Add
                    inner.innerHTML = '<div class="loading-spinner"></div>';
                    theme.quickadd.resizeObserver.observe(modalInner);

                    fetch(productUrl, { signal: theme.quickadd.currentRequestAbortController.signal })
                        .then((res) => res.text())
                        .then((html) => {
                            const tmpl = document.createElement('template');
                            tmpl.innerHTML = html;

                            // 从完整页面中提取 Quick Buy 内容
                            const newDetail = tmpl.content.querySelector('.quickbuy-content') || tmpl.content.querySelector('.product') || document.createElement('div');
                            newDetail.querySelectorAll('the-looks').forEach((el) => el.remove());
                            modalInner.innerHTML = '';
                            modalInner.appendChild(newDetail.cloneNode(true));

                            // propertiesInput
                            const theLook = document.querySelector('the-looks')
                            if(theLook){
                                //add channel
                                const channel = theLook.dataset.channel
                                const hiddenInput = document.createElement("input");
                                hiddenInput.type = "hidden";
                                hiddenInput.name = "attributes[_theLook]";
                                hiddenInput.value = channel;
                                modalInner.querySelector('buy-buttons form').appendChild(hiddenInput)
                                // add product Initial title
                                const sourceProductTitleInput = document.createElement("input");
                                const sourceProductTitle = theLook.dataset.title
                                sourceProductTitleInput.type = "hidden";
                                sourceProductTitleInput.name = "attributes[from_title]";
                                sourceProductTitleInput.value = sourceProductTitle;
                                modalInner.querySelector('buy-buttons form').appendChild(sourceProductTitleInput)
                                // add product  color
                                const productInput = document.createElement("input");
                                const sourceProductColor = theLook.dataset.option
                                productInput.type = "hidden";
                                productInput.name = "attributes[complete_the_look_color]";
                                productInput.value = sourceProductColor;
                                modalInner.querySelector('buy-buttons form').appendChild(productInput)
                                // add product title
                                const productTitleInput = document.createElement("input");
                                const productTitle = newDetail.querySelector('.product-title').innerText
                                productTitleInput.type = "hidden";
                                productTitleInput.name = "attributes[complete_the_look_title]";
                                productTitleInput.value = productTitle;
                                modalInner.querySelector('buy-buttons form').appendChild(productTitleInput)
                            }
                         

                            // 调整布局
                            modalInner.querySelectorAll('variant-picker').forEach((el) => el.dataset.updateUrl = false);
                            modalInner.querySelectorAll('.not-in-quickbuy').forEach((el) => el.remove());
                            modalInner.querySelectorAll('.complete-the-look').forEach((el) => el.remove());
                            modalInner.querySelectorAll('.sticky-content-container').forEach((el) => el.classList.remove('sticky-content-container'));
                            modalInner.querySelectorAll('pickup-availability').forEach((el) => el.remove());
                          
                            quickaddCont.dispatchEvent(new CustomEvent('on:quickadd:after-open', { bubbles: true  }));
                            theme.quickadd.contract(block);
                            modal.open()
                            
                            
                        })
                        .catch((err) => {
                            console.warn('Quick Add failed:', err);
                            inner.innerHTML = '<p>Failed to load product.</p>';
                        });
                } else {
                    // 关闭
                    theme.quickadd.contract(block);
                }
            });
        },

        debouncedResize: (inner) => {
            clearTimeout(theme.quickadd.debouncedResizeTimeoutID);
            theme.quickadd.debouncedResizeTimeoutID = setTimeout(() => {
                const cont = inner.closest('.quickadd-container');
                const block = cont.closest('.product-block');
                if (block && block.classList.contains('expanded')) {
                    const height = inner.clientHeight;
                    cont.style.height = `${height}px`;
                }
            }, 100);
        },

        contract: (block) => {
            theme.quickadd.resizeObserver.disconnect();
            clearTimeout(theme.quickadd.debouncedResizeTimeoutID);
            block.classList.remove('expanded');

            const cont = block.querySelector('.quickadd-container');
            const inner = cont.querySelector('.inner');

            cont.style.height = 0;
            block.style.paddingBottom = 0;

            setTimeout(() => {
                inner.innerHTML = '';
            }, parseFloat(getComputedStyle(cont).transitionDuration) * 1000);

            cont.dispatchEvent(new CustomEvent('on:quickadd:after-close', { bubbles: true }));
        },
    };

    theme.quickadd.init();
}


class SizesSelector extends Modal {

    constructor() {
        super();
        this.handleClick = this.handleClick.bind(this);
        // this.addEventListener('click', this.handleClick.bind(this));
        this.overlay = document.querySelector('.js-drawer-overlay')
    }

    handleClick(evt) {
        if(evt.target.matches('.size-selector-close')){
            evt.stopPropagation(); 
            this.hide()
        }
        if(evt.target.matches('.js-drawer-overlay')){
            evt.stopPropagation(); 
            this.hide()
        }
        if (evt.target !== this && !evt.target.matches('.js-close-selector-modal') ) return;
        this.hide()
    }



    connectedCallback(){

        this.isInDialog = this.closest('modal-dialog')

         this.querySelector('.size-selector-body').addEventListener('click',async (evt, delEl) => {
            const eventTarget = evt.target
             //add to cart
            if(eventTarget.localName === 'li'){
                const title = eventTarget.dataset.title
                const id = eventTarget.dataset.id
                const issoldOut = eventTarget.dataset.available < 1
                const preMessage  = eventTarget.dataset.preorderMessage
                const isPreOrder = eventTarget.classList.contains('pre-order')
                if(issoldOut)return;
                this.hide()
                const variants = (eventTarget.dataset.variants.split('/')[0]).trim()
                const cartRes = await  this.addToCart({
                    id,
                    title,
                    quantity:1,
                    color:variants,
                    properties:{
                       "Pre-Order":isPreOrder?preMessage:''
                    }
                })
                
                if (theme.settings.afterAddToCart === 'notification') {
                    const notification = document.getElementById('AddedNotification').content.firstElementChild.cloneNode(true);
                    notification.dataset.productTitle = title;
                    let notificationContainer = document.querySelector('.pageheader--sticky');
                    if (!notificationContainer) {
                        notificationContainer = document.querySelector('body');
                    }
                    notificationContainer.appendChild(notification);
                } else if (theme.settings.afterAddToCart === 'drawer') {
                    document.querySelector('.js-cart-drawer').open();
                }
                    document.dispatchEvent(new CustomEvent('on:cart:add', {
                        bubbles: true,
                        detail: {
                            variantId:id
                        }
                    }));

                    document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
        
            }
        })


    }
    hide(){
        document.removeEventListener('keyup', this.keyupHandler);
        if(!this.isInDialog){
            this.overlay.removeEventListener('click', this.handleClick);
            this.overlay.classList.remove('is-visible');
        }
        this.classList.add('sizes-selector-hide');
        this.removeEventListener('click', this.handleClick);
        document.querySelector('body').style.overflow = "auto"
    }
    show(){
        this.keyupHandler = (evt) => {
             evt.key === 'Escape' && this.hide()
        };

        document.addEventListener('keyup', this.keyupHandler);
        this.addEventListener('click', this.handleClick);
        if(!this.isInDialog){
            this.overlay.addEventListener('click', this.handleClick);
            this.overlay.classList.add('is-visible');
        }
        this.classList.remove('sizes-selector-hide');
        document.querySelector('body').style.overflow = "hidden"
    }

    addToCart({
        id,
        quantity = 1,
        title = '',
        color,
        properties
    }) {
        const theLook = document.querySelector('the-looks')
        if(theLook){
            const channel = theLook.dataset.channel
            const sourceProductColor = theLook.dataset.option
            const sourceProductTitle = theLook.dataset.title
            const propertiesData = {
                    _from_title:sourceProductTitle,
                    _from_color:sourceProductColor,
                    _channel:'complete-the-look',
                    _hanle:channel
                }
            Object.keys(properties).forEach((key) => {
                const value = properties[key]
                if(value){
                    propertiesData[key] = value
                }
            });

            return fetch('/cart/add.js', {
                method: 'POST',
                headers: {
            'Content-Type': 'application/json'
        },
                body: JSON.stringify({
                id,
                quantity,
                attributes:{
                    _theLook: 'ctl',
                },
                properties:propertiesData
                })
            }).then(res => res.json())
        }else{
            return fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
                },
                body: JSON.stringify({
                id,
                quantity,
                })
            }).then(res => res.json())
        }
        


        
    }

    position(top=0,left=0){
        this.style.top = top - 90 +'px'
        this.style.left = left - 180 +'px'
    }

}

customElements.define('sizes-selector-drawer', SizesSelector);



class CarouselSlider extends HTMLElement {
    constructor() {
        super();
        this.slides = [...this.querySelectorAll('.slider__item:not([hidden])')];
        if (this.slides.length < 2) {
            this.setCarouselState(false);
            return;
        }

        window.initLazyScript(this, this.init.bind(this));
    }

    init() {

        this.slides = [...this.querySelectorAll('.slider__item:not([hidden])')]; // 重新确认
        if (this.slides.length <= 2) return;

        this.slider = this.querySelector('.slider');
        this.grid = this.querySelector('.slider__grid');
        this.nav = this.querySelector('.slider-nav');
        this.rtl = document.dir === 'rtl';
        if (this.nav) {
            this.prevBtn = this.querySelector('button[name="prev"]');
            this.nextBtn = this.querySelector('button[name="next"]');
        }

        this.initSlider();
        window.addEventListener('on:debounced-resize', this.handleResize.bind(this));

        this.setAttribute('loaded', '');
    }

    initSlider() {

        if (!this.slides || this.slides.length < 2) {
            this.setCarouselState(false);
            return;
        }

        this.gridWidth = this.grid.clientWidth;

        // Distance between leading edges of adjacent slides (i.e. width of card + gap).
        this.slideSpan = this.getWindowOffset(this.slides[1]) - this.getWindowOffset(this.slides[0]);

        // Note: Slide can re-initialise after previous use
        this.currentIndex = Math.round(this.slider.scrollLeft / this.slideSpan) || 0;

        // Width of gap between slides.
        this.slideGap = this.slideSpan - this.slides[0].clientWidth;

        this.slidesPerPage = Math.round((this.gridWidth + this.slideGap) / this.slideSpan);
        this.slidesToScroll = theme.settings.sliderItemsPerNav === 'page' ? this.slidesPerPage : 1;
        this.totalPages = this.slides.length - this.slidesPerPage + 1;

        this.setCarouselState(this.totalPages >= 1);
        if (this.dataset.dynamicHeight === 'true') {
            this.updateDynamicHeight();
        }
        this.addListeners();
        if (this.totalPages < 1 || !this.nav) return;

        this.sliderStart = this.getWindowOffset(this.slider);
        if (!this.sliderStart) this.sliderStart = (this.slider.clientWidth - this.gridWidth) / 2;
        this.sliderEnd = this.sliderStart + this.gridWidth;

        if (window.matchMedia('(pointer: fine)').matches) {
            this.slider.classList.add('is-grabbable');
        }

        this.setButtonStates();
    }

    /**
     * Re-initialise state. Call if slides have changed.
     */
    refresh() {
        if (this.hasAttribute('loaded')) {
            this.removeListeners();
            this.style.removeProperty('--current-slide-height');
        }
        this.slides = [...this.querySelectorAll('.slider__item:not([hidden])')];
        if (this.slides.length < 2) {
            this.setCarouselState(false);
            return;
        }else{
             this.setCarouselState(true);
        }
        this.init();
    }

    addListeners() {
        this.scrollHandler = debounce(this.handleScroll.bind(this), 20);
        this.slider.addEventListener('scroll', this.scrollHandler);

        if (this.nav) {
            this.navClickHandler = this.handleNavClick.bind(this);
            this.nav.addEventListener('click', this.navClickHandler);
        }

        if (window.matchMedia('(pointer: fine)').matches) {
            this.mousedownHandler = this.handleMousedown.bind(this);
            this.mouseupHandler = this.handleMouseup.bind(this);
            this.mousemoveHandler = this.handleMousemove.bind(this);

            this.slider.addEventListener('mousedown', this.mousedownHandler);
            this.slider.addEventListener('mouseup', this.mouseupHandler);
            this.slider.addEventListener('mouseleave', this.mouseupHandler);
            this.slider.addEventListener('mousemove', this.mousemoveHandler);
            this.slider.addEventListener('dragstart', (evt) => { evt.preventDefault(); });
        }
    }

    removeListeners() {
        this.slider.removeEventListener('scroll', this.scrollHandler);

        if (this.nav) {
            this.nav.removeEventListener('click', this.navClickHandler);
        }

        if (window.matchMedia('(pointer: fine)').matches) {
            this.slider.removeEventListener('mousedown', this.mousedownHandler);
            this.slider.removeEventListener('mouseup', this.mouseupHandler);
            this.slider.removeEventListener('mouseleave', this.mouseupHandler);
            this.slider.removeEventListener('mousemove', this.mousemoveHandler);
        }
    }

    /**
     * Handles 'scroll' events on the slider element.
     */
    handleScroll() {
        // Scroll may be triggered immediately after a resize
        if (this.gridWidth !== this.grid.clientWidth) this.handleResize();

        const previousIndex = this.currentIndex;
        this.currentIndex = Math.round(Math.abs(this.slider.scrollLeft) / this.slideSpan);

        if (this.nav) {
            this.setButtonStates();
        }

        if (this.dataset.dynamicHeight === 'true') {
            this.updateDynamicHeight();
        }

        if (this.dataset.dispatchEvents === 'true' && previousIndex !== this.currentIndex) {
            this.dispatchEvent(
                new CustomEvent('on:carousel-slider:select', {
                    bubbles: true,
                    detail: {
                        index: this.currentIndex,
                        slide: this.slides[this.currentIndex]
                    }
                })
            );
        }
    }

    /**
     * Handles 'mousedown' events on the slider element.
     * @param {object} evt - Event object.
     */
    handleMousedown(evt) {
        this.mousedown = true;
        this.mousemoved = false;
        this.startX = evt.pageX - this.sliderStart;
        this.scrollPos = this.slider.scrollLeft;
        this.slider.classList.add('is-grabbing');
    }

    /**
     * Handles 'mouseup' events on the slider element.
     * @param {object} evt - Event object.
     */
    handleMouseup(evt) {
        this.mousedown = false;
        this.slider.classList.remove('is-grabbing');
        if (this.mousemoved) {
            evt.preventDefault();
            this.slider.classList.remove('is-dragging');
        }
    }

    /**
     * Handles 'mousemove' events on the slider element.
     * @param {object} evt - Event object.
     */
    handleMousemove(evt) {
        if (!this.mousedown) return;
        evt.preventDefault();

        const x = evt.pageX - this.sliderStart;
        this.slider.scrollTo({ left: this.scrollPos - (x - this.startX), behavior: 'instant' });
        this.mousemoved = true;
        this.slider.classList.add('is-dragging');
    }

    /**
     * Handles 'click' events on the nav buttons container.
     * @param {object} evt - Event object.
     */
    handleNavClick(evt) {
        if (!evt.target.matches('.slider-nav__btn')) return;

        if ((evt.target.name === 'next' && !this.rtl) || (evt.target.name === 'prev' && this.rtl)) {
            this.scrollPos = this.slider.scrollLeft + (this.slidesToScroll * this.slideSpan);
        } else {
            this.scrollPos = this.slider.scrollLeft - (this.slidesToScroll * this.slideSpan);
        }

        this.slider.scrollTo({ left: this.scrollPos, behavior: 'smooth' });
    }

    /**
     * Handles 'on:debounced-resize' events on the window.
     */
    handleResize() {
        if (this.nav) this.removeListeners();
        this.initSlider();
    }

    /**
     * Show a specific item inside this slider.
     * @param {Element} el - Slide element
     * @param {string} transition - Transition to pass to behavior parameter of scrollTo (optional)
     */
    scrollToElement(el, transition) {
        if (!this.getSlideVisibility(el)) {
            // this.scrollPos = this.rtl ? (el.offsetLeft + this.slideSpan) : el.offsetLeft;
            this.scrollPos = el.offsetLeft;
            this.slider.scrollTo({ left: this.scrollPos, behavior: transition || 'smooth' });
        }
    }

    /**
     * Sets a height property based on the current slide.
     */
    updateDynamicHeight() {
        this.style.setProperty('--current-slide-height', `${this.slides[this.currentIndex].firstElementChild.clientHeight}px`);
    }

    /**
     * Gets the offset of an element from the edge of the viewport (left for ltr, right for rtl).
     * @param {number} el - Element.
     * @returns {number}
     */
    getWindowOffset(el) {
        if (!el) return 0;
        return this.rtl
            ? window.innerWidth - el.getBoundingClientRect().right
            : el.getBoundingClientRect().left;
    }

    /**
     * Gets the visible state of a slide.
     * @param {Element} el - Slide element.
     * @returns {boolean}
     */
    getSlideVisibility(el) {
        const slideStart = this.getWindowOffset(el);
        const slideEnd = Math.floor(slideStart + this.slides[0].clientWidth);
        return slideStart >= this.sliderStart && slideEnd <= this.sliderEnd;
    }

    /**
     * Sets the active state of the carousel.
     * @param {boolean} active - Set carousel as active.
     */
    setCarouselState(active) {
        if (active) {
            this.removeAttribute('inactive');

            // If slider width changed when activated, reinitialise it.
            if(this.grid && this.gridWidth !== this.grid.clientWidth) {
                this.handleBreakpointChange && this.handleBreakpointChange();
            }
        } else {
            this.setAttribute('inactive', '');
        }
    }

    /**
     * Sets the disabled state of the nav buttons.
     */
    setButtonStates() {
        this.prevBtn.disabled = this.getSlideVisibility(this.slides[0]) && this.slider.scrollLeft === 0;
        this.nextBtn.disabled = this.getSlideVisibility(this.slides[this.slides.length - 1]) || this.slides.length - this.currentIndex<=2 ;
    }
}

customElements.define('carousel-slider', CarouselSlider);

const TermsAgreement = class extends HTMLElement {
    connectedCallback() {
        this.delegatedEvent = theme.addDelegateEventListener(document, 'click', '#cartform [name="checkout"], .additional-checkout-buttons input, a[href*="/checkout"]', this.handleFormSubmittingEvent.bind(this));
    }

    disconnectedCallback() {
        document.removeEventListener('click', this.delegatedEvent);
    }

    handleFormSubmittingEvent(evt) {
        if (!this.querySelector('input:checked')) {
            evt.preventDefault();
            theme.showQuickPopup(theme.strings.cartTermsConfirmation, this.querySelector('[type="checkbox"]'));
        }
    }
};

window.customElements.define('terms-agreement', TermsAgreement);

const ToggleTarget = class extends HTMLElement {
    constructor() {
        super();
        this.addEventListener('click', this.toggleOpen.bind(this));
        this.addEventListener('keyup', this.handleKeyUp.bind(this));
        if (this.dataset.toggleCloseLabel) {
            this.dataset.toggleOpenLabel = this.innerHTML;
        }
    }

    /**
     * Handles 'keyup' events on the document.
     * @param {object} evt - Event object.
     */
    handleKeyUp(evt) {
        if (evt.key === 'Enter') this.toggleOpen(evt);
    }

    /**
     * Opens or closes the target element.
     * @param {object} evt - Event object.
     */
    toggleOpen(evt) {
        evt.preventDefault();

        const target = document.querySelector(this.dataset.toggleTarget);
        const transCont = target.querySelector('.toggle-target-container');
        const doCollapse = !target.classList.contains('toggle-target--hidden');
        const transitionDuration = parseFloat(getComputedStyle(target).transitionDuration) * 1000;

        if (!target.classList.contains('toggle-target--in-transition')) {
            if (doCollapse) {
                this.classList.add('toggle-target-toggler--is-hidden');
                target.classList.add('toggle-target--in-transition', 'toggle-target--hiding');
                target.style.height = `${transCont.clientHeight}px`;
                target.style.opacity = 1;

                if (this.dataset.toggleCloseLabel) {
                    this.innerHTML = this.dataset.toggleOpenLabel;
                }

                setTimeout(() => {
                    target.style.height = 0;
                    target.style.opacity = 0;

                    setTimeout(() => {
                        target.classList.add('toggle-target--hidden');
                        target.classList.remove('toggle-target--in-transition', 'toggle-target--hiding');
                        target.style.height = '';
                        target.style.opacity = '';
                    }, transitionDuration);
                }, 10);
            } else {
                this.classList.remove('toggle-target-toggler--is-hidden');
                target.classList.add('toggle-target--in-transition', 'toggle-target--revealing');
                target.style.height = 0;
                target.style.opacity = 0;
                target.style.display = 'block';

                if (this.dataset.toggleCloseLabel) {
                    this.innerHTML = this.dataset.toggleCloseLabel;
                }

                setTimeout(() => {
                    target.style.height = `${transCont.clientHeight}px`;
                    target.style.opacity = 1;

                    setTimeout(() => {
                        target.classList.remove('toggle-target--hidden', 'toggle-target--in-transition', 'toggle-target--revealing');
                        target.style.height = '';
                        target.style.opacity = '';
                        target.style.display = '';
                    }, transitionDuration);
                }, 10);
            }
        }
    }
};

window.customElements.define('toggle-target', ToggleTarget);

class VariantContent extends HTMLElement {
    constructor() {
        super();
        if (this.childElementCount > 0) {
            this.closest('.js-product').addEventListener('on:variant:change', this.handleVariantChange.bind(this));
        }
    }

    /**
     * Handles a 'change' event on the variant picker element
     * @param {object} evt - Event object
     */
    handleVariantChange(evt) {
        [...this.childNodes].filter((el) => el.tagName !== 'SCRIPT').forEach((el) => el.remove());
        this.querySelectorAll(':not(script)').forEach((el) => el.remove());
        const contentToShow = this.querySelector(`[data-variant="${evt.detail.variant ? evt.detail.variant.id : ''}"]`);
        if (contentToShow) {
            this.insertAdjacentHTML('beforeend', contentToShow.innerHTML);
        }
    }
}

customElements.define('variant-content', VariantContent);

const AnnouncementBar = class extends HTMLElement {
    constructor() {
        super();

        this.announcements = this.querySelectorAll('.announcement');
        if (this.announcements.length > 1) {
            this.backgrounds = Array.from(this.querySelectorAll('.announcement-bg'));
            this.changeDelay = 5000;
            this.currentAnnouncement = 0;
            this.querySelector('.announcement-bar__middle').addEventListener('focusin', this.pauseAnnouncements.bind(this));
            this.querySelector('.announcement-bar__middle').addEventListener('focusout', this.playAnnouncements.bind(this));
            this.querySelector('.announcement-button--previous').addEventListener('click', this.previousAnnouncement.bind(this));
            this.querySelector('.announcement-button--next').addEventListener('click', this.nextAnnouncement.bind(this));
            this.observer = new IntersectionObserver(this.handleIntersect.bind(this));
            this.observer.observe(this);
        }
    }

    handleIntersect(entries) {
        entries.forEach((entry) => {
            if (entry.target === this) {
                if (entry.isIntersecting) {
                    this.playAnnouncements(this);
                } else {
                    this.pauseAnnouncements(this);
                }
            }
        });
    }

    playAnnouncements() {
        if (this.announcements.length > 1) {
            this.announcementInterval = setInterval(() => {
                this.setCurrentAnnouncement(this.currentAnnouncement + 1);
            }, this.changeDelay);
        }
    }

    pauseAnnouncements() {
        if (this.announcementInterval) {
            clearInterval(this.announcementInterval);
        }
    }

    setCurrentAnnouncement(newIndex) {
        this.currentAnnouncement = newIndex % this.announcements.length;
        this.announcements.forEach((announcement, index) => {
            const background = this.backgrounds.find((bg) => bg.dataset.index === index.toString());
            if (index !== this.currentAnnouncement) {
                announcement.classList.add('announcement--inactive');
                if (background) {
                    background.classList.remove('is-active');
                }
            } else {
                announcement.classList.remove('announcement--inactive');
                if (background) {
                    background.classList.add('is-active');
                }
            }
        });

        const headingColor = this.announcements[this.currentAnnouncement].style.getPropertyValue('--heading-color');
        const textColor = this.announcements[this.currentAnnouncement].style.getPropertyValue('--text-color');
        const linkColor = this.announcements[this.currentAnnouncement].style.getPropertyValue('--link-color');
        if (headingColor) {
            this.style.setProperty('--heading-color', headingColor);
        }
        if (textColor) {
            this.style.setProperty('--text-color', textColor);
        }
        if (linkColor) {
            this.style.setProperty('--link-color', linkColor);
        }
    }

    previousAnnouncement() {
        this.setCurrentAnnouncement(this.currentAnnouncement - 1);
    }

    nextAnnouncement() {
        this.setCurrentAnnouncement(this.currentAnnouncement + 1);
    }
};

window.customElements.define('announcement-bar', AnnouncementBar);

/*
  If the following conditions are met:
  1. This section directly follows the header
  2. This section is set to be full page height

  Then:
  If the header is transparent, subtract the height of messages and announcement bar.
  If the header if not transparent, subtract the height of the header.
*/

const ImageWithTextOverlay = class extends HTMLElement {
    constructor() {
        super();

        this.fullHeightContainer = this.classList.contains('height--full') ? this : this.querySelector('.height--full');

        if (this.fullHeightContainer) {
            if (this.closest('.shopify-section').previousElementSibling) return;
            const ph = document.querySelector('.pageheader');
            if (!ph) {
                this.fullHeightContainer.classList.add('height--full-ignore-header-height');
                return;
            }

            const thisOffsetTop = theme.getOffsetTopFromDoc(this);
            const phOffsetTop = theme.getOffsetTopFromDoc(ph);
            if (phOffsetTop - 5 > thisOffsetTop) { // Must be lower. For trans header, it'll roughly match
                this.fullHeightContainer.classList.add('height--full-ignore-header-height');
                return;
            }

            this.fullHeightContainer.classList.add('height--full-minus-header-height');
            this.checkForHeaderHeightSubtraction();

            const handleResize = debounce(this.checkForHeaderHeightSubtraction.bind(this), 300);
            this.resizeObserver = new ResizeObserver((entries) => {
                for (let i = 0; i < entries.length; i += 1) {
                    const entry = entries[i];
                    if (entry.contentBoxSize || entry.contentRect) {
                        handleResize();
                    }
                }
            });
            this.resizeObserver.observe(this);
        }
    }

    checkForHeaderHeightSubtraction() {
        let height = window.innerHeight;
        // transparent header & top section? slightly different calculation
        if (document.querySelector('.pageheader--transparent-permitted') && !this.closest('.shopify-section').previousElementSibling) {
            document.querySelectorAll('.section-store-messages, .section-announcement-bar').forEach((el) => {
                height -= el.clientHeight;
            });
        } else {
            height -= document.querySelector('.section-header').clientHeight;
        }
        this.fullHeightContainer.style.setProperty('--image-height', `${height}px`);
    }
};

window.customElements.define('image-with-text-overlay', ImageWithTextOverlay);

const PageHeader = class extends HTMLElement {
    constructor() {
        super();

        // Search (delegate event as contents may update with cart changes)
        const mainSearch = this.querySelector('.main-search');
        if (mainSearch) {
            theme.addDelegateEventListener(this, 'click', '.show-search-link', (evt) => {
                evt.preventDefault();
                theme.manuallyLoadImages(mainSearch);
                document.body.classList.add('show-search');
                setTimeout(() => {
                    this.querySelector('.main-search__input').focus();
                }, 500);
            });
        }

        // Navigation
        theme.inlineNavigationCheck();

        // Reveal cart drawer
        if (theme.settings.cartType === 'drawer' && document.querySelector('.js-cart-drawer') && this.querySelector('.cart-link')) {
            theme.addDelegateEventListener(this, 'click', '.cart-link', (evt) => {
                evt.preventDefault();
                document.querySelector('.js-cart-drawer').open();
            });
        }

        // Init mobile nav again if it's open and this is a reload
        if (window.Shopify.designMode && document.body.classList.contains('reveal-mobile-nav')) {
            theme.openMobileNav();
        }

        // Preload images after any interaction
        this.preloadOnFirstInteraction = () => {
            theme.manuallyLoadImages(this);
            this.removeEventListener('mouseenter', this.preloadOnFirstInteraction);
            this.removeEventListener('touchstart', this.preloadOnFirstInteraction);
            this.removeEventListener('keydown', this.preloadOnFirstInteraction);
        };

        this.addEventListener('mouseenter', this.preloadOnFirstInteraction);
        this.addEventListener('touchstart', this.preloadOnFirstInteraction);
        this.addEventListener('keydown', this.preloadOnFirstInteraction);

        // Preload all images after a delay
        setTimeout(() => this.preloadOnFirstInteraction(), 5000);
    }

    connectedCallback() {
        this.section = document.querySelector('.section-header');
        this.transparentPageheader = document.querySelector('.pageheader--transparent-permitted');

        this.setTransparency();
        this.setSticky();
        this.setHeaderHeightProperty();

        window.addEventListener('scroll', this.afterScroll.bind(this));

        this.refreshHeader = () => {
            fetch(`${window.location.origin}?sections=header`)
                .then((response) => response.json())
                .then((data) => {
                    const template = document.createElement('template');
                    template.innerHTML = data.header;
                    const selectorsForRefresh = ['#pageheader .logo-area__right__inner .cart-link .cart-link__icon .cart-link__count_'];
                    const selectorsForRefreshIcon = ['#pageheader .logo-area__right__inner .cart-link .cart-link__icon'];
                    for (let i = 0; i < selectorsForRefresh.length; i += 1) {
                        const newEl = template.content.querySelector(selectorsForRefresh[i]);
                        const currentEl = this.querySelector(selectorsForRefresh[i]);
                        if(newEl){
                            currentEl.innerHTML = newEl.innerHTML;
                        }else{
                            console.log('xxx 0')
                        }
                        const bagNumberEl =  document.querySelector('#bag-number')
                        if(bagNumberEl){
                            bagNumberEl.innerHTML =  newEl.innerHTML;
                        }
                    }

                });
        };
        document.addEventListener('on:cart:change', this.refreshHeader);

        const debouncedAfterResize = debounce(this.afterResize.bind(this), 300);
        this.resizeObserver = new ResizeObserver((entries) => {
            for (let i = 0; i < entries.length; i += 1) {
                const entry = entries[i];
                if (entry.contentBoxSize || entry.contentRect) {
                    debouncedAfterResize();
                }
            }
        });
        this.resizeObserver.observe(this);
    }

    disconnectedCallback() {
        this.resizeObserver.disconnect();
        document.removeEventListener('on:cart:change', this.refreshHeader);
    }

    afterScroll() {
        this.setTransparency();
    }

    afterResize() {
        if (theme.inlineNavigationCheck) {
            theme.inlineNavigationCheck();
        }
        this.setSticky();
        this.setHeaderHeightProperty();
    }

    setTransparency() {
        if (this.transparentPageheader) {
            const bar = document.querySelector('.section-announcement-bar');
            const scrollThreshold = bar ? (bar.offsetTop + bar.clientHeight) : 0;
            if (!this.isSticky || window.scrollY <= scrollThreshold) {
                this.transparentPageheader.classList.add('pageheader--transparent');
            } else {
                this.transparentPageheader.classList.remove('pageheader--transparent');
            }
        }
    }

    setSticky() {
        this.isSticky = getComputedStyle(this.section).position === 'sticky' || getComputedStyle(this.section).position === '-webkit-sticky';
    }

    setHeaderHeightProperty() {
        let headerHeight = 0;
        let stickyHeaderHeight = 0;

        if (this.section) {
            headerHeight = Math.ceil(this.section.clientHeight);

            if (this.isSticky) {
                stickyHeaderHeight = headerHeight;
            }
        }

        document.documentElement.style.setProperty('--theme-header-height', `${headerHeight}px`);
        document.documentElement.style.setProperty('--theme-sticky-header-height', `${stickyHeaderHeight}px`);
    }

    updateFromCartChange(html) {
        const selectorForUpdate = '.logo-area__right';
        const template = document.createElement('template');
        template.innerHTML = html;

        const elToUpdate = this.querySelector(selectorForUpdate);
        if (elToUpdate) {
            elToUpdate.innerHTML = template.content.querySelector(selectorForUpdate).innerHTML;
        }
    }
};

window.customElements.define('page-header', PageHeader);

const RelatedCollectionLinkButtons = class extends HTMLElement {
    constructor() {
        super();

        const handleResize = debounce(this.truncateButtons.bind(this), 300);
        this.resizeObserver = new ResizeObserver((entries) => {
            for (let i = 0; i < entries.length; i += 1) {
                const entry = entries[i];
                if (entry.contentBoxSize || entry.contentRect) {
                    handleResize();
                }
            }
        });
        this.resizeObserver.observe(this);

        this.truncateButtons();
    }

    truncateButtons() {
        if (getComputedStyle(this).display === 'flex') {
            const limit = 4;
            const btns = this.querySelectorAll('.btn');
            if (btns.length > limit && !this.querySelector('.related-collection-links__expander')) {
                Array.from(btns).slice(limit - 1).forEach((el) => el.classList.add('hidden'));
                const expander = document.createElement('a');
                expander.className = 'btn btn--tertiary related-collection-links__expander';
                expander.href = '#';
                expander.innerText = this.dataset.expanderBtnText;
                expander.addEventListener('click', (evt) => {
                    evt.preventDefault();
                    this.resizeObserver.disconnect();
                    [...evt.currentTarget.parentElement.children].forEach((el) => el.classList.remove('hidden'));
                    evt.currentTarget.remove();
                });
                this.insertAdjacentElement('beforeend', expander);
            }
        } else {
            const expander = this.querySelector('.related-collection-links__expander');
            if (expander) {
                [...expander.parentElement.children].forEach((el) => el.classList.remove('hidden'));
                expander.remove();
            }
        }
    }
};

window.customElements.define('related-collection-link-buttons', RelatedCollectionLinkButtons);

const videoPlayButton = class extends HTMLElement {
    constructor(){
        super()
        this.isplay = false
    }

    connectedCallback(){
        this.component = this.closest('video-component')
        this.video = this.closest('video-component').querySelector('video')
        if (!this.video) return;
        this.video.addEventListener('click',this.palyToggle.bind(this) );
        this.addEventListener('click',this.palyToggle.bind(this));
        this.video.addEventListener('play', () => {
            this.isplay = true;
            this.classList.add('hidden');
        });

        this.video.addEventListener('pause', () => {
            this.isplay = false;
            this.classList.remove('hidden');
        });

        this.video.addEventListener("ended",function(){
            console.log("the video is end");
        },false);
    }

    async palyToggle(){
        if(this.video.paused){
            const playPromise = this.video.play()
            this.classList.add('hidden')
            playPromise.then(()=>{
                this.video.play()
                this.isplay = true
            })
        }else{
            this.isplay = false
        }
    }
    
}

window.customElements.define('video-play-button', videoPlayButton);


const SocialCard = class extends HTMLElement {
    constructor(){
        super()
        this.modal =  document.querySelector('#modal-feed')
        this.fromSection = 'gtl'
        const shoppableInstagramHeader = this.modal.querySelector('.shoppable-instagram-header')
        this.shoppableInstagramHeader= shoppableInstagramHeader.clientHeight + 20
    }
    connectedCallback(){
        if(!this.dataset.disableInteraction){
            this.boundOpenHandle = this.openHandle.bind(this);
            this.addEventListener('click', this.boundOpenHandle)
        }
        this.fromSection = this.dataset.fromSection
        this.mediaType = this.dataset.mediaType
        this.cover = this.dataset.cover
        this.videoSrc = this.dataset.videoSrc
        this.username = this.dataset.username || ''
        this.coverAspectRatio = 375 / 500

        this.container = this.modal.querySelector('.featured-product-container')
        this.featuredProductsContainer = this.modal.querySelector('.featured-product')
        this.coverContainer = this.modal.querySelector('.cover')
        this.currentProductId = this.dataset.productId;

    }

    async openHandle(){
        const openToken = (this.openRequestToken || 0) + 1;
        this.openRequestToken = openToken;

        const baseInventory = this.getVariantInventory();
        if (!baseInventory.length) return;

        this.renderLoadingState(Math.max(2, Math.min(baseInventory.length, 4)));

        try {
            this.variantInventorys = await this.getHydratedVariantInventory(baseInventory);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('Failed to load Seen on You product data', error);
            this.variantInventorys = this.getFallbackVariantInventory(baseInventory);
        }

        if (openToken !== this.openRequestToken) {
            return;
        }

        if (!this.variantInventorys.length) {
            this.renderEmptyState();
            return;
        }

        await this.preloadCriticalAssets();
        this.preloadProductImages(this.variantInventorys);
        if (openToken !== this.openRequestToken) {
            return;
        }

        if(this.mediaType === 'IMAGE'){
            const imageEl = document.createElement('img')
            imageEl.src = this.cover
            this.coverContainer.innerHTML = `<span class="instagram-card__username">@${this.variantInventorys[0].username}</span>`;
            this.coverContainer.appendChild(imageEl);
            this.syncCoverHeightWithFallback();
        }else{

            const videoComponent = document.createElement('video-component');
            videoComponent.className = 'has-video';
            videoComponent.setAttribute('type', 'ig');
            videoComponent.setAttribute('data-video-url', this.videoSrc);
            videoComponent.setAttribute('data-video-id', this.videoSrc);
            videoComponent.setAttribute('data-autoplay', 'false');
            videoComponent.setAttribute('data-background', 'false');
            const videoEl = document.createElement('video')
            videoEl.muted = true;
            // videoEl.autoplay = true;
            // videoEl.loop = true;
            videoEl.playsinline = true; 
            videoEl.poster = this.cover;
            videoEl.setAttribute('controls', '');
            videoEl.style.width = "100%"
            videoEl.style.display = "inline-block"
            videoEl.src = this.videoSrc
            videoEl.load()

            

            videoComponent.appendChild(videoEl);
            this.coverContainer.innerHTML = '';

            this.coverContainer.appendChild(videoComponent);
            videoEl.insertAdjacentHTML('afterend', `
                <video-play-button class="play-overlay" id="play-overlay">
                    <div class="video-play-btn video-section__play-btn">
                        <svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                    </div>
                </video-play-button>
            `);

        }
        const productBlockList =  this.variantInventorys.map(variantInventory=>{
            const sizesVariantList =  variantInventory.available_sizes.map((currentColorVariant)=>{
              const title = currentColorVariant.title
              const size = currentColorVariant.size
              const id = currentColorVariant.id
              const available = currentColorVariant.available
             return  `<li data-available=${available} data-size='${size}' data-title='${variantInventory.title}' data-variants='${variantInventory.title}' data-id='${id}' class="${ available == false ? 'sold-out':''}" >${size} ${ available == false ? ' -  Sold Out':''} </li>`
            }).join('')
            const variantFristId = variantInventory.available_sizes?.[0]?.id || variantInventory.variant_id
            const sizesVariantUl = `<ul>${sizesVariantList}</ul>`
            // &utm_source=gethelook&utm_medium=onsite&utm_content=postid_${variantInventory.post_id}
            const productQuery = variantFristId
                ? `?variant=${variantFristId}&from_section=${this.fromSection}&_get_the_look_id=${variantInventory.post_id}&_influencer_handle=${variantInventory.username}`
                : `?from_section=${this.fromSection}&_get_the_look_id=${variantInventory.post_id}&_influencer_handle=${variantInventory.username}`;
            const ProductUrl = `/products/${variantInventory.href}${productQuery}`
            const res =  this.productBlockBuildHtml({
                id:variantInventory.id,
                href:ProductUrl,
                imgSrc:variantInventory.image_url,
                amount:variantInventory.price,
                title:variantInventory.title,
                index:1,
                variant:[],
                sizesVariantUl:sizesVariantUl
            })
            return res
        }).join('')

        this.featuredProductsContainer.style.position = "static"
        if(isMobile){   
           // this.featuredProductsContainer.style.position = "fixed"
            this.featuredProductsContainer.style.transform = "translateY(0px)"

        }

        this.featuredProductsContainer.innerHTML = `
        <featured-ig class="w-full">
                <div class="drawer-handle-bar">
                    
                </div>
                <div class="shoppable-instagram-header">
                    <div class="shoppable-instagram-header-box">
                    <h4>
                        Get the Look
                    </h4>
                    <button type="button" class="modal__close-btn absolute  js-close-modal mobile-only">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="icon feather feather-x" aria-hidden="true" focusable="false" role="presentation"><path d="M18 6 6 18M6 6l12 12"></path></svg>
                        <span class="visually-hidden">Close</span>
                    </button>
                    </div>
                    <span>
                        ${this.variantInventorys.length} ${this.variantInventorys.length<2?'item':'items'} 
                    </span>
                </div> 
                 <div class="featured-product-container">
                    <div class="slider__grid product-grid  product-grid--per-row-2 product-grid--per-row-mob-2 complete-the-look__grid"> 
                        ${productBlockList}
                    </div>
                </div>
            </featured-ig>
        
        `

       // this.container.innerHTML = productBlockList

        this.modal.open()
        requestAnimationFrame(() => {
            this.syncCoverHeightWithFallback();
        });
        
        
    }

    resetFeaturedProductHeight(){
         const root = document.documentElement;
         if (window.innerWidth < 768) {
             root.style.setProperty('--ig-panel-height', 'auto');
             root.style.setProperty('--ig-cover-height', 'auto');
             return;
         }

         root.style.setProperty('--ig-panel-height', `${Math.max(this.coverHeight, 0)}px`);
         root.style.setProperty('--ig-cover-height', `${Math.max(this.coverHeight - this.shoppableInstagramHeader, 0)}px`);
    }

    syncCoverHeightWithFallback() {
        const renderedCoverHeight = this.coverContainer
            ? Math.round(this.coverContainer.getBoundingClientRect().height)
            : 0;
        const header = this.modal.querySelector('.shoppable-instagram-header');
        const headerStyles = header ? window.getComputedStyle(header) : null;
        const headerHeight = header
            ? Math.round(
                header.getBoundingClientRect().height
                + parseFloat(headerStyles.marginTop || 0)
                + parseFloat(headerStyles.marginBottom || 0)
            )
            : 0;

        if (renderedCoverHeight > 0) {
            this.coverHeight = renderedCoverHeight;
        } else {
            const coverWidth = this.coverContainer?.clientWidth || 0;
            if (!coverWidth) return;
            this.coverHeight = Math.round(coverWidth / this.coverAspectRatio);
        }

        this.shoppableInstagramHeader = headerHeight;
        this.resetFeaturedProductHeight();
    }

    getVariantInventory() {
        const dataEl = this.querySelector('[type="application/json"]');
        return  JSON.parse(dataEl.textContent) || [];
    }

    getFallbackVariantInventory(baseInventory = []) {
        return (baseInventory || []).map((item) => ({
            ...item,
            available_sizes: item.variant_id ? [{
                id: item.variant_id,
                size: item.size || '',
                price: item.price || '',
                available: true
            }] : []
        })).filter((item) => item && item.href && item.image_url);
    }

    async preloadCriticalAssets() {
        if (!this.cover) return;

        await this.preloadImage(this.cover);
    }

    preloadProductImages(variantInventorys = []) {
        variantInventorys.slice(0, 4).forEach((item) => {
            if (item?.image_url) {
                this.preloadImage(item.image_url);
            }
        });
    }

    preloadImage(url) {
        return new Promise((resolve) => {
            const image = new Image();
            image.onload = () => resolve(image);
            image.onerror = () => resolve(null);
            image.src = url;

            if (image.complete) {
                resolve(image);
            }
        });
    }

    renderLoadingState(count = 4) {
        this.featuredProductsContainer.style.position = "static"
        if(isMobile){
            this.featuredProductsContainer.style.transform = "translateY(0px)"
        }
        this.coverContainer.innerHTML = this.renderCoverSkeleton();
        this.featuredProductsContainer.innerHTML = `
        <featured-ig class="w-full">
            <div class="drawer-handle-bar"></div>
            <div class="shoppable-instagram-header">
                <div class="shoppable-instagram-header-box">
                    <h4>Get the Look</h4>
                    <button type="button" class="modal__close-btn absolute  js-close-modal mobile-only">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="icon feather feather-x" aria-hidden="true" focusable="false" role="presentation"><path d="M18 6 6 18M6 6l12 12"></path></svg>
                        <span class="visually-hidden">Close</span>
                    </button>
                </div>
                <span>Loading items</span>
            </div>
            <div class="featured-product-container">
                <div class="slider__grid product-grid product-grid--per-row-2 product-grid--per-row-mob-2 complete-the-look__grid complete-the-look__grid--skeleton">
                    ${this.renderSkeletonCards(count)}
                </div>
            </div>
        </featured-ig>
        `;
        this.modal.open();
        this.syncCoverHeightWithFallback();
    }

    renderSkeletonCards(count = 2) {
        return Array.from({ length: count }, () => `
        <div class="slider__item soy-skeleton-card" aria-hidden="true">
            <div class="soy-skeleton-card__image theme-skeleton theme-skeleton--media"></div>
            <div class="soy-skeleton-card__content">
                <div class="soy-skeleton-card__title theme-skeleton theme-skeleton--text theme-skeleton--title"></div>
                <div class="soy-skeleton-card__price theme-skeleton theme-skeleton--text"></div>
            </div>
        </div>
        `).join('');
    }

    renderEmptyState() {
        this.featuredProductsContainer.style.position = "static"
        if(isMobile){
            this.featuredProductsContainer.style.transform = "translateY(0px)"
        }
        this.coverContainer.innerHTML = this.renderCoverSkeleton();
        this.featuredProductsContainer.innerHTML = `
        <featured-ig class="w-full">
            <div class="drawer-handle-bar"></div>
            <div class="shoppable-instagram-header">
                <div class="shoppable-instagram-header-box">
                    <h4>Get the Look</h4>
                    <button type="button" class="modal__close-btn absolute  js-close-modal mobile-only">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="icon feather feather-x" aria-hidden="true" focusable="false" role="presentation"><path d="M18 6 6 18M6 6l12 12"></path></svg>
                        <span class="visually-hidden">Close</span>
                    </button>
                </div>
                <span>0 items</span>
            </div>
            <div class="featured-product-container">
                <div class="align-center" style="padding: 24px 16px;">Unable to load products right now.</div>
            </div>
        </featured-ig>
        `;
        this.modal.open();
        this.syncCoverHeightWithFallback();
    }

    renderCoverSkeleton() {
        return `
        <div class="theme-skeleton-cover" aria-hidden="true">
            <div class="theme-skeleton theme-skeleton--media theme-skeleton-cover__media"></div>
            <span class="theme-skeleton theme-skeleton--text theme-skeleton-cover__username"></span>
        </div>
        `;
    }

    async getHydratedVariantInventory(baseInventory) {
        const variantIds = [...new Set(baseInventory.map((item) => item.variant_id).filter(Boolean))];
        if (!variantIds.length) {
            return baseInventory.map((item) => ({
                ...item,
                available_sizes: []
            }));
        }

        const response = await this.fetchSeenOnYouProducts(variantIds);
        const nodes = response?.nodes || [];
        const variantsById = new Map();

        nodes.forEach((node) => {
            if (node && node.id) {
                variantsById.set(this.getEntityId(node.id), node);
            }
        });

        return baseInventory.map((item) => {
            const matchedVariant = variantsById.get(String(item.variant_id));
            if (!matchedVariant) {
                return {
                    ...item,
                    available_sizes: this.getFallbackVariantInventory([item])[0]?.available_sizes || []
                };
            }

            const parentProduct = matchedVariant.product;
            const targetColor = this.getColorOptionValue(matchedVariant.selectedOptions || []);
            const availableSizes = (parentProduct?.variants?.nodes || [])
                .filter((variant) => this.normaliseOptionValue(this.getColorOptionValue(variant.selectedOptions || [])) === this.normaliseOptionValue(targetColor))
                .map((variant) => ({
                    id: this.getEntityId(variant.id),
                    size: this.getSizeOptionValue(variant.selectedOptions || []),
                    price: this.formatMoneyAmount(variant.price?.amount),
                    available: !!variant.availableForSale
                }));

            const primaryImage = matchedVariant.image?.url
                || parentProduct?.images?.edges?.[0]?.node?.url
                || item.image_url;

            return {
                ...item,
                id: this.getEntityId(parentProduct?.id) || item.id,
                title: parentProduct?.title || item.title,
                color: targetColor,
                price: this.formatMoneyAmount(matchedVariant.price?.amount) || item.price,
                image_url: primaryImage || item.image_url,
                href: parentProduct?.handle || item.href,
                available_sizes: availableSizes.length ? availableSizes : (this.getFallbackVariantInventory([item])[0]?.available_sizes || [])
            };
        });
    }

    async fetchSeenOnYouProducts(variantIds) {
        const query = `
query SeenOnYouVariants($variantIds: [ID!]!) {
  nodes(ids: $variantIds) {
    ... on ProductVariant {
      id
      title
      image {
        url(transform: { maxWidth: 512, preferredContentType: WEBP })
      }
      price {
        amount
      }
      selectedOptions {
        name
        value
      }
      availableForSale
      product {
        id
        title
        handle
        images(first: 1) {
          edges {
            node {
              url(transform: { maxWidth: 512, preferredContentType: WEBP })
            }
          }
        }
        variants(first: 100) {
          nodes {
            id
            price {
              amount
            }
            availableForSale
            selectedOptions {
              name
              value
            }
          }
        }
      }
    }
  }
}
        `;

        return this.shopifyFetch(query, {
            variantIds: variantIds.map((id) => `gid://shopify/ProductVariant/${id}`)
        });
    }

    getEntityId(gid = '') {
        return typeof gid === 'string' ? gid.split('/').pop() : '';
    }

    normaliseOptionValue(value) {
        return typeof value === 'string' ? value.trim().toLowerCase() : '';
    }

    getOptionValueByName(selectedOptions = [], optionName) {
        const matchedOption = (selectedOptions || []).find((item) => this.normaliseOptionValue(item?.name) === optionName);
        return matchedOption?.value || '';
    }

    getColorOptionValue(selectedOptions = []) {
        return this.getOptionValueByName(selectedOptions, 'color') || this.getOptionValueByName(selectedOptions, 'colour');
    }

    getSizeOptionValue(selectedOptions = []) {
        return this.getOptionValueByName(selectedOptions, 'size');
    }

    formatMoneyAmount(amount) {
        const normalizedAmount = typeof amount === 'string'
            ? amount.replace(/[^0-9.-]/g, '')
            : amount;
        if (normalizedAmount === '') return '';
        const parsedAmount = Number(normalizedAmount);
        if (!Number.isFinite(parsedAmount)) return '';

        try {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: window.Shopify?.currency?.active || 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 2
            }).format(parsedAmount);
        } catch (error) {
            return `$${parsedAmount.toFixed(2).replace(/\.?0+$/, '')}`;
        }
    }

    async shopifyFetch(query, variables = {}) {
        const storeDomain = '3d54b1-24.myshopify.com';
        const storefrontAccessToken = 'e568a4e85eae05f90490fc8e90b40098';
        const fetchImpl = window.__themeNativeFetch || (window.fetch ? window.fetch.bind(window) : null);
        if (!fetchImpl) {
            throw new Error('Fetch API is unavailable');
        }

        const response = await fetchImpl(`https://${storeDomain}/api/2025-01/graphql.json`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-Shopify-Storefront-Access-Token': storefrontAccessToken
            },
            body: JSON.stringify({ query, variables })
        });

        if (!response.ok) {
            throw new Error(`GraphQL request failed with status ${response.status}`);
        }

        const payload = await response.json();
        if (payload.errors) {
            throw new Error(payload.errors.map((error) => error.message).join(', '));
        }

        return payload.data;
    }

    productBlockBuildHtml({ id, href, imgSrc, amount, title, index,variant ,sizesVariantUl}) {
      const displayAmount = this.formatMoneyAmount(amount) || amount;
      return `
   <div
    class="slider__item"> 
    <product-block class="product-block" data-variant="${variant}" data-price="${amount}" data-title="${title}" data-product-id="${id}" data-product-handle="handle">
    <div class="block-inner">
        <div class="block-inner-inner">
            <div class="image-cont ">
                <a class="product-link" href="${href}" aria-hidden="true"
                    tabindex="-1">
                    <div class="image-label-wrap">
                        <div>
                            <div class="product-block__image product-block__image--primary product-block__image--active"
                                data-media-id="${id}">
                                <img src="${imgSrc}&amp;width=1080"
                                    alt="" width="1080" height="1296" loading="lazy" sizes="100vw"
                                    srcset="${imgSrc}&amp;width=425 340w, ${imgSrc}?v=1763036070&amp;width=1350 1080w"
                                    class="theme-img">
                            </div>
                        </div>
                    </div>
                </a>${false?`<a class="quickadd-icon-btn quickadd-toggle"
                    data-simple-modal="true"
                    href="${href}">
                    <span class="visually-hidden">Quick buy</span>
                    <img
                      src="//athluna.com/cdn/shop/t/26/assets/add-cart.png?v=142331930162378913981767161352"
                    >
                </a>`:''}
            </div>
            <div class="product-block__detail align-ltr-left">
                <div class="inner">
                    <div class="innerer">
                        <a class="product-link" href="${href}">
                            <div class="product-block__title">${title}</div>
                            <div class="product-price product-price--block">
                                <div class="price">
                                    <div class="price__default">
                                        <span class="price__current">${displayAmount} </span>
                                    </div>
                                </div>
                            </div>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div class="alert-product">
        <div class="quickadd-container use-color-scheme use-color-scheme--default">
            <div class="inner"></div>
        </div>
    </div>
    <div class="hide size-varint-template ">
      ${sizesVariantUl}
    </div>
</product-block>

</div>
    `;
    }


    disconnectedCallback(){
        if (this.boundOpenHandle) {
            this.removeEventListener('click', this.boundOpenHandle)
        }
    }
}

window.customElements.define('social-card', SocialCard);



class LengthSwitch extends HTMLElement {
    static observedAttributes = ['data-color'];
    constructor(){
        super()
    }
    connectedCallback(){
        this.closest('.js-product').addEventListener('on:variant:change', this.switchHandle.bind(this));
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name === 'data-color' && oldValue !== newValue) {
        const color = this.dataset.color
        const title = this.dataset.fromTitle
        const channel = this.dataset.channel
        const links = this.querySelectorAll('a[href]')
        links.forEach(link => {
            try {
                const url = new URL(link.href);
                url.searchParams.set('from_color', color);
                url.searchParams.set('from_title', title);
                url.searchParams.set('channel', channel);
                url.searchParams.set('from_section', 'ls');
                link.href = url.href;
            } catch (e) {
                console.warn('跳过非标准链接:', link.href);
            }
        });
      }
    }

    async switchHandle(e) {
      const currentUrl = e.target.dataset.url;
      if(e.target.selectedOptions[0]!= this.dataset.color){
        const option = e.detail.selectedOptions[0];
        this.dataset.color = option;
      } 
    }

}

window.customElements.define('length-switch',LengthSwitch)


class featuredIg extends HTMLElement {
    constructor(){
        super()
        this.container =  this.closest(".modal__content")
        
    }
    connectedCallback(){
        return
        this.currentTranslateY = 0
        this.drawer = this.container.querySelector(".featured-product")
        this.mediaHeight = 0
        this.minH = window.innerHeight - window.innerWidth * 1.3333 +8; // 最低露出高度
        this.maxH = window.innerHeight; // 最高（全屏）
        this.y = window.innerHeight - this.minH; // 初始 Y 坐标
        this.isDragging = false;
        this.velocity = 0;
        this.lastY = 0;
        this.lastTime = 0;
        if(isMobile){
            this.drawer.style.position = "fixed"
            this.drawer.style.transform = `translateY(${this.y}px)`;
        }
        // if(this.mobile){
        //     this.drawer.style.transform = `translateY(${this.y}px)`;
        // }
        this.drawerHandle = this.querySelector(".drawer-handle-bar")
        this.container.addEventListener("touchstart",this.drawerTouchStartHandle.bind(this))
        this.container.addEventListener("touchmove",this.drawerTouchmovetHandle.bind(this))
        this.container.addEventListener("touchend",this.drawerTouchendetHandle.bind(this))
    }

    disconnectedCallback(){
        return
        this.container.removeEventListener("touchstart",this.drawerTouchStartHandle.bind(this))
        this.container.removeEventListener("touchmove",this.drawerTouchmovetHandle.bind(this))
        this.container.removeEventListener("touchend",this.drawerTouchendetHandle.bind(this))
    }

    drawerTouchStartHandle(e){
        const inner =  this.container.querySelector(".featured-product-container")
        const isContentClicked = e.composedPath().includes(inner);
        if(isContentClicked) return;

        if (inner.scrollTop > 0 && !e.composedPath().includes(this.container.querySelector('.drawer-handle-bar'))) return;
        //
        // if(this.mediaHeight == 0){
        //   this.mediaHeight = this.drawer.getBoundingClientRect().top
        //   this.minh = this.drawer.getBoundingClientRect().top
        //   this.y = window.innerHeight - this.mediaHeight;
        // }
        // if(this.drawer.style.position!="fixed"){
        //     this.drawer.style.position = "fixed"
        //     this.drawer.style.transform = `translateY(${this.y}px)`;
        // }
        //
        this.isDragging = true;
        this.startY = e.touches[0].clientY - this.y;
        this.drawer.classList.remove('drawer-animate');
        this.lastTime = performance.now();
        this.lastY = e.touches[0].clientY;
        
        // 

        // this.startY = e.touches[0].clientY;
        // this.drawer.style.transition = 'none';
        // if(this.drawer.style.position!="fixed"){
        //     this.drawer.style.position = "fixed"
        //     this.drawer.style.transform = `translateY(${this.mediaHeight}px)`;
        // }
        // this.isDragging = true;
    }

    drawerTouchmovetHandle(e){

        if (!this.isDragging) return;
        
        const now = performance.now();
        const currentY = e.touches[0].clientY;
        const dt = now - this.lastTime;
        if (dt > 0) this.velocity = (currentY - this.lastY) / dt;

        let nextY = currentY - this.startY;
        const limit = window.innerHeight - this.minH;

        if (nextY > limit) {
            // 下拉阻尼：越往下拉越吃力
            nextY = limit + (nextY - limit) * 0.35;
        } else if (nextY < 0) {
            // 上拉阻尼
            nextY = nextY * 0.2;
        }

        this.y = nextY;
        this.lastY = currentY;
        this.lastTime = now;
        
        this.render();
        e.preventDefault();
               
        // if (!this.isDragging) return;
        // const touchY = e.touches[0].clientY;
        // let deltaY = touchY - this.startY;
        // deltaY = touchY
        // deltaY = Math.min(deltaY,this.mediaHeight-20)
        // deltaY = Math.max(deltaY,0)
        //  console.log('this.drawerHandle',this.mediaHeight,deltaY )
        // this.currentTranslateY = deltaY;
        // this.drawer.style.transform = `translateY(${deltaY}px)`;
    }

    drawerTouchendetHandle(e){

         if (!this.isDragging) return;
                this.isDragging = false;
                this.drawer.classList.add('drawer-animate');

                const limit = window.innerHeight - this.minH;
                 const vThreshold = 0.5; 
                // 物理甩动判断
                                if (this.velocity < -vThreshold) {
                    // 快速向上甩
                    this.snapTo(0);
                } else if (this.velocity > vThreshold) {
                    // 快速向下甩
                    this.snapTo(limit);
                } else {
                    // 慢速状态：松开即停，仅处理越界回弹
                    if (this.y > limit) this.snapTo(limit);
                    else if (this.y < 0) this.snapTo(0);
                    // 在 0 和 limit 之间时不执行任何 snapTo，实现即停
                }
                this.velocity = 0;


        // if (!this.isDragging) return;
        // this.isDragging = false;
        
        // const endY = e.changedTouches[0].clientY;
        // const diff = endY - this.startY;
        
        //  this.drawer.style.transition = 'transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)';

        // if(endY<this.mediaHeight*0.3){
        //      this.drawer.style.transform = `translateY(0)`;
        // }else if(endY>this.mediaHeight*0.9){
        //     this.drawer.style.transform = `translateY(${this.mediaHeight}px)`;
        // }else{
        //     this.drawer.style.transform = `translateY(${endY}px)`;
        // }

       


        // if (diff < -150) {
        //     const expandValue = -(screenHeight * 0.6); 
        //     this.drawer.style.transform = `translateY(${expandValue}px)`;
        //     currentY = expandValue;
        // } else {
        //     // 否则弹回初始位置
        //     this.drawer.style.transform = `translateY(0)`;
        //     currentY = 0;
        //    // video.style.filter = `brightness(1)`;
        // }
    }

               snapTo(target) {
                this.y = target;
                this.render();
            }

    render(){
        this.drawer.style.transform = `translateY(${this.y}px)`;
        const limit = window.innerHeight - this.minH;
        const progress = (limit - this.y) / limit;
        if (this.y < limit - 50) {
            this.setAttribute('expanded', '');
            document.body.classList.add('is-expanded');
        } else {
            this.removeAttribute('expanded');
            document.body.classList.remove('is-expanded');
        }
    }
}

window.customElements.define('featured-ig', featuredIg);



class SwatchField extends HTMLElement {

    connectedCallback(){
       
        const optionsItem = this.querySelectorAll('.product-block-options__item')
        const optionsItemLen = optionsItem.length
         const limit = isMobile?(optionsItemLen==5?5:4):(optionsItemLen==7?7:6);
        const allItems = Array.from(optionsItem);
        const topFiveItems = allItems.slice(0, limit);
        topFiveItems.forEach(item => {
            item.classList.remove('product-block-options__item--truncated');
        });
        if(this.querySelector('.product-block-options__more-label')){
            if(optionsItem.length - limit>0){
                this.querySelector('.product-block-options__more-label').innerHTML = `+ ${optionsItem.length - limit}`
            }else{
                this.querySelector('.product-block-options__more-label').innerHTML = ''
            }

        }
    }
}

window.customElements.define('swatch-field', SwatchField);
	
// Combined Listings
const CombinedMoreScript = class extends HTMLElement {
    constructor(){
        super()
    }
    connectedCallback(){
        const productBlock = this.closest('product-block')
        const content = productBlock.querySelector('template').content.cloneNode(true);
        const allowNextImage =  productBlock.querySelector('.image-page-button--next');
        if(allowNextImage){
            productBlock.querySelector('.image-label-wrap div').appendChild(content)
            productBlock.init()   
        }  
    }
}
window.customElements.define('combined-more-script', CombinedMoreScript);

window.initLazyScript = initLazyScript;

if (!theme.Shopify) theme.Shopify = {};
try {
    theme.Shopify.features = JSON.parse(document.documentElement.querySelector('#shopify-features').textContent);
} catch (e) {
    theme.Shopify.features = {};
}

document.addEventListener('DOMContentLoaded', () => {
    // Smooth scroll to an internal link
    const internalLinksSmoothScroll = (evt) => {
        const link = evt.target.tagName === 'A' ? evt.target : evt.target.closest('a');
        if (link && link.getAttribute('href') && link.getAttribute('href').length > 1 && link.getAttribute('href')[0] === '#') {
            const target = document.querySelector(link.getAttribute('href'));
            if (target && target.offsetParent) {
                evt.preventDefault();
                theme.scrollToRevealElement(target);
            }
        }
    };

    if (theme.settings.internalLinksSmoothScroll) {
        document.addEventListener('click', internalLinksSmoothScroll);
    }

    // Enable focus outlines after using the tab key, and remove scroll internal link scroll handler
    const tabCheck = (evt) => {
        if (evt.code === 'Tab') {
            document.body.classList.add('tab-used');
            document.removeEventListener('keydown', tabCheck);
            document.removeEventListener('click', internalLinksSmoothScroll);
        }
    };
    document.addEventListener('keydown', tabCheck);

    // Opening links in a new tab
    if (theme.settings.externalLinksNewTab) {
        document.addEventListener('click', (evt) => {
            const link = evt.target.tagName === 'A' ? evt.target : evt.target.closest('a');
            if (link && link.href && link.tagName === 'A' && window.location.hostname !== new URL(link.href).hostname) {
                link.target = '_blank';
            }
        });
    }

    // Mobile nav
    theme.addDelegateEventListener(document, 'click', '.mobile-nav-toggle', (evt) => {
        evt.preventDefault();
        if (document.body.classList.contains('enable-mobile-nav-transition')) {
            // hide nav
            document.body.classList.remove('reveal-mobile-nav', 'reveal-mobile-nav--revealed');
            setTimeout(() => {
                document.body.classList.remove('enable-mobile-nav-transition');
            }, 750);
        } else {
            // show nav
            theme.openMobileNav();
        }
    });

    theme.openMobileNav = () => {
        document.body.classList.add('enable-mobile-nav-transition');
        setTimeout(() => {
            document.body.classList.add('reveal-mobile-nav');

            // after reveal, add class
            const cs = getComputedStyle(document.querySelector('.mobile-navigation-drawer .navigation__tier-1 > .navigation__item > .navigation__link'));
            const list = document.querySelectorAll('.mobile-navigation-drawer .navigation__tier-1 > .navigation__item > .navigation__link')
            list.forEach(item=>{
                const parent = item.dataset.gaParent
                const href = item.dataset.gaUrl
                const level = item.dataset.gaLevel
                const name = item.dataset.gaName

                window.gaPort.postMessage({
                    type: 'GA4_EVENT',
                    eventName: 'nav_impression',
                    params:{
                        parent,
                        href,
                        level,
                        name,
                        device:'mobile',
                        type:'anchor'
                    }
                });
            })

            const footerLinks = document.querySelectorAll('.mobile-navigation-drawer__footer .footer-account');
            footerLinks.forEach((item) => {
                const parent = item.dataset.gaParent;
                const href = item.dataset.gaUrl;
                const level = item.dataset.gaLevel;
                const name = item.dataset.gaName;

                window.gaPort.postMessage({
                    type: 'GA4_EVENT',
                    eventName: 'nav_impression',
                    params: {
                        parent,
                        href,
                        level,
                        name,
                        device: 'mobile',
                        type: 'anchor'
                    }
                });
            });

            const delayS = parseFloat(cs.transitionDelay.split(',')[0]) + parseFloat(cs.transitionDuration.split(',')[0]);
            setTimeout(() => {
                document.body.classList.add('reveal-mobile-nav--revealed');
            }, delayS * 1000);

            // restore all disabled images
            theme.manuallyLoadImages(document.querySelector('.mobile-navigation-drawer'));
        }, 10);
    };

    // Window shade
    const shade = document.querySelector('.page-shade');
    if (shade) {
        shade.addEventListener('click', (evt) => {
            evt.preventDefault();
            document.body.classList.remove('reveal-mobile-nav', 'show-search');
            setTimeout(() => {
                document.body.classList.remove('enable-mobile-nav-transition');
            }, 750);
        });
    }
});

window.onpageshow = () => {
    fetch(`${theme.routes.cart}.js`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        }
    })
        .then((response) => {
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            return response.json();
        })
        .then(async (data) => {
            const { items } = data;
            const cartString = items.map((item) => `${item.key}|${item.quantity}`).join(',');
            const cartUint8 = new TextEncoder().encode(cartString);
            const cartHashBuffer = await crypto.subtle.digest('SHA-256', cartUint8);
            const cartHashArray = Array.from(new Uint8Array(cartHashBuffer));
            const cartHashHex = cartHashArray.map((x) => x.toString(16).padStart(2, '0')).join('');
            const cartHash = cartHashHex.toString();
            const cartHashFromDom = document.querySelector('.cart-link').dataset.hash;
            if (cartHash !== cartHashFromDom) {
                document.dispatchEvent(
                    new CustomEvent('on:cart:change', { bubbles: true, cancelable: false })
                );
            }
        })
        .catch((error) => {
            // eslint-disable-next-line no-console
            console.log(error.message);
        });
};

function getProductsByHandle(handle) {
      const query = `
        query  getProductByHandle($handle: String!)  {
            productByHandle(handle: $handle) {
                id
                title
                handle
                productType
                vendor
                createdAt
                tags
                descriptionHtml
                collections(first: 50) {
                nodes { id }
                }
                images(first: 100) {
                nodes {
                    url
                    altText
                    width
                    height
                    id
                }
                }
                variants(first: 250){
                nodes {
                    id
                    sku
                    availableForSale
                    selectedOptions{
                    name
                    value
                    }
                     price {
                        amount
                        currencyCode
                     }
                    quantityAvailable  
                    image{
                        id
                     }  
                }
                }
                metafields(
                identifiers: [
                    { namespace: "custom", key: "sale_badge" }
                    { namespace: "custom", key: "color_complete_the_look" }
                    { namespace: "global", key: "description_tag" }
                ]
                ) {
                namespace
                key
                value
                type
                }
            }
        }
        `;
      return  this.shopifyFetch(query, { handle: handle });
}





function transformShopifyJsonToList(shopifyJson) {
  const product = shopifyJson.productByHandle;

  const variantNodes = (product.variants && product.variants.nodes) || [];

  // Build ordered option names and their possible values from variants' selectedOptions
  const optionValuesMap = new Map();
  // Prefer order from the first variant if available
  if (variantNodes.length > 0) {
    const first = variantNodes[0].selectedOptions || [];
    for (const so of first) {
      optionValuesMap.set(so.name || 'Option', []);
    }
  }

  for (const v of variantNodes) {
    for (const so of (v.selectedOptions || [])) {
      const name = so.name || 'Option';
      const value = so.value == null ? '' : so.value;
      if (!optionValuesMap.has(name)) optionValuesMap.set(name, []);
      const arr = optionValuesMap.get(name);
      if (!arr.includes(value)) arr.push(value);
    }
  }

  const options = Array.from(optionValuesMap.entries()).map(([name, values]) => ({ name, values }));

  const variants = variantNodes.map(variant => {
    const selected = variant.selectedOptions || [];
    const optIndexes = options.map(opt => {
      const so = selected.find(s => s.name === opt.name);
      const value = so ? (so.value == null ? '' : so.value) : null;
      const idx = value == null ? -1 : opt.values.indexOf(value);
      return idx >= 0 ? idx : 0;
    });

    // Extract price: can be variant.price.amount (object) or variant.price (number)
    let price = null;
    if (variant.price) {
      if (typeof variant.price === 'object' && variant.price.amount) {
        price = parseFloat(variant.price.amount);
      } else if (typeof variant.price === 'number') {
        price = variant.price;
      }
    }

    // Extract available: prioritize quantityAvailable, then available, then availableForSale
    let available = null;
    if (typeof variant.quantityAvailable === 'number') {
      available = variant.quantityAvailable;
    } else if (typeof variant.available === 'number') {
      available = variant.available;
    } else if (variant.availableForSale === false) {
      available = 0;
    }

    // Extract weight: convert to number if present, otherwise null
    let weight = null;
    if (typeof variant.weight === 'number') {
      weight = variant.weight;
    } else if (typeof variant.weight === 'string') {
      const parsed = parseFloat(variant.weight);
      weight = isNaN(parsed) ? null : parsed;
    }

    // Find imageIndex from variant.image.id by matching with product.images.nodes
    let imageIndex = 0;
    if (variant.image && variant.image.id) {
      const variantImageId = variant.image.id;
      const foundIdx = product.images.nodes.findIndex(img => img.id === variantImageId);
      imageIndex = foundIdx >= 0 ? foundIdx : 0;
    }

    return {
      id: parseInt(variant.id.split('/').pop()),
      sku: variant.sku || null,
      barcode: null,
      available: available,
      price: price,
      weight: weight,
      compareAtPrice: variant.compareAtPrice || 0,
      imageIndex: imageIndex,
      options: optIndexes,
      metafields: null,
      flags: 0
    };
  });

  return {
    id: parseInt(product.id.split('/').pop()),
    productType: product.productType,
    title: product.title,
    description: null,
    collections: product.collections.nodes.map(col => parseInt(col.id.split('/').pop())),
    tags: product.tags,
    urlName: product.handle,
    vendor: product.vendor,
    date: product.createdAt,
    variants: variants,
    selectedVariantId: null,
    images: product.images.nodes.map(img => ({
      url: img.url.replace('https://', '//'),
      alt: img.altText,
      width: img.width,
      height: img.height
    })),
    metafields: (product.metafields || [])
      .filter(m => m !== null)
      .map(m => ({
        key: m.key,
        value: m.value,
        multiple: false,
        valueType: (m.type || '').includes('json') ? 'Json' : 'Text',
        namespace: m.namespace
      })),
    options: options,
    review: 0,
    reviewCount: 0,
    extra: null
  };
}
